import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getUserIdFromToken } from '../../utils/jwt';
import NetworkAdsAdmin from '../NetworkAds/NetworkAdsAdmin';
import './Admin.css';

const formatSubscription = (value) => {
  const s = String(value || 'free');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatNumber = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString();
};

const SUBSCRIPTION_DISPLAY_ORDER = ['free', 'basic', 'standard', 'premium', 'enterprise'];

const formatPackageCountSummary = (counts = {}) => {
  return SUBSCRIPTION_DISPLAY_ORDER
    .filter((key) => Number(counts?.[key] || 0) > 0)
    .map((key) => `${formatNumber(counts[key])} ${formatSubscription(key)}`)
    .join(' • ');
};

const formatRuntimeOverageSummary = (runtimeLimits = {}) => {
  const overages = Array.isArray(runtimeLimits?.overages) ? runtimeLimits.overages : [];
  if (!overages.length) return '';
  return overages
    .map((entry) => `${entry.label}: ${formatNumber(entry.used)}/${formatNumber(entry.limit)}`)
    .join(' • ');
};

const formatRuntimeLimitValue = (value) => {
  if (value === 'unlimited') return 'Unlimited';
  return formatNumber(value);
};

const createEmptyPartnerPanel = () => ({
  id: `partner-panel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  description: '',
  url: '',
  logoUrl: '',
  objectKey: '',
  enabled: false,
  complete: false,
});

const normalizePartnerPanel = (entry) => {
  const title = String(entry?.title || '').trim();
  const description = String(entry?.description || '').trim();
  const url = String(entry?.url || '').trim();
  const logoUrl = String(entry?.logoUrl || '').trim();
  const objectKey = String(entry?.objectKey || '').trim();
  const id = String(entry?.id || '').trim() || createEmptyPartnerPanel().id;
  const complete = Boolean(title && description && url && logoUrl);
  return {
    id,
    title,
    description,
    url,
    logoUrl,
    objectKey,
    enabled: complete ? Boolean(entry?.enabled) : false,
    complete,
  };
};

const PERIOD_OPTIONS = [
  { key: 'month', label: 'Month to date' },
  { key: '30d', label: 'Last 30 days' },
  { key: '7d', label: 'Last 7 days' },
  { key: 'day', label: 'Last 24h' },
];

const BILLING_COLUMNS = [
  { key: 'sites', label: 'Sites' },
  { key: 'screens', label: "TV's" },
  { key: 'musicPlayers', label: 'Music' },
  { key: 'websites', label: 'Websites' },
  { key: 'wifi', label: 'Wi-Fi' },
  { key: 'menuQrs', label: 'Menu' },
  { key: 'productQrs', label: 'Product' },
  { key: 'serviceQrs', label: 'Service' },
  { key: 'infoQrs', label: 'Info' },
];

const EMPTY_BILLING_TOTALS = {
  sites: 0,
  screens: 0,
  musicPlayers: 0,
  websites: 0,
  wifi: 0,
  menuQrs: 0,
  productQrs: 0,
  serviceQrs: 0,
  infoQrs: 0,
};

const DEFAULT_BILLING_PRICING = {
  sites: 500000,
  screens: 250000,
  musicPlayers: 250000,
  websites: 250000,
  wifi: 250000,
  menuQrs: 250000,
  productQrs: 250000,
  serviceQrs: 250000,
  infoQrs: 250000,
};

const DEFAULT_BILLING_PACKAGE_CATALOG = {
  free: {
    key: 'free',
    label: 'Free',
    basePrice: 0,
    advertisingEnabled: true,
    maxSitesPerAccount: 1,
    summary: 'Ad-supported single-site starter package.',
    websitePolicy: 'Hosted website, captive Wi-Fi, and affiliate inventory included. No custom DNS, camera streaming, promotions, pixels, or white-label controls.',
    aiPolicy: 'Low account-level AI allowance for setup and trial usage.',
    apiPolicy: 'No external API access by default.',
    included: {
      sites: 1,
      screens: 5,
      musicPlayers: 1,
      websites: 1,
      wifi: 1,
      menuQrs: 1,
      productQrs: 1,
      serviceQrs: 1,
      infoQrs: 1,
    },
  },
  basic: {
    key: 'basic',
    label: 'Basic',
    basePrice: 500000,
    advertisingEnabled: true,
    maxSitesPerAccount: null,
    summary: 'Low-cost ad-supported package for budget operators and multi-site accounts.',
    websitePolicy: 'Hosted website, captive Wi-Fi, and affiliate inventory remain on. No custom DNS, camera streaming, promotions, pixels, or white-label controls.',
    aiPolicy: 'Higher AI allowance than Free, still capped at account level.',
    apiPolicy: 'Limited API access if enabled.',
    included: {
      sites: 1,
      screens: 5,
      musicPlayers: 1,
      websites: 1,
      wifi: 1,
      menuQrs: 1,
      productQrs: 1,
      serviceQrs: 1,
      infoQrs: 1,
    },
  },
  standard: {
    key: 'standard',
    label: 'Standard',
    basePrice: 750000,
    advertisingEnabled: false,
    maxSitesPerAccount: null,
    summary: 'Ad-free default paid venue package.',
    websitePolicy: 'Own DNS, promotions, and a cleaner branded website experience are enabled.',
    aiPolicy: 'Operational monthly AI allowance for normal venue usage.',
    apiPolicy: 'Standard account-level API access.',
    included: {
      sites: 1,
      screens: 5,
      musicPlayers: 1,
      websites: 1,
      wifi: 1,
      menuQrs: 1,
      productQrs: 1,
      serviceQrs: 1,
      infoQrs: 1,
    },
  },
  premium: {
    key: 'premium',
    label: 'Premium',
    basePrice: 1500000,
    advertisingEnabled: false,
    maxSitesPerAccount: null,
    summary: 'High-capacity package for large venues and screen-heavy sites.',
    websitePolicy: 'Everything in Standard plus camera streaming and higher operational ceilings.',
    aiPolicy: 'High AI allowance for advanced venue workflows.',
    apiPolicy: 'Higher API allowance than Standard.',
    included: {
      sites: 1,
      screens: 20,
      musicPlayers: 5,
      websites: 1,
      wifi: 1,
      menuQrs: 5,
      productQrs: 5,
      serviceQrs: 5,
      infoQrs: 5,
    },
  },
  enterprise: {
    key: 'enterprise',
    label: 'Enterprise',
    basePrice: null,
    advertisingEnabled: false,
    isPoa: true,
    maxSitesPerAccount: null,
    summary: 'Account-level negotiated commercial arrangement.',
    websitePolicy: 'Negotiated per group, site cluster, or custom deal.',
    aiPolicy: 'Custom AI allowance and support terms.',
    apiPolicy: 'Custom API and operational terms.',
    included: {
      sites: 1,
      screens: 50,
      musicPlayers: 10,
      websites: 1,
      wifi: 1,
      menuQrs: 10,
      productQrs: 10,
      serviceQrs: 10,
      infoQrs: 10,
    },
  },
};

const BILLING_BINARY_KEYS = new Set(['websites', 'wifi']);
const DEFAULT_VAT_PERCENT = 20;
const VAT_RATE_OPTIONS = [0, 5, 8, 10, 20];
const INVOICE_CADENCE_OPTIONS = [1, 2, 3, 6, 12];
const BILLING_SCOPE_OPTIONS = [
  { key: 'all_sites', label: 'All sites use these business details' },
  { key: 'selected_sites', label: 'Only selected sites use these business details' },
  { key: 'per_site', label: 'Separate billing entity per site' },
];
const DEFAULT_BILLING_CONTRACT_CATALOG = {
  templates: {
    managed_service: {
      key: 'managed_service',
      label: 'Managed Service',
      summary: 'Best for current manual onboarding where Soundcheck creates initial menus and service content for the venue to maintain and update.',
      defaultServiceModel: 'managed',
      defaultResponseTimeKey: 'four_business_hours',
      defaultSupportChannel: 'whatsapp_email',
      defaultContentResponsibility: 'provider',
      defaultIncludedUpdateRequests: 4,
      defaultIncludedAdminHours: 0,
      availableClauseKeys: ['fair_use_updates', 'venue_network_exclusion', 'third_party_dependencies', 'on_site_support_excluded'],
    },
    self_service: {
      key: 'self_service',
      label: 'Self-Service',
      summary: 'Customer manages their own menus, promotions, and site content with support via helpdesk and forum.',
      defaultServiceModel: 'self_service',
      defaultResponseTimeKey: 'next_business_day',
      defaultSupportChannel: 'helpdesk_forum',
      defaultContentResponsibility: 'customer',
      defaultIncludedUpdateRequests: 0,
      defaultIncludedAdminHours: 0,
      availableClauseKeys: ['customer_managed_content', 'venue_network_exclusion', 'third_party_dependencies', 'on_site_support_excluded'],
    },
    enterprise: {
      key: 'enterprise',
      label: 'Enterprise',
      summary: 'Custom multi-site service terms, rollout support, and negotiated service levels for larger groups.',
      defaultServiceModel: 'enterprise_custom',
      defaultResponseTimeKey: 'four_business_hours',
      defaultSupportChannel: 'helpdesk_email',
      defaultContentResponsibility: 'shared',
      defaultIncludedUpdateRequests: 12,
      defaultIncludedAdminHours: 4,
      availableClauseKeys: ['fair_use_updates', 'venue_network_exclusion', 'third_party_dependencies', 'on_site_support_excluded', 'multi_site_schedule'],
    },
  },
  clauses: {
    fair_use_updates: { key: 'fair_use_updates', label: 'Included Content Updates', summary: 'Soundcheck can complete content updates up to the Included Update Requests and Included Admin Hours shown above each month. Additional work will be agreed before it is charged.' },
    customer_managed_content: { key: 'customer_managed_content', label: 'Customer Managed Content', summary: 'The customer is responsible for day-to-day menu, pricing, promotion, and content changes.' },
    venue_network_exclusion: { key: 'venue_network_exclusion', label: 'Venue Network Exclusion', summary: 'Soundcheck is not responsible for venue-side internet, router, controller, or local network failures.' },
    third_party_dependencies: { key: 'third_party_dependencies', label: 'Third-Party Dependency Exclusion', summary: 'Third-party outages and platform restrictions may affect service availability or timelines.' },
    on_site_support_excluded: { key: 'on_site_support_excluded', label: 'On-Site Support Excluded', summary: 'On-site visits, hardware replacement, and physical interventions are excluded unless separately agreed.' },
    multi_site_schedule: { key: 'multi_site_schedule', label: 'Multi-Site Schedule', summary: 'The agreement may cover multiple venues under one commercial schedule or appendix.' },
  },
  responseTimes: [
    { key: 'best_efforts', label: 'Best efforts', summary: 'Initial response on a reasonable best-efforts basis during business hours.' },
    { key: 'next_business_day', label: 'Guaranteed next business day', summary: 'We aim to respond as soon as possible and guarantee an initial response by the next business day.' },
    { key: 'four_business_hours', label: '4-hour target / next-day guarantee', summary: 'We aim to respond as soon as possible, with a target initial response within 4 business hours and a guaranteed initial response by the next business day.' },
  ],
  supportChannels: [
    { key: 'whatsapp_email', label: 'WhatsApp + Email + Phone' },
    { key: 'phone_email', label: 'Phone + Email' },
    { key: 'helpdesk_email', label: 'Helpdesk + Email' },
    { key: 'helpdesk_phone_email', label: 'Helpdesk + Phone + Email' },
    { key: 'helpdesk_forum', label: 'Helpdesk + Forum' },
  ],
  contentResponsibilities: [
    { key: 'provider', label: 'Soundcheck updates content' },
    { key: 'customer', label: 'Customer self-manages content' },
    { key: 'shared', label: 'Shared responsibility' },
  ],
  termOptions: [1, 3, 6, 12, 24],
};
const EMPTY_BILLING_PROFILE = {
  issuerCompanyKey: 'company1',
  billingScope: 'all_sites',
  selectedSiteIds: [],
  activeBillingSiteId: null,
  legalBusinessName: '',
  taxCode: '',
  businessRegistrationNumber: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  ward: '',
  district: '',
  cityProvince: '',
  countryCode: 'VN',
  billingContactName: '',
  billingEmail: '',
  billingPhone: '',
  signerName: '',
  signerTitle: '',
  invoiceNotes: '',
  paymentNotes: '',
  invoiceStartDate: '',
  invoiceCadenceMonths: 1,
  invoicingActive: false,
  timeAndMaterials: { hourlyRate: 0, items: [] },
  billingSources: {
    service: { issuerCompanyKey: 'company1', addToCombined: false },
    contract: { issuerCompanyKey: 'company1', addToCombined: false },
    timeMaterials: { issuerCompanyKey: 'company1', addToCombined: false },
  },
  combinedInvoice: { issuerCompanyKey: 'company1', discountType: 'percent', discountValue: 0, surchargePercent: 0 },
  invoiceDocuments: [],
  contractDraft: {},
  contractDocuments: [],
  siteBillingEntities: {},
  billingIntakeRequest: null,
  subscriptionChangeRequest: null,
};

const PLATFORM_COMPANY_PROFILE = {
  key: 'company1',
  companyName: String(process.env.REACT_APP_COMPANY_NAME || '').trim(),
  address: String(process.env.REACT_APP_COMPANY_ADDRESS || '').trim(),
  taxCode: String(process.env.REACT_APP_COMPANY_TAX_CODE || '').trim(),
  representative: String(process.env.REACT_APP_COMPANY_REPRESENTATIVE || '').trim(),
  phone: String(process.env.REACT_APP_COMPANY_PHONE || '').trim(),
  email: String(process.env.REACT_APP_COMPANY_EMAIL || process.env.REACT_APP_CONTACT_EMAIL || '').trim(),
  bankName: String(process.env.REACT_APP_COMPANY_BANK_NAME || '').trim(),
  bankAccountNumber: String(process.env.REACT_APP_COMPANY_BANK_ACCOUNT_NUMBER || '').trim(),
  logoUrl: '/system-logos/00-suitestream-tv-default.png',
};
const DEFAULT_INVOICE_LOGO_URL = '/system-logos/00-suitestream-tv-default.png';
const DEFAULT_BILLING_COMPANIES = [PLATFORM_COMPANY_PROFILE];

const normalizeBillingPrice = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return Math.max(0, Number(fallback) || 0);
  return Math.round(numeric);
};

const normalizeTimeAndMaterials = (value = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return {
    hourlyRate: normalizeBillingPrice(source.hourlyRate, 0),
    items: (Array.isArray(source.items) ? source.items : []).slice(0, 200).map((entry, index) => {
      const type = entry?.type === 'labor' ? 'labor' : 'material';
      const quantity = Number(entry?.quantity || 0);
      return {
        id: String(entry?.id || `item-${index + 1}`).trim(),
        type,
        description: String(entry?.description || ''),
        quantity: Number.isFinite(quantity) && quantity >= 0 ? Math.round(quantity * 100) / 100 : 0,
        unit: String(entry?.unit || (type === 'labor' ? 'hours' : 'units')),
        unitRate: normalizeBillingPrice(entry?.unitRate, 0),
        includeInInvoice: true,
      };
    }),
  };
};

const normalizeBillingSources = (value = {}, fallbackIssuerCompanyKey = 'company1') => {
  const fallback = /^company\d+$/.test(String(fallbackIssuerCompanyKey || '').trim()) ? fallbackIssuerCompanyKey : 'company1';
  const normalizeSource = (source = {}) => ({
    issuerCompanyKey: /^company\d+$/.test(String(source?.issuerCompanyKey || '').trim()) ? source.issuerCompanyKey : fallback,
    addToCombined: Boolean(source?.addToCombined),
  });
  return {
    service: normalizeSource(value?.service),
    contract: normalizeSource(value?.contract),
    timeMaterials: normalizeSource(value?.timeMaterials),
  };
};

const normalizeCombinedInvoice = (value = {}, fallbackIssuerCompanyKey = 'company1') => ({
  issuerCompanyKey: /^company\d+$/.test(String(value?.issuerCompanyKey || '').trim()) ? value.issuerCompanyKey : fallbackIssuerCompanyKey,
  discountType: value?.discountType === 'flat' ? 'flat' : 'percent',
  discountValue: Math.max(0, Number(value?.discountValue || 0)),
  surchargePercent: Math.max(0, Number(value?.surchargePercent || 0)),
});

const normalizeBillingPricing = (value, fallback = DEFAULT_BILLING_PRICING) => {
  return BILLING_COLUMNS.reduce((acc, column) => {
    const fallbackValue = normalizeBillingPrice(fallback?.[column.key], DEFAULT_BILLING_PRICING[column.key]);
    acc[column.key] = normalizeBillingPrice(value?.[column.key], fallbackValue);
    return acc;
  }, {});
};

const buildBillingPricingDraft = (pricing) => {
  const normalized = normalizeBillingPricing(pricing, DEFAULT_BILLING_PRICING);
  return BILLING_COLUMNS.reduce((acc, column) => {
    acc[column.key] = String(normalized[column.key] || 0);
    return acc;
  }, {});
};

const normalizeBillingPackageKey = (value, fallback = 'free') => {
  const normalized = String(value || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(DEFAULT_BILLING_PACKAGE_CATALOG, normalized) ? normalized : fallback;
};

const normalizeVatPercent = (value, fallback = DEFAULT_VAT_PERCENT) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
    return Math.round(Number(fallback) || DEFAULT_VAT_PERCENT);
  }
  return Math.round(numeric);
};

const applyVatToAmount = (value, vatPercent = DEFAULT_VAT_PERCENT) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount * (1 + (normalizeVatPercent(vatPercent) / 100)));
};

const normalizeInvoiceCadenceMonths = (value, fallback = 1) => {
  const numeric = Number(value);
  if (!Number.isInteger(numeric) || !INVOICE_CADENCE_OPTIONS.includes(numeric)) {
    return INVOICE_CADENCE_OPTIONS.includes(Number(fallback)) ? Number(fallback) : 1;
  }
  return numeric;
};

const normalizeInvoiceStartDate = (value) => {
  const normalized = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : '';
};

const normalizeBillingContractTemplateKey = (value, contractCatalog = DEFAULT_BILLING_CONTRACT_CATALOG) => {
  const normalized = String(value || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(contractCatalog?.templates || {}, normalized) ? normalized : 'managed_service';
};

const normalizeBillingContractTermMonths = (value, contractCatalog = DEFAULT_BILLING_CONTRACT_CATALOG, fallback = 12) => {
  const numeric = Number(value);
  const allowed = Array.isArray(contractCatalog?.termOptions) ? contractCatalog.termOptions : DEFAULT_BILLING_CONTRACT_CATALOG.termOptions;
  if (!Number.isInteger(numeric) || !allowed.includes(numeric)) {
    return allowed.includes(Number(fallback)) ? Number(fallback) : 12;
  }
  return numeric;
};

const normalizeBillingContractDraft = (value = {}, contractCatalog = DEFAULT_BILLING_CONTRACT_CATALOG) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const templateKey = normalizeBillingContractTemplateKey(source.templateKey, contractCatalog);
  const template = contractCatalog?.templates?.[templateKey] || DEFAULT_BILLING_CONTRACT_CATALOG.templates.managed_service;
  const responseTimeKeys = new Set((contractCatalog?.responseTimes || []).map((entry) => entry.key));
  const supportChannelKeys = new Set((contractCatalog?.supportChannels || []).map((entry) => entry.key));
  const contentResponsibilityKeys = new Set((contractCatalog?.contentResponsibilities || []).map((entry) => entry.key));
  const allowedClauseKeys = new Set(template?.availableClauseKeys || []);
  const selectedClauseKeys = Array.isArray(source.selectedClauseKeys)
    ? source.selectedClauseKeys
      .map((entry) => String(entry || '').trim().toLowerCase())
      .filter((entry) => allowedClauseKeys.has(entry))
    : [];

  return {
    templateKey,
    serviceModel: String(source.serviceModel || template?.defaultServiceModel || 'managed').trim() || (template?.defaultServiceModel || 'managed'),
    contractStartDate: normalizeInvoiceStartDate(source.contractStartDate),
    monthlyRate: source.monthlyRate === '' || source.monthlyRate === null || source.monthlyRate === undefined
      ? null
      : normalizeBillingPrice(source.monthlyRate, 0),
    termMonths: normalizeBillingContractTermMonths(source.termMonths, contractCatalog, 12),
    autoRenew: source.autoRenew == null ? true : Boolean(source.autoRenew),
    noticeDays: normalizeBillingPrice(source.noticeDays, 30),
    responseTimeKey: responseTimeKeys.has(String(source.responseTimeKey || '').trim().toLowerCase())
      ? String(source.responseTimeKey).trim().toLowerCase()
      : (template?.defaultResponseTimeKey || 'next_business_day'),
    supportChannel: supportChannelKeys.has(String(source.supportChannel || '').trim().toLowerCase())
      ? String(source.supportChannel).trim().toLowerCase()
      : (template?.defaultSupportChannel || 'helpdesk_email'),
    contentResponsibility: contentResponsibilityKeys.has(String(source.contentResponsibility || '').trim().toLowerCase())
      ? String(source.contentResponsibility).trim().toLowerCase()
      : (template?.defaultContentResponsibility || 'provider'),
    includedUpdateRequests: normalizeBillingPrice(source.includedUpdateRequests, template?.defaultIncludedUpdateRequests || 0),
    includedAdminHours: normalizeBillingPrice(source.includedAdminHours, template?.defaultIncludedAdminHours || 0),
    onSiteSupportIncluded: Boolean(source.onSiteSupportIncluded),
    selectedClauseKeys: Array.isArray(source.selectedClauseKeys)
      ? Array.from(new Set(selectedClauseKeys))
      : (template?.availableClauseKeys || []).filter((key) => ['venue_network_exclusion', 'third_party_dependencies'].includes(key)),
    specialTerms: String(source.specialTerms || ''),
  };
};

const normalizeBillingIdentityFields = (value = {}, fallback = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const resolvedFallback = fallback && typeof fallback === 'object' && !Array.isArray(fallback) ? fallback : {};
  const pick = (field, fallbackValue = '') => String(source?.[field] ?? resolvedFallback?.[field] ?? fallbackValue);
  return {
    legalBusinessName: pick('legalBusinessName'),
    taxCode: pick('taxCode'),
    businessRegistrationNumber: pick('businessRegistrationNumber'),
    billingAddressLine1: pick('billingAddressLine1'),
    billingAddressLine2: pick('billingAddressLine2'),
    ward: pick('ward'),
    district: pick('district'),
    cityProvince: pick('cityProvince'),
    countryCode: pick('countryCode', 'VN').toUpperCase() || 'VN',
    billingContactName: pick('billingContactName'),
    billingEmail: pick('billingEmail'),
    billingPhone: pick('billingPhone'),
    signerName: pick('signerName'),
    signerTitle: pick('signerTitle'),
    invoiceNotes: pick('invoiceNotes'),
    paymentNotes: pick('paymentNotes'),
  };
};

const normalizeSiteBillingEntities = (value = {}, billingSites = [], fallbackIdentity = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const siteIdSet = new Set((billingSites || []).map((site) => Number(site?.id || 0)).filter(Boolean));
  return Object.entries(source).reduce((acc, [siteIdKey, entry]) => {
    const siteId = Number(siteIdKey || 0);
    if (!siteIdSet.has(siteId)) return acc;
    acc[String(siteId)] = normalizeBillingIdentityFields(entry, fallbackIdentity);
    return acc;
  }, {});
};

const normalizeBillingIntakeRequest = (value = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const token = String(source.token || '').trim();
  if (!token) return null;
  return {
    token,
    status: String(source.status || 'generated').trim() || 'generated',
    createdAt: String(source.createdAt || '').trim(),
    updatedAt: String(source.updatedAt || '').trim(),
    lastSubmittedAt: String(source.lastSubmittedAt || '').trim(),
    intakePath: String(source.intakePath || '').trim(),
    intakeUrl: String(source.intakeUrl || '').trim(),
  };
};

const normalizeSubscriptionChangeRequest = (value = {}, fallbackSubscriptionType = 'free') => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const targetSubscriptionType = normalizeBillingPackageKey(source.targetSubscriptionType, fallbackSubscriptionType);
  const effectiveOn = normalizeInvoiceStartDate(source.effectiveOn);
  if (!effectiveOn) return null;
  return {
    targetSubscriptionType,
    effectiveOn,
    requestedAt: String(source.requestedAt || '').trim(),
    status: String(source.status || 'scheduled').trim() || 'scheduled',
  };
};

const getTodayIsoDate = () => new Date().toISOString().slice(0, 10);

const parseIsoDate = (isoValue) => {
  const normalized = normalizeInvoiceStartDate(isoValue);
  if (!normalized) return null;
  const [year, month, day] = normalized.split('-').map((part) => Number(part));
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatIsoDate = (isoValue) => {
  const parsed = parseIsoDate(isoValue);
  if (!parsed) return '—';
  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const addMonthsToIsoDate = (isoValue, monthsToAdd) => {
  const parsed = parseIsoDate(isoValue);
  if (!parsed) return '';
  const next = new Date(parsed);
  next.setUTCMonth(next.getUTCMonth() + Number(monthsToAdd || 0));
  return next.toISOString().slice(0, 10);
};

const subtractDaysFromIsoDate = (isoValue, daysToSubtract = 1) => {
  const parsed = parseIsoDate(isoValue);
  if (!parsed) return '';
  const next = new Date(parsed);
  next.setUTCDate(next.getUTCDate() - Number(daysToSubtract || 0));
  return next.toISOString().slice(0, 10);
};

const computeInvoiceSchedule = (billingProfile = EMPTY_BILLING_PROFILE, nowDate = new Date()) => {
  const cadenceMonths = normalizeInvoiceCadenceMonths(billingProfile?.invoiceCadenceMonths, 1);
  const startDate = normalizeInvoiceStartDate(billingProfile?.invoiceStartDate);
  if (!startDate) {
    return {
      startDate: '',
      cadenceMonths,
      periodStart: '',
      periodEndInclusive: '',
      nextDueDate: '',
    };
  }
  const todayIso = nowDate.toISOString().slice(0, 10);
  let periodStart = startDate;
  let nextDueDate = addMonthsToIsoDate(startDate, cadenceMonths);
  if (!nextDueDate) {
    return {
      startDate: '',
      cadenceMonths,
      periodStart: '',
      periodEndInclusive: '',
      nextDueDate: '',
    };
  }

  while (nextDueDate <= todayIso) {
    periodStart = nextDueDate;
    nextDueDate = addMonthsToIsoDate(nextDueDate, cadenceMonths);
  }

  return {
    startDate,
    cadenceMonths,
    periodStart,
    periodEndInclusive: subtractDaysFromIsoDate(nextDueDate, 1),
    nextDueDate,
  };
};

const formatVnd = (value) => `${formatNumber(value)} VND`;

const formatBillingAddress = (billingProfile = {}) => (
  [
    billingProfile.billingAddressLine1,
    billingProfile.billingAddressLine2,
    billingProfile.ward,
    billingProfile.district,
    billingProfile.cityProvince,
    billingProfile.countryCode,
  ]
    .filter(Boolean)
    .join(', ')
    || '—'
);

const buildRedInvoiceClipboardText = ({
  issuer = PLATFORM_COMPANY_PROFILE,
  customer = EMPTY_BILLING_PROFILE,
  invoiceDocument = null,
  coveredSites = [],
  billingScopeLabel = '',
}) => {
  const coveredSiteNames = Array.isArray(invoiceDocument?.coveredSiteNames) && invoiceDocument.coveredSiteNames.length
    ? invoiceDocument.coveredSiteNames
    : (coveredSites || []).map((site) => String(site?.name || '').trim()).filter(Boolean);
  const lines = [
    'RED INVOICE INFO',
    '',
    'SELLER',
    `Name: ${issuer.companyName || '—'}`,
    `Tax code: ${issuer.taxCode || '—'}`,
    `Address: ${issuer.address || '—'}`,
    `Representative: ${issuer.representative || '—'}`,
    `Phone: ${issuer.phone || '—'}`,
    `Hotline: ${issuer.hotline || '—'}`,
    `Email: ${issuer.email || '—'}`,
    `Website: ${issuer.website || '—'}`,
    `Bank: ${issuer.bankName || '—'}`,
    `Bank account: ${issuer.bankAccountNumber || '—'}`,
    '',
    'BUYER',
    `Name: ${customer.legalBusinessName || '—'}`,
    `Tax code: ${customer.taxCode || '—'}`,
    `Business registration: ${customer.businessRegistrationNumber || '—'}`,
    `Address: ${formatBillingAddress(customer)}`,
    `Billing contact: ${customer.billingContactName || '—'}`,
    `Phone: ${customer.billingPhone || '—'}`,
    `Email: ${customer.billingEmail || '—'}`,
    `Signer: ${customer.signerName || '—'}${customer.signerTitle ? ` | ${customer.signerTitle}` : ''}`,
    '',
    'INVOICE',
    `Reference: ${invoiceDocument?.referenceNumber || '—'}`,
    `Issue date: ${formatIsoDate(invoiceDocument?.issueDate)}`,
    `Service period: ${formatIsoDate(invoiceDocument?.periodStart)} - ${formatIsoDate(invoiceDocument?.periodEndInclusive)}`,
    `Billing scope: ${billingScopeLabel || '—'}`,
    `Covered sites: ${coveredSiteNames.join(' | ') || '—'}`,
    `Subtotal: ${formatVnd(invoiceDocument?.subtotal || 0)}`,
    `VAT: ${formatNumber(invoiceDocument?.vatPercent || 0)}% (${formatVnd(invoiceDocument?.vatAmount || 0)})`,
    `Total inc VAT: ${formatVnd(invoiceDocument?.totalWithVat || 0)}`,
  ];
  return lines.join('\n');
};

const normalizeBillingProfile = (value = {}, billingSites = [], contractCatalog = DEFAULT_BILLING_CONTRACT_CATALOG) => {
  const siteIdSet = new Set((billingSites || []).map((site) => Number(site?.id || 0)).filter(Boolean));
  const selectedSiteIds = Array.isArray(value?.selectedSiteIds)
    ? value.selectedSiteIds
      .map((siteId) => Number(siteId))
      .filter((siteId) => siteIdSet.size === 0 || siteIdSet.has(siteId))
    : [];
  const billingScope = BILLING_SCOPE_OPTIONS.some((option) => option.key === value?.billingScope)
    ? value.billingScope
    : 'all_sites';
  const baseBillingIdentity = normalizeBillingIdentityFields(value);
  const siteBillingEntities = normalizeSiteBillingEntities(value?.siteBillingEntities, billingSites, baseBillingIdentity);
  const requestedActiveBillingSiteId = Number(value?.activeBillingSiteId || 0);
  const activeBillingSiteId = billingScope === 'per_site'
    ? (selectedSiteIds.includes(requestedActiveBillingSiteId) ? requestedActiveBillingSiteId : (selectedSiteIds[0] || null))
    : null;

  return {
    issuerCompanyKey: /^company\d+$/.test(String(value?.issuerCompanyKey || '').trim())
      ? String(value.issuerCompanyKey).trim()
      : 'company1',
    billingScope,
    selectedSiteIds,
    activeBillingSiteId,
    ...baseBillingIdentity,
    siteBillingEntities,
    invoiceStartDate: normalizeInvoiceStartDate(value?.invoiceStartDate),
    invoiceCadenceMonths: normalizeInvoiceCadenceMonths(value?.invoiceCadenceMonths, 1),
    invoicingActive: Boolean(value?.invoicingActive),
    timeAndMaterials: normalizeTimeAndMaterials(value?.timeAndMaterials),
    billingSources: normalizeBillingSources(value?.billingSources, value?.issuerCompanyKey || 'company1'),
    combinedInvoice: normalizeCombinedInvoice(value?.combinedInvoice, value?.issuerCompanyKey || 'company1'),
    invoiceDocuments: Array.isArray(value?.invoiceDocuments)
      ? value.invoiceDocuments
        .map((entry) => ({
          id: String(entry?.id || '').trim(),
          referenceNumber: String(entry?.referenceNumber || '').trim(),
          fileName: String(entry?.fileName || '').trim(),
          objectKey: String(entry?.objectKey || '').trim(),
          issueDate: normalizeInvoiceStartDate(entry?.issueDate),
          periodStart: normalizeInvoiceStartDate(entry?.periodStart),
          periodEndInclusive: normalizeInvoiceStartDate(entry?.periodEndInclusive),
          cadenceMonths: normalizeInvoiceCadenceMonths(entry?.cadenceMonths, 1),
          subtotal: normalizeBillingPrice(entry?.subtotal, 0),
          vatPercent: normalizeVatPercent(entry?.vatPercent, DEFAULT_VAT_PERCENT),
          vatAmount: normalizeBillingPrice(entry?.vatAmount, 0),
          totalWithVat: normalizeBillingPrice(entry?.totalWithVat, 0),
          status: String(entry?.status || 'stored').trim() || 'stored',
          invoiceMode: ['service', 'contract', 'time_materials', 'combined'].includes(entry?.invoiceMode)
            ? entry.invoiceMode
            : 'standard',
          issuerCompanyKey: String(entry?.issuerCompanyKey || 'company1').trim(),
          issuerCompanyName: String(entry?.issuerCompanyName || '').trim(),
          draftSnapshot: entry?.draftSnapshot && typeof entry.draftSnapshot === 'object'
            ? {
              timeAndMaterials: normalizeTimeAndMaterials(entry.draftSnapshot.timeAndMaterials),
              billingSources: normalizeBillingSources(entry.draftSnapshot.billingSources, entry.issuerCompanyKey),
              combinedInvoice: normalizeCombinedInvoice(entry.draftSnapshot.combinedInvoice, entry.issuerCompanyKey),
              contractDraft: normalizeBillingContractDraft(entry.draftSnapshot.contractDraft, contractCatalog),
              invoiceStartDate: normalizeInvoiceStartDate(entry.draftSnapshot.invoiceStartDate),
              invoiceCadenceMonths: normalizeInvoiceCadenceMonths(entry.draftSnapshot.invoiceCadenceMonths, 1),
            }
            : null,
          createdAt: String(entry?.createdAt || '').trim(),
          downloadUrl: String(entry?.downloadUrl || '').trim(),
          coveredSiteIds: Array.isArray(entry?.coveredSiteIds)
            ? entry.coveredSiteIds.map((siteId) => Number(siteId)).filter((siteId) => Number.isInteger(siteId) && siteId > 0)
            : [],
          coveredSiteNames: Array.isArray(entry?.coveredSiteNames)
            ? entry.coveredSiteNames.map((siteName) => String(siteName || '').trim()).filter(Boolean)
            : [],
        }))
        .filter((entry) => entry.id)
      : [],
    contractDraft: normalizeBillingContractDraft(value?.contractDraft, contractCatalog),
    contractDocuments: Array.isArray(value?.contractDocuments)
      ? value.contractDocuments
        .map((entry) => ({
          id: String(entry?.id || '').trim(),
          referenceNumber: String(entry?.referenceNumber || '').trim(),
          templateKey: String(entry?.templateKey || '').trim(),
          templateLabel: String(entry?.templateLabel || '').trim(),
          fileName: String(entry?.fileName || '').trim(),
          objectKey: String(entry?.objectKey || '').trim(),
          issueDate: normalizeInvoiceStartDate(entry?.issueDate),
          contractStartDate: normalizeInvoiceStartDate(entry?.contractStartDate),
          termMonths: normalizeBillingPrice(entry?.termMonths, 12),
          noticeDays: normalizeBillingPrice(entry?.noticeDays, 30),
          subtotal: normalizeBillingPrice(entry?.subtotal, 0),
          vatPercent: normalizeVatPercent(entry?.vatPercent, DEFAULT_VAT_PERCENT),
          vatAmount: normalizeBillingPrice(entry?.vatAmount, 0),
          totalWithVat: normalizeBillingPrice(entry?.totalWithVat, 0),
          status: String(entry?.status || 'stored').trim() || 'stored',
          createdAt: String(entry?.createdAt || '').trim(),
          downloadUrl: String(entry?.downloadUrl || '').trim(),
          coveredSiteIds: Array.isArray(entry?.coveredSiteIds)
            ? entry.coveredSiteIds.map((siteId) => Number(siteId)).filter((siteId) => Number.isInteger(siteId) && siteId > 0)
            : [],
          coveredSiteNames: Array.isArray(entry?.coveredSiteNames)
            ? entry.coveredSiteNames.map((siteName) => String(siteName || '').trim()).filter(Boolean)
            : [],
        }))
        .filter((entry) => entry.id)
      : [],
    billingIntakeRequest: normalizeBillingIntakeRequest(value?.billingIntakeRequest),
    subscriptionChangeRequest: normalizeSubscriptionChangeRequest(
      value?.subscriptionChangeRequest,
      value?.currentSubscriptionType || 'free'
    ),
  };
};

const getCoveredBillingSites = (billingProfile = EMPTY_BILLING_PROFILE, sites = []) => {
  const allSites = Array.isArray(sites) ? sites : [];
  if (billingProfile?.billingScope === 'selected_sites') {
    const selectedSiteIdSet = new Set((billingProfile.selectedSiteIds || []).map((siteId) => Number(siteId)));
    return allSites.filter((site) => selectedSiteIdSet.has(Number(site?.id || 0)));
  }
  if (billingProfile?.billingScope === 'per_site') {
    const activeBillingSiteId = Number(billingProfile?.activeBillingSiteId || 0);
    if (Number.isInteger(activeBillingSiteId) && activeBillingSiteId > 0) {
      return allSites.filter((site) => Number(site?.id || 0) === activeBillingSiteId).slice(0, 1);
    }
    const selectedSiteIdSet = new Set((billingProfile.selectedSiteIds || []).map((siteId) => Number(siteId)));
    return allSites.filter((site) => selectedSiteIdSet.has(Number(site?.id || 0))).slice(0, 1);
  }
  return allSites;
};

const getEffectiveBillingEntityProfile = (billingProfile = EMPTY_BILLING_PROFILE, coveredSites = []) => {
  const baseIdentity = normalizeBillingIdentityFields(billingProfile);
  if (billingProfile?.billingScope !== 'per_site') {
    return baseIdentity;
  }
  const activeSiteId = Number(coveredSites?.[0]?.id || billingProfile?.activeBillingSiteId || 0);
  if (!Number.isInteger(activeSiteId) || activeSiteId <= 0) {
    return baseIdentity;
  }
  const siteBillingEntity = billingProfile?.siteBillingEntities?.[String(activeSiteId)] || {};
  return normalizeBillingIdentityFields(siteBillingEntity, baseIdentity);
};

const getBillingProfileMissingFields = (billingProfile = EMPTY_BILLING_PROFILE, coveredSites = []) => {
  const effectiveBillingEntity = getEffectiveBillingEntityProfile(billingProfile, coveredSites);
  const missing = [];
  if (!effectiveBillingEntity.legalBusinessName) missing.push('legal business name');
  if (!effectiveBillingEntity.taxCode) missing.push('tax code');
  if (!effectiveBillingEntity.billingAddressLine1) missing.push('billing address');
  if (!effectiveBillingEntity.cityProvince) missing.push('city / province');
  if (!effectiveBillingEntity.billingEmail) missing.push('billing email');
  if (!effectiveBillingEntity.signerName) missing.push('signer name');
  if (!effectiveBillingEntity.signerTitle) missing.push('signer title');
  if (!billingProfile.invoiceStartDate) missing.push('invoice start date');
  if (billingProfile.billingScope !== 'all_sites' && !(billingProfile.selectedSiteIds || []).length) {
    missing.push('covered sites');
  }
  if (billingProfile.billingScope === 'per_site' && !coveredSites.length) {
    missing.push('active billing site');
  }
  return missing;
};

const getContractProfileMissingFields = (
  billingProfile = EMPTY_BILLING_PROFILE,
  coveredSites = [],
  contractCatalog = DEFAULT_BILLING_CONTRACT_CATALOG
) => {
  const effectiveBillingEntity = getEffectiveBillingEntityProfile(billingProfile, coveredSites);
  const missing = [];
  const contractDraft = normalizeBillingContractDraft(billingProfile?.contractDraft, contractCatalog);
  if (!effectiveBillingEntity.legalBusinessName) missing.push('legal business name');
  if (!effectiveBillingEntity.taxCode) missing.push('tax code');
  if (!effectiveBillingEntity.billingAddressLine1) missing.push('billing address');
  if (!effectiveBillingEntity.cityProvince) missing.push('city / province');
  if (!effectiveBillingEntity.billingEmail) missing.push('billing email');
  if (!effectiveBillingEntity.signerName) missing.push('signer name');
  if (!effectiveBillingEntity.signerTitle) missing.push('signer title');
  if (!(contractDraft.contractStartDate || billingProfile?.invoiceStartDate)) {
    missing.push('contract start date');
  }
  if (billingProfile?.billingScope !== 'all_sites' && !(billingProfile?.selectedSiteIds || []).length) {
    missing.push('covered sites');
  }
  if (billingProfile?.billingScope === 'per_site' && !coveredSites.length) {
    missing.push('active billing site');
  }
  return missing;
};

const getCompanyProfileMissingFields = (companyProfile = PLATFORM_COMPANY_PROFILE) => {
  const missing = [];
  if (!companyProfile.companyName) missing.push('company name');
  if (!companyProfile.address) missing.push('company address');
  if (!companyProfile.taxCode) missing.push('company tax code');
  if (!companyProfile.representative) missing.push('representative');
  if (!companyProfile.phone) missing.push('company phone');
  if (!companyProfile.email) missing.push('company email');
  if (!companyProfile.bankName) missing.push('bank');
  if (!companyProfile.bankAccountNumber) missing.push('bank account');
  return missing;
};

const getContractOptionLabel = (entries = [], key, fallback = '—') => {
  const match = (entries || []).find((entry) => entry.key === key);
  return match?.label || fallback;
};

const getContractOptionSummary = (entries = [], key, fallback = '') => {
  const match = (entries || []).find((entry) => entry.key === key);
  return match?.summary || fallback;
};

const formatBillingScopeLabel = (scope) => {
  const resolved = BILLING_SCOPE_OPTIONS.find((option) => option.key === scope);
  return resolved?.label || 'All sites use these business details';
};

const buildContractPreviewDefinition = ({
  userId,
  email,
  billingProfile = EMPTY_BILLING_PROFILE,
  effectiveBillingEntityProfile = EMPTY_BILLING_PROFILE,
  coveredSites = [],
  invoiceSchedule = {},
  invoicePreviewSubtotal = 0,
  invoicePreviewVatAmount = 0,
  invoicePreviewTotalWithVat = 0,
  vatPercent = DEFAULT_VAT_PERCENT,
  contractDraft = {},
  contractCatalog = DEFAULT_BILLING_CONTRACT_CATALOG,
  billingPackageCatalog = DEFAULT_BILLING_PACKAGE_CATALOG,
  companyProfile = PLATFORM_COMPANY_PROFILE,
  logoUrl = DEFAULT_INVOICE_LOGO_URL,
}) => {
  const customerProfile = effectiveBillingEntityProfile || billingProfile || EMPTY_BILLING_PROFILE;
  const normalizedContractDraft = normalizeBillingContractDraft(contractDraft, contractCatalog);
  const contractTemplate = contractCatalog?.templates?.[normalizedContractDraft.templateKey] || DEFAULT_BILLING_CONTRACT_CATALOG.templates.managed_service;
  const responseTimeLabel = getContractOptionLabel(contractCatalog?.responseTimes, normalizedContractDraft.responseTimeKey, 'Best efforts');
  const responseTimeSummary = getContractOptionSummary(
    contractCatalog?.responseTimes,
    normalizedContractDraft.responseTimeKey,
    'Initial response on a reasonable best-efforts basis during business hours.'
  );
  const supportChannelLabel = getContractOptionLabel(contractCatalog?.supportChannels, normalizedContractDraft.supportChannel, 'Helpdesk + Email');
  const contentResponsibilityLabel = getContractOptionLabel(
    contractCatalog?.contentResponsibilities,
    normalizedContractDraft.contentResponsibility,
    'Soundcheck updates content'
  );
  const selectedClauses = (normalizedContractDraft.selectedClauseKeys || [])
    .map((clauseKey) => contractCatalog?.clauses?.[clauseKey])
    .filter(Boolean);
  const normalizedVatPercent = normalizeVatPercent(vatPercent, DEFAULT_VAT_PERCENT);
  const contractStartDate = normalizedContractDraft.contractStartDate || billingProfile?.invoiceStartDate || '';
  const cadenceMonths = Number(invoiceSchedule?.cadenceMonths || 1);
  const sections = [
    {
      heading: 'Commercial Schedule',
      body: [
        `This agreement covers ${coveredSites.length} site${coveredSites.length === 1 ? '' : 's'}: ${coveredSites.map((site) => site.name).join(' | ') || '—'}.`,
        `The current commercial position is ${formatVnd(invoicePreviewSubtotal || 0)} ex VAT and ${formatVnd(invoicePreviewTotalWithVat || 0)} inc VAT, billed every ${cadenceMonths} month${cadenceMonths === 1 ? '' : 's'}.`,
        `Contract start date: ${formatIsoDate(contractStartDate)}. Notice period: ${formatNumber(normalizedContractDraft.noticeDays)} day${Number(normalizedContractDraft.noticeDays) === 1 ? '' : 's'}.`,
      ],
    },
    {
      heading: 'Service Model',
      body: [
        contractTemplate.summary,
        `Ongoing content responsibility: ${contentResponsibilityLabel}. Monthly assistance includes up to ${formatNumber(normalizedContractDraft.includedUpdateRequests)} content update request${Number(normalizedContractDraft.includedUpdateRequests) === 1 ? '' : 's'} and ${formatNumber(normalizedContractDraft.includedAdminHours)} admin hour${Number(normalizedContractDraft.includedAdminHours) === 1 ? '' : 's'}.`,
        `Support will be provided via ${supportChannelLabel}. Response target: ${responseTimeLabel}. ${responseTimeSummary}`.trim(),
        normalizedContractDraft.onSiteSupportIncluded
          ? 'On-site support is available when scheduled and agreed in advance. The scope, timing, travel, and any additional charges will be confirmed before the visit.'
          : 'Remote support is included. If on-site assistance is needed, it can be arranged separately with the scope, timing, travel, and any charges agreed in advance.',
      ],
    },
    {
      heading: 'Term and Renewal',
      body: [
        normalizedContractDraft.autoRenew
          ? `The agreement automatically renews after the initial ${formatNumber(normalizedContractDraft.termMonths)}-month term unless either party gives ${formatNumber(normalizedContractDraft.noticeDays)} days' written notice.`
          : `The agreement ends after the initial ${formatNumber(normalizedContractDraft.termMonths)}-month term unless extended in writing by both parties.`,
      ],
    },
  ];

  if (selectedClauses.length) {
    sections.push({
      heading: 'Selected Clauses',
      body: selectedClauses.map((clause) => `${clause.label}: ${clause.summary}`),
    });
  }

  if (normalizedContractDraft.specialTerms) {
    sections.push({
      heading: 'Special Terms',
      body: String(normalizedContractDraft.specialTerms)
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    });
  }

  return {
    title: `${contractTemplate.label.toUpperCase()} AGREEMENT`,
    subtitle: 'Provision of services agreement',
    referenceNumber: `AGR-PREVIEW-${String(userId).padStart(4, '0')}-${getTodayIsoDate().replace(/-/g, '')}`,
    issueDate: formatIsoDate(getTodayIsoDate()),
    logoUrl,
    headerMeta: [
      { label: 'Template', value: contractTemplate.label },
      { label: 'Initial term', value: `${formatNumber(normalizedContractDraft.termMonths)} month${Number(normalizedContractDraft.termMonths) === 1 ? '' : 's'}` },
      { label: 'Response target', value: responseTimeLabel },
      { label: 'Support channel', value: supportChannelLabel },
    ],
    parties: [
      {
        label: 'Provider',
        title: companyProfile.companyName || 'Company details missing',
        lines: [
          companyProfile.address || '—',
          `Tax code: ${companyProfile.taxCode || '—'}`,
          `Representative: ${companyProfile.representative || '—'}`,
          `Contact: ${companyProfile.phone || '—'} | ${companyProfile.email || '—'}`,
        ],
      },
      {
        label: 'Customer',
        title: customerProfile.legalBusinessName || email || 'Customer',
        lines: [
          formatBillingAddress(customerProfile),
          `Tax code: ${customerProfile.taxCode || '—'}`,
          `Signer: ${customerProfile.signerName || '—'}${customerProfile.signerTitle ? ` | ${customerProfile.signerTitle}` : ''}`,
          `Contact: ${customerProfile.billingEmail || '—'}${customerProfile.billingPhone ? ` | ${customerProfile.billingPhone}` : ''}`,
        ],
      },
    ],
    table: {
      columns: ['Covered Site', 'Package', 'Monthly ex VAT', 'Monthly inc VAT'],
      rows: [
        ...coveredSites.map((site) => {
        const monthlyExVat = Math.max(0, Math.round(Number(site.manualMonthlyTotal || 0)));
        const monthlyIncVat = monthlyExVat + Math.round(monthlyExVat * (normalizedVatPercent / 100));
        const packageKey = String(site.packageKey || site.resolvedPackageKey || '').trim().toLowerCase();
        const packageEntry = (billingPackageCatalog || DEFAULT_BILLING_PACKAGE_CATALOG)[packageKey] || null;
        return [
          site.name || `Site ${site.id}`,
          packageEntry?.label || String(site.packageKey || site.resolvedPackageKey || 'Custom'),
          formatVnd(monthlyExVat),
          formatVnd(monthlyIncVat),
        ];
        }),
        ...(normalizedContractDraft.monthlyRate !== null && Number(normalizedContractDraft.monthlyRate) !== coveredSites.reduce((sum, site) => sum + Math.max(0, Math.round(Number(site.manualMonthlyTotal || 0))), 0)
          ? [[
            'Agreed rate adjustment',
            'Contract override',
            formatVnd(Number(normalizedContractDraft.monthlyRate) - coveredSites.reduce((sum, site) => sum + Math.max(0, Math.round(Number(site.manualMonthlyTotal || 0))), 0)),
            formatVnd(Math.round((Number(normalizedContractDraft.monthlyRate) - coveredSites.reduce((sum, site) => sum + Math.max(0, Math.round(Number(site.manualMonthlyTotal || 0))), 0)) * (1 + (normalizedVatPercent / 100)))),
          ]]
          : []),
      ],
      emptyMessage: 'No covered sites selected for this billing entity yet.',
    },
    sections,
    notes: [
      { label: 'Billing cadence', value: `${cadenceMonths} month${cadenceMonths === 1 ? '' : 's'}` },
      { label: 'Next invoice due', value: formatIsoDate(invoiceSchedule?.nextDueDate) },
    ],
    totals: [
      { label: 'Commercial subtotal', value: formatVnd(invoicePreviewSubtotal || 0) },
      { label: `VAT (${normalizedVatPercent}%)`, value: formatVnd(invoicePreviewVatAmount || 0) },
      { label: 'Commercial total', value: formatVnd(invoicePreviewTotalWithVat || 0) },
    ],
  };
};

const normalizeBillingFeatureSelection = (value, site = {}) => {
  return BILLING_COLUMNS.reduce((acc, column) => {
    if (column.key === 'sites') {
      acc[column.key] = value?.[column.key] == null ? true : Boolean(value[column.key]);
      return acc;
    }
    if (value?.[column.key] == null) {
      acc[column.key] = Number(site?.[column.key] || 0) > 0;
      return acc;
    }
    acc[column.key] = Boolean(value[column.key]);
    return acc;
  }, {});
};

const normalizeBillingQuantityOverrides = (value = {}) => {
  return BILLING_COLUMNS.reduce((acc, column) => {
    if (value?.[column.key] == null || value?.[column.key] === '') {
      acc[column.key] = null;
      return acc;
    }
    const numeric = Number(value[column.key]);
    if (!Number.isFinite(numeric) || numeric < 0) {
      acc[column.key] = null;
      return acc;
    }
    acc[column.key] = BILLING_BINARY_KEYS.has(column.key) || column.key === 'sites'
      ? (numeric > 0 ? 1 : 0)
      : Math.round(numeric);
    return acc;
  }, {});
};

const buildBillingSiteDraft = (site, billingDefaults) => ({
  pricing: buildBillingPricingDraft(site?.pricingOverride || billingDefaults),
  featureSelection: {
    ...normalizeBillingFeatureSelection(site?.featureSelection, site),
    sites: true,
  },
  quantityOverrides: {
    ...normalizeBillingQuantityOverrides(site?.quantityOverrides),
    sites: 1,
  },
  packageKey: normalizeBillingPackageKey(site?.packageKey || site?.defaultPackageKey || 'free'),
  scenarioPackageKey: normalizeBillingPackageKey(site?.packageKey || site?.defaultPackageKey || 'free'),
});

const getBillableQuantityForManual = (site, featureSelection, quantityOverrides, key) => {
  if (key === 'sites') return 1;
  if (!featureSelection?.[key]) return 0;
  if (quantityOverrides?.[key] != null) {
    const overridden = Math.max(0, Number(quantityOverrides[key] || 0));
    if (key === 'sites') return overridden > 0 ? 1 : 0;
    if (BILLING_BINARY_KEYS.has(key)) return overridden > 0 ? 1 : 0;
    return overridden;
  }
  const count = Math.max(0, Number(site?.[key] || 0));
  if (key === 'sites') return count > 0 ? 1 : 0;
  if (BILLING_BINARY_KEYS.has(key)) return count > 0 ? 1 : 0;
  return count;
};

const getRecommendedFeatureSelectionForPackage = (
  site,
  packageKey,
  quantityOverrides = {},
  packageCatalog = DEFAULT_BILLING_PACKAGE_CATALOG
) => {
  const resolvedPackageKey = normalizeBillingPackageKey(packageKey, site?.defaultPackageKey || 'free');
  const packageConfig = packageCatalog?.[resolvedPackageKey] || DEFAULT_BILLING_PACKAGE_CATALOG[resolvedPackageKey];
  return BILLING_COLUMNS.reduce((acc, column) => {
    if (column.key === 'sites') {
      acc[column.key] = true;
      return acc;
    }
    const included = Math.max(0, Number(packageConfig?.included?.[column.key] || 0));
    const planned = getScenarioQuantity(site, quantityOverrides, column.key);
    acc[column.key] = included > 0 || planned > 0;
    return acc;
  }, {});
};

const computeCurrentBillingMonthlyTotal = (
  site,
  pricing,
  featureSelection,
  quantityOverrides,
  packageKey,
  packageCatalog = DEFAULT_BILLING_PACKAGE_CATALOG
) => {
  const normalizedPricing = normalizeBillingPricing(pricing, DEFAULT_BILLING_PRICING);
  const resolvedPackageKey = normalizeBillingPackageKey(packageKey, site?.defaultPackageKey || 'free');
  const packageConfig = packageCatalog?.[resolvedPackageKey] || DEFAULT_BILLING_PACKAGE_CATALOG[resolvedPackageKey];
  return BILLING_COLUMNS.reduce((sum, column) => {
    if (column.key === 'sites') {
      return sum + Number(normalizedPricing.sites || 0);
    }
    if (!featureSelection?.[column.key]) return sum;
    const plannedQuantity = getBillableQuantityForManual(site, featureSelection, quantityOverrides, column.key);
    const includedQuantity = Math.max(0, Number(packageConfig?.included?.[column.key] || 0));
    const extraQuantity = Math.max(0, plannedQuantity - includedQuantity);
    return sum + (extraQuantity * Number(normalizedPricing[column.key] || 0));
  }, 0);
};

const getScenarioQuantity = (site, quantityOverrides, key) => {
  if (key === 'sites') return 1;
  if (quantityOverrides?.[key] != null) {
    const overridden = Math.max(0, Number(quantityOverrides[key] || 0));
    if (key === 'sites') return overridden > 0 ? 1 : 0;
    if (BILLING_BINARY_KEYS.has(key)) return overridden > 0 ? 1 : 0;
    return overridden;
  }
  const used = Math.max(0, Number(site?.[key] || 0));
  if (key === 'sites') return used > 0 ? 1 : 0;
  if (BILLING_BINARY_KEYS.has(key)) return used > 0 ? 1 : 0;
  return used;
};

const computePackageEstimate = (site, packageKey, billingDefaults, packageCatalog = DEFAULT_BILLING_PACKAGE_CATALOG, quantityOverrides = {}) => {
  const resolvedPackageKey = normalizeBillingPackageKey(packageKey, site?.defaultPackageKey || 'free');
  const packageConfig = packageCatalog?.[resolvedPackageKey] || DEFAULT_BILLING_PACKAGE_CATALOG[resolvedPackageKey];
  if (packageConfig?.isPoa) {
    return {
      key: resolvedPackageKey,
      label: packageConfig?.label || formatSubscription(resolvedPackageKey),
      advertisingEnabled: Boolean(packageConfig?.advertisingEnabled),
      basePrice: null,
      included: packageConfig?.included || {},
      extras: [],
      extrasTotal: null,
      total: null,
      isPoa: true,
    };
  }
  const pricing = normalizeBillingPricing(billingDefaults, DEFAULT_BILLING_PRICING);
  const extras = BILLING_COLUMNS.reduce((acc, column) => {
    const used = getScenarioQuantity(site, quantityOverrides, column.key);
    const included = Math.max(0, Number(packageConfig?.included?.[column.key] || 0));
    const extraQuantity = Math.max(0, used - included);
    if (extraQuantity <= 0) return acc;
    acc.push({
      key: column.key,
      label: column.label,
      quantity: extraQuantity,
      total: extraQuantity * Number(pricing[column.key] || 0),
    });
    return acc;
  }, []);
  const extrasTotal = extras.reduce((sum, extra) => sum + Number(extra.total || 0), 0);
  return {
    key: resolvedPackageKey,
    label: packageConfig?.label || formatSubscription(resolvedPackageKey),
    advertisingEnabled: Boolean(packageConfig?.advertisingEnabled),
    basePrice: Number(packageConfig?.basePrice || 0),
    included: packageConfig?.included || {},
    extras,
    extrasTotal,
    total: Number(packageConfig?.basePrice || 0) + extrasTotal,
  };
};

const getAvailableIncludedQuantity = (site, quantityOverrides, packageEstimate, key) => {
  const included = Math.max(0, Number(packageEstimate?.included?.[key] || 0));
  const planned = getScenarioQuantity(site, quantityOverrides, key);
  return Math.max(0, included - planned);
};

const formatIncludedAllowance = (packageEstimate) => {
  const allowance = packageEstimate?.included || {};
  return BILLING_COLUMNS
    .map((column) => {
      const count = Number(allowance?.[column.key] || 0);
      if (count <= 0) return null;
      return `${count} ${column.label.toLowerCase()}`;
    })
    .filter(Boolean)
    .join(' | ');
};

const formatPriceOrPoa = (value) => {
  if (value == null) return 'POA';
  return formatNumber(value);
};

const getPackageUiCopy = (packageConfig = {}) => {
  return {
    summary: String(packageConfig?.summary || '').trim(),
    websitePolicy: String(packageConfig?.websitePolicy || '').trim(),
    aiPolicy: String(packageConfig?.aiPolicy || '').trim(),
    apiPolicy: String(packageConfig?.apiPolicy || '').trim(),
  };
};

const getActiveBillingColumnsForSite = (site) => {
  return BILLING_COLUMNS;
};

const filterUsersByEmail = (entries, searchQuery) => {
  const q = String(searchQuery || '').trim().toLowerCase();
  if (!q) return entries;
  return (entries || []).filter((entry) => String(entry.email || '').toLowerCase().includes(q));
};

const formatSiteNames = (sites = []) => {
  const names = [...new Set(
    (Array.isArray(sites) ? sites : [])
      .map((site) => String(site?.name || '').trim())
      .filter(Boolean)
  )];
  return names.length ? names.join(', ') : '—';
};

const getFeatureGroup = (feature) => {
  const f = String(feature || '');
  if (f.startsWith('sites.')) return 'Onboarding';
  if (f.startsWith('creative.')) return 'Creative Studio';
  if (f.startsWith('menus.')) return 'Menus';
  if (f.startsWith('plaques.')) return 'Plaques';
  if (f.startsWith('ai.')) return 'AI';
  return 'Other';
};

function Admin() {
  const token = localStorage.getItem('token');
  const currentUserId = useMemo(() => getUserIdFromToken(token), [token]);

  const [subTab, setSubTab] = useState('Users');
  const [period, setPeriod] = useState('month');

  const [users, setUsers] = useState([]);
  const [billingUsers, setBillingUsers] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingWarning, setBillingWarning] = useState('');
  const [billingDefaults, setBillingDefaults] = useState(DEFAULT_BILLING_PRICING);
  const [billingPackageCatalog, setBillingPackageCatalog] = useState(DEFAULT_BILLING_PACKAGE_CATALOG);
  const [billingContractCatalog, setBillingContractCatalog] = useState(DEFAULT_BILLING_CONTRACT_CATALOG);
  const [billingCompanies, setBillingCompanies] = useState(DEFAULT_BILLING_COMPANIES);
  const [billingVatPercent, setBillingVatPercent] = useState(DEFAULT_VAT_PERCENT);
  const [billingVatDraft, setBillingVatDraft] = useState(String(DEFAULT_VAT_PERCENT));
  const [billingDefaultsDraft, setBillingDefaultsDraft] = useState(() => buildBillingPricingDraft(DEFAULT_BILLING_PRICING));
  const [billingDefaultsSaving, setBillingDefaultsSaving] = useState(false);
  const [billingSiteDrafts, setBillingSiteDrafts] = useState({});
  const [billingSiteSavingIds, setBillingSiteSavingIds] = useState({});
  const [accountPackageSavingIds, setAccountPackageSavingIds] = useState({});
  const [billingProfileDrafts, setBillingProfileDrafts] = useState({});
  const [billingProfileSavingIds, setBillingProfileSavingIds] = useState({});
  const [draft, setDraft] = useState({});
  const [savingIds, setSavingIds] = useState({});
  const [deletingIds, setDeletingIds] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [query, setQuery] = useState('');

  const [editingUserId, setEditingUserId] = useState(null);
  const [editingBillingUserId, setEditingBillingUserId] = useState(null);
  const [expandedInvoicePreviewUserIds, setExpandedInvoicePreviewUserIds] = useState(() => new Set());
  const [collapsedBillingContractsByUserId, setCollapsedBillingContractsByUserId] = useState({});
  const [collapsedBillingWorkbenchByUserId, setCollapsedBillingWorkbenchByUserId] = useState({});
  const [collapsedTimeMaterialsByUserId, setCollapsedTimeMaterialsByUserId] = useState({});
  const [billingIntakeGeneratingIds, setBillingIntakeGeneratingIds] = useState({});
  const [billingIntakeCopiedIds, setBillingIntakeCopiedIds] = useState({});
  const [redInvoiceCopiedIds, setRedInvoiceCopiedIds] = useState({});
  const [invoicePdfGeneratingIds, setInvoicePdfGeneratingIds] = useState({});
  const [contractPdfGeneratingIds, setContractPdfGeneratingIds] = useState({});
  const [invoiceDocumentDeletingIds, setInvoiceDocumentDeletingIds] = useState({});
  const [contractDocumentDeletingIds, setContractDocumentDeletingIds] = useState({});
  const [invoiceResettingIds, setInvoiceResettingIds] = useState({});
  const [userUsageByKey, setUserUsageByKey] = useState({});

  const [apiSummary, setApiSummary] = useState(null);
  const [webSummary, setWebSummary] = useState(null);
  const [rateLimits, setRateLimits] = useState([]);
  const [limitsDraft, setLimitsDraft] = useState({});
  const [savingLimitKey, setSavingLimitKey] = useState('');
  const [s3Status, setS3Status] = useState(null);
  const [s3Loading, setS3Loading] = useState(false);
  const [s3Error, setS3Error] = useState('');
  const [s3Message, setS3Message] = useState('');
  const [loginSettings, setLoginSettings] = useState(null);
  const [loginSettingsLoading, setLoginSettingsLoading] = useState(false);
  const [loginSettingsSaving, setLoginSettingsSaving] = useState(false);
  const [loginSettingsError, setLoginSettingsError] = useState('');
  const [loginSettingsMessage, setLoginSettingsMessage] = useState('');
  const [siteIntegrations, setSiteIntegrations] = useState(null);
  const [siteIntegrationsLoading, setSiteIntegrationsLoading] = useState(false);
  const [siteIntegrationsSaving, setSiteIntegrationsSaving] = useState(false);
  const [siteIntegrationsError, setSiteIntegrationsError] = useState('');
  const [siteIntegrationsMessage, setSiteIntegrationsMessage] = useState('');
  const [partnerPanels, setPartnerPanels] = useState([]);
  const [partnerPanelsLoading, setPartnerPanelsLoading] = useState(false);
  const [partnerPanelsSaving, setPartnerPanelsSaving] = useState(false);
  const [partnerPanelsError, setPartnerPanelsError] = useState('');
  const [partnerPanelsMessage, setPartnerPanelsMessage] = useState('');
  const [partnerPanelUploadingId, setPartnerPanelUploadingId] = useState('');
  const pendingBillingIntakeRefreshUserIdsRef = useRef(new Set());
  const partnerPanelFileInputsRef = useRef({});
  const billingDefaultsSaveTimeoutRef = useRef(null);
  const billingSiteSaveTimeoutsRef = useRef({});
  const billingProfileSaveTimeoutsRef = useRef({});

  const loadUsers = async () => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/admin/users?period=${encodeURIComponent(period)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load users (${res.status})`);
      setUsers(data.users || []);
      setDraft({});
    } catch (e) {
      setError(e.message || 'Failed to load users.');
    }
  };

  const loadBilling = async () => {
    setBillingLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/admin/users/billing', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load billing (${res.status})`);
      setBillingUsers(data.users || []);
      const nextDefaults = normalizeBillingPricing(data.billingDefaults || DEFAULT_BILLING_PRICING, DEFAULT_BILLING_PRICING);
      const nextVatPercent = normalizeVatPercent(data.vatPercent, DEFAULT_VAT_PERCENT);
      setBillingDefaults(nextDefaults);
      setBillingPackageCatalog(data.packageCatalog || DEFAULT_BILLING_PACKAGE_CATALOG);
      setBillingContractCatalog(data.contractCatalog || DEFAULT_BILLING_CONTRACT_CATALOG);
      setBillingCompanies(Array.isArray(data.billingCompanies) && data.billingCompanies.length
        ? data.billingCompanies
        : DEFAULT_BILLING_COMPANIES);
      setBillingVatPercent(nextVatPercent);
      setBillingVatDraft(String(nextVatPercent));
      setBillingDefaultsDraft(buildBillingPricingDraft(nextDefaults));
      setBillingSiteDrafts({});
      setBillingProfileDrafts({});
      setBillingWarning(data.warning || '');
    } catch (e) {
      setError(e.message || 'Failed to load billing.');
    } finally {
      setBillingLoading(false);
    }
  };

  const saveBillingDefaults = async (draftValues, vatDraftValue = billingVatDraft) => {
    const normalizedPricing = normalizeBillingPricing(draftValues, billingDefaults);
    const normalizedVatPercent = normalizeVatPercent(vatDraftValue, billingVatPercent);
    setBillingDefaultsSaving(true);
    setError('');
    try {
      const res = await fetch('/api/admin/users/billing/defaults', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ pricing: normalizedPricing, vatPercent: normalizedVatPercent }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to save default prices (${res.status})`);
      const savedPricing = normalizeBillingPricing(data.pricing || normalizedPricing, normalizedPricing);
      const savedVatPercent = normalizeVatPercent(data.vatPercent, normalizedVatPercent);
      setBillingDefaults(savedPricing);
      setBillingVatPercent(savedVatPercent);
      setBillingVatDraft(String(savedVatPercent));
      setBillingDefaultsDraft(buildBillingPricingDraft(savedPricing));
      setBillingWarning('');
    } catch (e) {
      setError(e.message || 'Failed to save default prices.');
    } finally {
      setBillingDefaultsSaving(false);
    }
  };

  const saveBillingSitePricing = async (site, draftValues) => {
    const normalizedPricing = normalizeBillingPricing(draftValues?.pricing, billingDefaults);
    const normalizedFeatureSelection = normalizeBillingFeatureSelection(draftValues?.featureSelection, site);
    const normalizedQuantityOverrides = normalizeBillingQuantityOverrides(draftValues?.quantityOverrides);
    const normalizedPackageKey = normalizeBillingPackageKey(
      draftValues?.packageKey,
      site?.defaultPackageKey || 'free'
    );
    const siteId = Number(site?.id || 0);
    setBillingSiteSavingIds((prev) => ({ ...prev, [siteId]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/billing/sites/${site.id}/pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pricing: normalizedPricing,
          featureSelection: normalizedFeatureSelection,
          quantityOverrides: normalizedQuantityOverrides,
          packageKey: normalizedPackageKey,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to save site prices (${res.status})`);
      const savedPricing = normalizeBillingPricing(data.pricing || normalizedPricing, normalizedPricing);
      const savedFeatureSelection = {
        ...normalizeBillingFeatureSelection(data.featureSelection || normalizedFeatureSelection, site),
        sites: true,
      };
      const savedQuantityOverrides = {
        ...normalizeBillingQuantityOverrides(data.quantityOverrides || normalizedQuantityOverrides),
        sites: 1,
      };
      const savedPackageKey = normalizeBillingPackageKey(data.packageKey || normalizedPackageKey, site?.defaultPackageKey || 'free');
      setBillingUsers((prev) => prev.map((user) => ({
        ...user,
        subscription_type:
          Number(user.id) === Number(data.ownerUserId || 0) && data.effectiveSubscriptionType
            ? data.effectiveSubscriptionType
            : user.subscription_type,
        subscriptionSummary:
          Number(user.id) === Number(data.ownerUserId || 0) && data.subscriptionSummary
            ? data.subscriptionSummary
            : user.subscriptionSummary,
        sites: (user.sites || []).map((entry) => (
          Number(entry.id) === siteId
            ? { ...entry, pricingOverride: savedPricing, featureSelection: savedFeatureSelection, quantityOverrides: savedQuantityOverrides, packageKey: savedPackageKey }
            : entry
        )),
      })));
      setBillingSiteDrafts((prev) => ({
        ...prev,
        [site.id]: {
          pricing: buildBillingPricingDraft(savedPricing),
          featureSelection: savedFeatureSelection,
          quantityOverrides: savedQuantityOverrides,
          packageKey: savedPackageKey,
          scenarioPackageKey: prev?.[site.id]?.scenarioPackageKey || savedPackageKey,
        },
      }));
      setBillingWarning('');
      return true;
    } catch (e) {
      setError(e.message || 'Failed to save site prices.');
      return false;
    } finally {
      setBillingSiteSavingIds((prev) => ({ ...prev, [site.id]: false }));
    }
  };

  const changeAccountPackage = async (user, packageKey) => {
    const sites = Array.isArray(user?.sites) ? user.sites : [];
    if (!sites.length) return;
    const normalizedPackageKey = normalizeBillingPackageKey(packageKey, 'free');
    const packageEntry = (billingPackageCatalog || DEFAULT_BILLING_PACKAGE_CATALOG)[normalizedPackageKey];
    const packageLabel = packageEntry?.label || formatSubscription(normalizedPackageKey);
    const confirmed = window.confirm(
      `Change ${sites.length === 1 ? sites[0].name : `all ${sites.length} sites`} on ${user.email} to ${packageLabel}? This updates billing pricing and the account runtime subscription.`
    );
    if (!confirmed) return;

    setAccountPackageSavingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    try {
      const results = [];
      for (const site of sites) {
        const siteDraft = billingSiteDrafts[site.id] || buildBillingSiteDraft(site, billingDefaults);
        results.push(await saveBillingSitePricing(site, { ...siteDraft, packageKey: normalizedPackageKey }));
      }
      if (results.every(Boolean)) {
        setMessage(`Changed ${user.email} to ${packageLabel}.`);
      }
    } finally {
      setAccountPackageSavingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const getBillingProfileDraft = (user) => {
    return normalizeBillingProfile(
      billingProfileDrafts[user.id] || user.billingProfile || EMPTY_BILLING_PROFILE,
      user.billingSites || [],
      billingContractCatalog
    );
  };

  const saveBillingProfile = async (user, nextBillingProfile, options = {}) => {
    const normalizedBillingProfile = normalizeBillingProfile(nextBillingProfile, user.billingSites || [], billingContractCatalog);
    setBillingProfileSavingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          billingProfile: normalizedBillingProfile,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to save billing profile (${res.status})`);
      const savedUser = {
        ...user,
        ...data.user,
        billingProfile: normalizeBillingProfile(
          data.user?.billingProfile || normalizedBillingProfile,
          data.user?.billingSites || user.billingSites || [],
          billingContractCatalog
        ),
      };
      setBillingUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...savedUser } : entry)));
      setUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...savedUser } : entry)));
      setBillingProfileDrafts((prev) => ({
        ...prev,
        [user.id]: savedUser.billingProfile,
      }));
      if (options.message) {
        setMessage(options.message);
      }
      return true;
    } catch (e) {
      setError(e.message || 'Failed to save billing profile.');
      return false;
    } finally {
      setBillingProfileSavingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const queueSaveBillingProfileDraft = (user, nextBillingProfile, options = {}) => {
    if (billingProfileSaveTimeoutsRef.current[user.id]) {
      window.clearTimeout(billingProfileSaveTimeoutsRef.current[user.id]);
    }
    billingProfileSaveTimeoutsRef.current[user.id] = window.setTimeout(() => {
      saveBillingProfile(user, nextBillingProfile, options);
    }, options.delayMs || 350);
  };

  const updateBillingProfileDraft = (user, field, value, options = {}) => {
    setBillingProfileDrafts((prev) => {
      const existingDraft = getBillingProfileDraft(user);
      const nextDraft = {
        ...existingDraft,
        [field]: value,
      };
      if (options.save !== false) {
        queueSaveBillingProfileDraft(user, nextDraft, options);
      }
      return {
        ...prev,
        [user.id]: nextDraft,
      };
    });
  };

  const updateBillingContractDraft = (user, patch, options = {}) => {
    const existingDraft = getBillingProfileDraft(user);
    const nextContractDraft = normalizeBillingContractDraft(
      {
        ...(existingDraft.contractDraft || {}),
        ...patch,
      },
      billingContractCatalog
    );
    updateBillingProfileDraft(user, 'contractDraft', nextContractDraft, options);
  };

  const saveUserAndClose = async (user) => {
    const saved = await saveUser(user);
    if (saved) {
      setEditingUserId((prev) => (prev === user.id ? null : prev));
    }
  };

  const saveBillingProfileAndClose = async (user) => {
    const draftProfile = getBillingProfileDraft(user);
    const currentProfile = normalizeBillingProfile(
      user.billingProfile || EMPTY_BILLING_PROFILE,
      user.billingSites || [],
      billingContractCatalog
    );
    if (JSON.stringify(currentProfile) === JSON.stringify(draftProfile)) {
      setEditingBillingUserId((prev) => (prev === user.id ? null : prev));
      return;
    }
    const saved = await saveBillingProfile(user, draftProfile, { message: `Saved billing profile for ${user.email}` });
    if (saved) {
      setEditingBillingUserId((prev) => (prev === user.id ? null : prev));
    }
  };

  const loadApiSummary = async () => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/admin/api-usage/summary?period=${encodeURIComponent(period)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load API usage (${res.status})`);
      setApiSummary(data);
    } catch (e) {
      setError(e.message || 'Failed to load API usage.');
    }
  };

  const loadWebSummary = async () => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/admin/webserver-stats/summary?period=${encodeURIComponent(period)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load webserver stats (${res.status})`);
      setWebSummary(data);
    } catch (e) {
      setError(e.message || 'Failed to load webserver stats.');
    }
  };

  const loadRateLimits = async () => {
    setError('');
    try {
      const res = await fetch('/api/admin/api-rate-limits', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load rate limits (${res.status})`);
      setRateLimits(data.limits || []);
    } catch (e) {
      setError(e.message || 'Failed to load rate limits.');
    }
  };

  const loadS3Status = async () => {
    setS3Error('');
    try {
      setS3Loading(true);
      const res = await fetch('/api/admin/s3/convert-all/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load S3 status (${res.status})`);
      setS3Status(data.status || null);
    } catch (e) {
      setS3Error(e.message || 'Failed to load S3 status.');
    } finally {
      setS3Loading(false);
    }
  };

  const startS3ConvertAll = async () => {
    setS3Error('');
    setS3Message('');
    try {
      const res = await fetch('/api/admin/s3/convert-all', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ dryRun: false }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to start S3 conversion (${res.status})`);
      setS3Status(data.status || null);
      setS3Message('Convert-all job started.');
    } catch (e) {
      setS3Error(e.message || 'Failed to start conversion.');
    }
  };

  const loadLoginSettings = async () => {
    setLoginSettingsError('');
    setLoginSettingsMessage('');
    try {
      setLoginSettingsLoading(true);
      const res = await fetch('/api/admin/login-settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load login settings (${res.status})`);
      setLoginSettings(data.settings || null);
    } catch (e) {
      setLoginSettingsError(e.message || 'Failed to load login settings.');
    } finally {
      setLoginSettingsLoading(false);
    }
  };

  const updateLoginSettings = async (updates) => {
    if (!loginSettings) return;
    const previous = loginSettings;
    const next = { ...loginSettings, ...updates };
    setLoginSettings(next);
    setLoginSettingsSaving(true);
    setLoginSettingsError('');
    setLoginSettingsMessage('');
    try {
      const res = await fetch('/api/admin/login-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(next),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to save login settings (${res.status})`);
      setLoginSettings(data.settings || next);
      setLoginSettingsMessage('Login settings saved.');
    } catch (e) {
      setLoginSettings(previous);
      setLoginSettingsError(e.message || 'Failed to save login settings.');
    } finally {
      setLoginSettingsSaving(false);
    }
  };

  const loadSiteIntegrations = async () => {
    setSiteIntegrationsError('');
    setSiteIntegrationsMessage('');
    try {
      setSiteIntegrationsLoading(true);
      const res = await fetch('/api/admin/platform-settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load site integrations (${res.status})`);
      setSiteIntegrations({
        travelpayoutsDriveHeadScript: data?.travelpayoutsDriveHeadScript || data?.travelpayoutsHeadScript || '',
        travelpayoutsWhiteLabelHeadScript: data?.travelpayoutsWhiteLabelHeadScript || '',
        travelpayoutsWhiteLabelSearchMarkup: data?.travelpayoutsWhiteLabelSearchMarkup || '',
        travelpayoutsWhiteLabelResultsMarkup: data?.travelpayoutsWhiteLabelResultsMarkup || '',
        getyourguideHeadScript: data?.getyourguideHeadScript || '',
      });
    } catch (e) {
      setSiteIntegrationsError(e.message || 'Failed to load site integrations.');
    } finally {
      setSiteIntegrationsLoading(false);
    }
  };

  const updateSiteIntegrationsField = (field, value) => {
    setSiteIntegrations((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
    setSiteIntegrationsError('');
    setSiteIntegrationsMessage('');
  };

  const saveSiteIntegrations = async () => {
    if (!siteIntegrations) return;
    setSiteIntegrationsSaving(true);
    setSiteIntegrationsError('');
    setSiteIntegrationsMessage('');
    try {
      const res = await fetch('/api/admin/platform-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          travelpayoutsDriveHeadScript: String(siteIntegrations.travelpayoutsDriveHeadScript || '').trim() || null,
          travelpayoutsWhiteLabelHeadScript: String(siteIntegrations.travelpayoutsWhiteLabelHeadScript || '').trim() || null,
          travelpayoutsWhiteLabelSearchMarkup: String(siteIntegrations.travelpayoutsWhiteLabelSearchMarkup || '').trim() || null,
          travelpayoutsWhiteLabelResultsMarkup: String(siteIntegrations.travelpayoutsWhiteLabelResultsMarkup || '').trim() || null,
          getyourguideHeadScript: String(siteIntegrations.getyourguideHeadScript || '').trim() || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to save site integrations (${res.status})`);
      setSiteIntegrations({
        travelpayoutsDriveHeadScript: data?.travelpayoutsDriveHeadScript || data?.travelpayoutsHeadScript || '',
        travelpayoutsWhiteLabelHeadScript: data?.travelpayoutsWhiteLabelHeadScript || '',
        travelpayoutsWhiteLabelSearchMarkup: data?.travelpayoutsWhiteLabelSearchMarkup || '',
        travelpayoutsWhiteLabelResultsMarkup: data?.travelpayoutsWhiteLabelResultsMarkup || '',
        getyourguideHeadScript: data?.getyourguideHeadScript || '',
      });
      setSiteIntegrationsMessage('Site integrations saved.');
    } catch (e) {
      setSiteIntegrationsError(e.message || 'Failed to save site integrations.');
    } finally {
      setSiteIntegrationsSaving(false);
    }
  };

  const loadPartnerPanels = async () => {
    setPartnerPanelsError('');
    setPartnerPanelsMessage('');
    try {
      setPartnerPanelsLoading(true);
      const res = await fetch('/api/admin/platform-settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load partner panels (${res.status})`);
      const nextPanels = Array.isArray(data?.websitePartnerPanels)
        ? data.websitePartnerPanels.map((entry) => normalizePartnerPanel(entry))
        : [];
      setPartnerPanels(nextPanels);
    } catch (e) {
      setPartnerPanelsError(e.message || 'Failed to load partner panels.');
    } finally {
      setPartnerPanelsLoading(false);
    }
  };

  const updatePartnerPanelField = (panelId, field, value) => {
    setPartnerPanels((prev) => prev.map((panel) => {
      if (panel.id !== panelId) return panel;
      return normalizePartnerPanel({
        ...panel,
        [field]: value,
      });
    }));
    setPartnerPanelsError('');
    setPartnerPanelsMessage('');
  };

  const addPartnerPanel = () => {
    setPartnerPanels((prev) => [...prev, createEmptyPartnerPanel()]);
    setPartnerPanelsError('');
    setPartnerPanelsMessage('');
  };

  const removePartnerPanel = (panelId) => {
    setPartnerPanels((prev) => prev.filter((panel) => panel.id !== panelId));
    setPartnerPanelsError('');
    setPartnerPanelsMessage('');
  };

  const triggerPartnerLogoSelect = (panelId) => {
    const input = partnerPanelFileInputsRef.current?.[panelId];
    if (!input) return;
    input.value = '';
    input.click();
  };

  const handlePartnerLogoFileSelected = async (panelId, event) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    setPartnerPanelUploadingId(panelId);
    setPartnerPanelsError('');
    setPartnerPanelsMessage('');
    try {
      const presignRes = await fetch(
        `/api/admin/platform-settings/partner-logos/presign?filename=${encodeURIComponent(file.name)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const presignData = await presignRes.json().catch(() => ({}));
      if (!presignRes.ok) {
        throw new Error(presignData?.error || presignData?.message || 'Failed to presign partner logo upload.');
      }

      const putRes = await fetch(presignData.url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Upload failed (${putRes.status})`);
      }

      const finalizeRes = await fetch('/api/admin/platform-settings/partner-logos/finalize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ key: presignData.key }),
      });
      const finalizeData = await finalizeRes.json().catch(() => ({}));
      if (!finalizeRes.ok) {
        throw new Error(finalizeData?.error || finalizeData?.message || 'Failed to finalize partner logo upload.');
      }

      setPartnerPanels((prev) => prev.map((panel) => {
        if (panel.id !== panelId) return panel;
        return normalizePartnerPanel({
          ...panel,
          logoUrl: finalizeData.logoUrl || '',
          objectKey: finalizeData.objectKey || '',
        });
      }));
      setPartnerPanelsMessage('Partner logo uploaded.');
    } catch (e) {
      setPartnerPanelsError(e.message || 'Failed to upload partner logo.');
    } finally {
      setPartnerPanelUploadingId('');
    }
  };

  const savePartnerPanels = async () => {
    setPartnerPanelsSaving(true);
    setPartnerPanelsError('');
    setPartnerPanelsMessage('');
    try {
      const payloadPanels = (partnerPanels || []).map((panel) => {
        const normalized = normalizePartnerPanel(panel);
        return {
          id: normalized.id,
          title: normalized.title,
          description: normalized.description,
          url: normalized.url,
          logoUrl: normalized.logoUrl,
          objectKey: normalized.objectKey,
          enabled: normalized.enabled,
        };
      });
      const res = await fetch('/api/admin/platform-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          websitePartnerPanels: payloadPanels,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to save partner panels (${res.status})`);
      const nextPanels = Array.isArray(data?.websitePartnerPanels)
        ? data.websitePartnerPanels.map((entry) => normalizePartnerPanel(entry))
        : [];
      setPartnerPanels(nextPanels);
      setPartnerPanelsMessage('Partner panels saved.');
    } catch (e) {
      setPartnerPanelsError(e.message || 'Failed to save partner panels.');
    } finally {
      setPartnerPanelsSaving(false);
    }
  };

  useEffect(() => {
    if (subTab === 'Users') {
      loadUsers();
      return;
    }
    if (subTab === 'Billing' || subTab === 'Default prices') {
      loadBilling();
      return;
    }
    if (subTab === 'APIs') {
      loadApiSummary();
      return;
    }
    if (subTab === 'Webserver') {
      loadWebSummary();
      return;
    }
  }, [subTab, period]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    const refreshPendingBillingIntakeState = () => {
      if (!pendingBillingIntakeRefreshUserIdsRef.current.size) return;
      pendingBillingIntakeRefreshUserIdsRef.current.clear();
      loadUsers();
      loadBilling();
    };

    const handleWindowFocus = () => {
      refreshPendingBillingIntakeState();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshPendingBillingIntakeState();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [period, token]);

  useEffect(() => {
    if (subTab === 'Users' || subTab === 'Billing') {
      setQuery('');
    }
  }, [subTab]);

  useEffect(() => {
    return () => {
      if (billingDefaultsSaveTimeoutRef.current) {
        window.clearTimeout(billingDefaultsSaveTimeoutRef.current);
      }
      Object.values(billingSiteSaveTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      Object.values(billingProfileSaveTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
    };
  }, []);

  useEffect(() => {
    loadRateLimits();
  }, []);

  useEffect(() => {
    if (subTab === 'S3') {
      loadS3Status();
    }
  }, [subTab]);

  useEffect(() => {
    if (subTab === 'Logins') {
      loadLoginSettings();
    }
  }, [subTab]);

  useEffect(() => {
    if (subTab === 'Site Integrations') {
      loadSiteIntegrations();
    }
  }, [subTab]);

  useEffect(() => {
    if (subTab === 'Partner Logos') {
      loadPartnerPanels();
    }
  }, [subTab]);

  useEffect(() => {
    if (subTab !== 'S3') return;
    if (!s3Status?.running) return;
    const interval = setInterval(() => {
      loadS3Status();
    }, 2000);
    return () => clearInterval(interval);
  }, [subTab, s3Status?.running]);

  const filteredUsers = useMemo(() => {
    return filterUsersByEmail(users, query);
  }, [query, users]);

  const filteredUsersPackageSummary = useMemo(() => {
    return filteredUsers.reduce((acc, user) => {
      const counts = user.subscriptionSummary?.packageCounts || {};
      SUBSCRIPTION_DISPLAY_ORDER.forEach((key) => {
        acc[key] = (acc[key] || 0) + Number(counts[key] || 0);
      });
      return acc;
    }, SUBSCRIPTION_DISPLAY_ORDER.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {}));
  }, [filteredUsers]);

  const filteredUsersRuntimeSummary = useMemo(() => {
    return filteredUsers.reduce((acc, user) => {
      const tier = String(user.subscriptionSummary?.effectiveSubscriptionType || user.subscription_type || 'free').toLowerCase();
      if (SUBSCRIPTION_DISPLAY_ORDER.includes(tier)) {
        acc[tier] = (acc[tier] || 0) + 1;
      }
      return acc;
    }, SUBSCRIPTION_DISPLAY_ORDER.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {}));
  }, [filteredUsers]);

  const filteredBillingUsers = useMemo(() => {
    return filterUsersByEmail(billingUsers, query);
  }, [billingUsers, query]);

  const displayBillingUsers = useMemo(() => {
    return filteredBillingUsers.map((user) => {
      const billingProfile = normalizeBillingProfile(user.billingProfile || EMPTY_BILLING_PROFILE, user.billingSites || [], billingContractCatalog);
      const sites = (user.sites || []).map((site) => {
        const siteDraft = billingSiteDrafts[site.id] || null;
        const resolvedPricing = siteDraft
          ? normalizeBillingPricing(siteDraft.pricing, site.pricingOverride || billingDefaults)
          : normalizeBillingPricing(site.pricingOverride || billingDefaults, billingDefaults);
        const resolvedFeatureSelection = normalizeBillingFeatureSelection(
          siteDraft?.featureSelection || site.featureSelection,
          site
        );
        const resolvedPackageKey = normalizeBillingPackageKey(
          siteDraft?.packageKey || site.packageKey || site.defaultPackageKey,
          site.defaultPackageKey || 'free'
        );
        const resolvedQuantityOverrides = normalizeBillingQuantityOverrides(
          siteDraft?.quantityOverrides || site.quantityOverrides
        );
        const assignedPackageEstimate =
          computePackageEstimate(site, resolvedPackageKey, resolvedPricing, billingPackageCatalog, resolvedQuantityOverrides);
        const manualMonthlyTotal = computeCurrentBillingMonthlyTotal(
          site,
          resolvedPricing,
          resolvedFeatureSelection,
          resolvedQuantityOverrides,
          resolvedPackageKey,
          billingPackageCatalog
        );
        return {
          ...site,
          resolvedPricing,
          resolvedFeatureSelection,
          resolvedQuantityOverrides,
          resolvedPackageKey,
          assignedPackageEstimate,
          manualMonthlyTotal,
        };
      });
      const coveredSites = getCoveredBillingSites(billingProfile, sites);
      const effectiveBillingEntityProfile = getEffectiveBillingEntityProfile(billingProfile, coveredSites);
      const companyProfile = billingCompanies.find((company) => company.key === billingProfile.issuerCompanyKey)
        || billingCompanies[0]
        || PLATFORM_COMPANY_PROFILE;
      const uncoveredSites = sites.filter((site) => !coveredSites.some((coveredSite) => Number(coveredSite.id) === Number(site.id)));
      const coveredManualMonthlyTotal = coveredSites.reduce((sum, site) => sum + Number(site.manualMonthlyTotal || 0), 0);
      const customerMissingFields = getBillingProfileMissingFields(billingProfile, coveredSites);
      const issuerMissingFields = getCompanyProfileMissingFields(companyProfile);
      const invoiceSchedule = computeInvoiceSchedule(billingProfile);
      const invoiceSubtotal = coveredSites.reduce(
        (sum, site) => sum + (Number(site.manualMonthlyTotal || 0) * Number(invoiceSchedule.cadenceMonths || 1)),
        0
      );
      const invoiceVatAmount = Math.round(invoiceSubtotal * (normalizeVatPercent(billingVatPercent) / 100));
      const invoiceTotalWithVat = invoiceSubtotal + invoiceVatAmount;
      const invoiceLineItems = coveredSites.map((site) => ({
        id: site.id,
        description: `Suitestream.tv subscription services for ${site.name} (${formatIsoDate(invoiceSchedule.periodStart)} - ${formatIsoDate(invoiceSchedule.periodEndInclusive)})`,
        unit: 'site-month',
        quantity: Number(invoiceSchedule.cadenceMonths || 1),
        unitPrice: Number(site.manualMonthlyTotal || 0),
        amount: Number(site.manualMonthlyTotal || 0) * Number(invoiceSchedule.cadenceMonths || 1),
      }));
      return {
        ...user,
        billingProfile,
        companyProfile,
        effectiveBillingEntityProfile,
        activeBillingSite: coveredSites[0] || null,
        sites,
        coveredSites,
        uncoveredSites,
        invoiceSchedule,
        invoiceSiteLogoUrl: companyProfile.logoUrl || DEFAULT_INVOICE_LOGO_URL,
        invoiceLineItems,
        invoiceSubtotal,
        invoiceVatAmount,
        invoiceTotalWithVat,
        monthlyTotal: sites.reduce((sum, site) => sum + Number(site.manualMonthlyTotal || 0), 0),
        monthlyTotalWithVat: applyVatToAmount(
          sites.reduce((sum, site) => sum + Number(site.manualMonthlyTotal || 0), 0),
          billingVatPercent
        ),
        coveredManualMonthlyTotal,
        coveredManualMonthlyTotalWithVat: applyVatToAmount(coveredManualMonthlyTotal, billingVatPercent),
        invoiceReadiness: {
          issuerMissingFields,
          customerMissingFields,
          ready: issuerMissingFields.length === 0 && customerMissingFields.length === 0,
        },
      };
    });
  }, [billingCompanies, billingDefaults, billingPackageCatalog, billingSiteDrafts, billingVatPercent, filteredBillingUsers]);

  const toggleExpandedUser = (userId) => {
    setEditingUserId((prev) => (prev === userId ? null : userId));
  };

  const toggleExpandedBillingUser = (userId) => {
    setEditingBillingUserId((prev) => (prev === userId ? null : userId));
  };

  const toggleExpandedInvoicePreview = (userId) => {
    setExpandedInvoicePreviewUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleBillingContractsCollapsed = (userId) => {
    setCollapsedBillingContractsByUserId((prev) => ({
      ...prev,
      [userId]: !(prev[userId] ?? true),
    }));
  };

  const toggleBillingWorkbenchCollapsed = (userId) => {
    setCollapsedBillingWorkbenchByUserId((prev) => ({
      ...prev,
      [userId]: !(prev[userId] ?? true),
    }));
  };

  const toggleTimeMaterialsCollapsed = (userId) => {
    setCollapsedTimeMaterialsByUserId((prev) => ({
      ...prev,
      [userId]: !(prev[userId] ?? true),
    }));
  };

  const updateTimeAndMaterials = (user, nextValue) => {
    updateBillingProfileDraft(user, 'timeAndMaterials', nextValue, { save: false });
  };

  const loadInvoiceDraft = (user, documentEntry) => {
    const snapshot = documentEntry?.draftSnapshot;
    if (!snapshot) return;
    const currentDraft = getBillingProfileDraft(user);
    const nextDraft = normalizeBillingProfile({
      ...currentDraft,
      timeAndMaterials: snapshot.timeAndMaterials || currentDraft.timeAndMaterials,
      billingSources: snapshot.billingSources || currentDraft.billingSources,
      combinedInvoice: snapshot.combinedInvoice || currentDraft.combinedInvoice,
      contractDraft: snapshot.contractDraft || currentDraft.contractDraft,
      invoiceStartDate: snapshot.invoiceStartDate || currentDraft.invoiceStartDate,
      invoiceCadenceMonths: snapshot.invoiceCadenceMonths || currentDraft.invoiceCadenceMonths,
    }, user.billingSites || [], billingContractCatalog);
    setBillingProfileDrafts((prev) => ({ ...prev, [user.id]: nextDraft }));
    setCollapsedTimeMaterialsByUserId((prev) => ({ ...prev, [user.id]: false }));
    setMessage(`Loaded ${documentEntry.referenceNumber || 'saved invoice'} as an editable draft. Your saved PDF is unchanged.`);
  };

  const renderSavedInvoiceList = (user, documents, emptyMessage) => (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(71,85,105,0.5)' }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>Saved Invoices</div>
      {documents.length ? (
        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
          {documents.map((documentEntry) => {
            const deletionKey = `${user.id}:${documentEntry.id}`;
            const isDeleting = Boolean(invoiceDocumentDeletingIds[deletionKey]);
            return (
              <div
                key={`${user.id}:panel-invoice:${documentEntry.id}`}
                style={{
                  padding: 10,
                  borderRadius: 10,
                  border: '1px solid rgba(71,85,105,0.5)',
                  background: 'rgba(2,6,23,0.32)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'grid', gap: 3 }}>
                  <div style={{ fontWeight: 700 }}>{documentEntry.referenceNumber || documentEntry.fileName || 'Stored invoice'}</div>
                  <div style={{ fontSize: 11, color: '#cbd5e1' }}>
                    {formatIsoDate(documentEntry.issueDate)} • {formatVnd(documentEntry.totalWithVat || 0)}
                    {documentEntry.issuerCompanyName ? ` • ${documentEntry.issuerCompanyName}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {documentEntry.draftSnapshot && (
                    <button type="button" disabled={isDeleting} onClick={() => loadInvoiceDraft(user, documentEntry)}>
                      Load & Adjust
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={!documentEntry.downloadUrl || isDeleting}
                    onClick={() => openDownloadUrl(documentEntry.downloadUrl)}
                  >
                    Download
                  </button>
                  <button type="button" disabled={isDeleting} onClick={() => deleteInvoiceDocument(user, documentEntry)}>
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ marginTop: 7, fontSize: 12, color: '#9ca3af' }}>{emptyMessage}</div>
      )}
    </div>
  );

  const openDownloadUrl = (downloadUrl) => {
    const normalized = String(downloadUrl || '').trim();
    if (!normalized || typeof document === 'undefined') return;
    const anchor = document.createElement('a');
    anchor.href = normalized;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const openBillingIntakeForm = (userId, intakeUrl) => {
    const normalized = String(intakeUrl || '').trim();
    if (!normalized) return;
    pendingBillingIntakeRefreshUserIdsRef.current.add(Number(userId));
    openDownloadUrl(normalized);
  };

  const copyBillingIntakeUrl = async (userId, intakeUrl) => {
    const normalized = String(intakeUrl || '').trim();
    if (!normalized) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalized);
      } else if (typeof window !== 'undefined') {
        window.prompt('Copy billing intake link', normalized);
      }
      setBillingIntakeCopiedIds((prev) => ({ ...prev, [userId]: true }));
      window.setTimeout(() => {
        setBillingIntakeCopiedIds((prev) => ({ ...prev, [userId]: false }));
      }, 1800);
      setMessage('Billing intake link copied.');
    } catch (error) {
      setError('Failed to copy billing intake link.');
    }
  };

  const copyRedInvoiceInfo = async ({ userId, issuerProfile, invoiceDocument, customerProfile, coveredSites, billingScopeLabel }) => {
    if (!invoiceDocument) return;
    const clipboardText = buildRedInvoiceClipboardText({
      issuer: issuerProfile,
      customer: customerProfile,
      invoiceDocument,
      coveredSites,
      billingScopeLabel,
    });
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
      } else if (typeof window !== 'undefined') {
        window.prompt('Copy red invoice info', clipboardText);
      }
      setRedInvoiceCopiedIds((prev) => ({ ...prev, [userId]: true }));
      window.setTimeout(() => {
        setRedInvoiceCopiedIds((prev) => ({ ...prev, [userId]: false }));
      }, 1800);
      setMessage('Red invoice info copied.');
    } catch (error) {
      setError('Failed to copy red invoice info.');
    }
  };

  const generateBillingIntakeLink = async (user) => {
    setBillingIntakeGeneratingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/billing/${user.id}/intake-link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Failed to generate billing intake link.');
      }
      const nextBillingProfile = normalizeBillingProfile(
        data?.billingProfile || user.billingProfile || EMPTY_BILLING_PROFILE,
        user.billingSites || [],
        billingContractCatalog
      );
      const nextUser = {
        ...user,
        billingProfile: nextBillingProfile,
      };
      setBillingUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
      setUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
      setBillingProfileDrafts((prev) => ({
        ...prev,
        [user.id]: nextBillingProfile,
      }));
      setMessage(data?.message || `Billing intake link generated for ${user.email}.`);
    } catch (e) {
      setError(e?.message || 'Failed to generate billing intake link.');
    } finally {
      setBillingIntakeGeneratingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const generateInvoicePdf = async (user, invoiceMode = 'standard') => {
    const billingProfileDraft = getBillingProfileDraft(user);
    setInvoicePdfGeneratingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/billing/${user.id}/invoices/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          billingProfile: billingProfileDraft,
          invoiceMode,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const missingFields = Array.isArray(data?.missingFields) && data.missingFields.length
          ? ` Missing: ${data.missingFields.join(', ')}.`
          : '';
        throw new Error((data?.message || 'Failed to generate invoice PDF.') + missingFields);
      }

      const nextBillingProfile = normalizeBillingProfile(
        data?.billingProfile || billingProfileDraft,
        user.billingSites || [],
        billingContractCatalog
      );
      const nextUser = {
        ...user,
        billingProfile: nextBillingProfile,
      };
      setBillingUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
      setUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
      setBillingProfileDrafts((prev) => ({
        ...prev,
        [user.id]: nextBillingProfile,
      }));
      setMessage(`${invoiceMode === 'combined' ? 'Combined invoice' : 'Invoice'} PDF generated for ${user.email}.`);
      if (data?.document?.downloadUrl) {
        openDownloadUrl(data.document.downloadUrl);
      }
    } catch (e) {
      setError(e?.message || 'Failed to download invoice PDF.');
    } finally {
      setInvoicePdfGeneratingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const applyReturnedBillingProfile = (user, billingProfileValue) => {
    const nextBillingProfile = normalizeBillingProfile(
      billingProfileValue || getBillingProfileDraft(user),
      user.billingSites || [],
      billingContractCatalog
    );
    const nextUser = {
      ...user,
      billingProfile: nextBillingProfile,
    };
    setBillingUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
    setUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
    setBillingProfileDrafts((prev) => ({
      ...prev,
      [user.id]: nextBillingProfile,
    }));
    return nextBillingProfile;
  };

  const generateContractPdf = async (user) => {
    const billingProfileDraft = getBillingProfileDraft(user);
    setContractPdfGeneratingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/billing/${user.id}/contracts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          billingProfile: billingProfileDraft,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const missingFields = Array.isArray(data?.missingFields) && data.missingFields.length
          ? ` Missing: ${data.missingFields.join(', ')}.`
          : '';
        throw new Error((data?.message || 'Failed to generate contract PDF.') + missingFields);
      }

      const nextBillingProfile = normalizeBillingProfile(
        data?.billingProfile || billingProfileDraft,
        user.billingSites || [],
        billingContractCatalog
      );
      const nextUser = {
        ...user,
        billingProfile: nextBillingProfile,
      };
      setBillingUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
      setUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...nextUser } : entry)));
      setBillingProfileDrafts((prev) => ({
        ...prev,
        [user.id]: nextBillingProfile,
      }));
      setMessage(`Contract PDF generated for ${user.email}.`);
      if (data?.document?.downloadUrl) {
        openDownloadUrl(data.document.downloadUrl);
      }
    } catch (e) {
      setError(e?.message || 'Failed to generate contract PDF.');
    } finally {
      setContractPdfGeneratingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const deleteInvoiceDocument = async (user, documentEntry) => {
    const documentId = String(documentEntry?.id || '').trim();
    if (!documentId) return;
    if (!window.confirm(`Delete invoice ${documentEntry.referenceNumber || documentEntry.fileName || documentId}? This removes the PDF from S3.`)) {
      return;
    }
    const deletionKey = `${user.id}:${documentId}`;
    setInvoiceDocumentDeletingIds((prev) => ({ ...prev, [deletionKey]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/billing/${user.id}/invoices/${encodeURIComponent(documentId)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to delete invoice PDF.');
      applyReturnedBillingProfile(user, data?.billingProfile);
      setMessage(data?.message || `Invoice deleted for ${user.email}.`);
    } catch (e) {
      setError(e?.message || 'Failed to delete invoice PDF.');
    } finally {
      setInvoiceDocumentDeletingIds((prev) => ({ ...prev, [deletionKey]: false }));
    }
  };

  const resetInvoicing = async (user) => {
    if (!window.confirm(`Reset invoicing for ${user.email}? This clears stored invoice PDFs and removes the current billing cycle start date.`)) {
      return;
    }
    setInvoiceResettingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/billing/${user.id}/invoices/reset`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to reset invoicing.');
      applyReturnedBillingProfile(user, data?.billingProfile);
      setMessage(data?.message || `Invoicing reset for ${user.email}.`);
    } catch (e) {
      setError(e?.message || 'Failed to reset invoicing.');
    } finally {
      setInvoiceResettingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const deleteContractDocument = async (user, documentEntry) => {
    const documentId = String(documentEntry?.id || '').trim();
    if (!documentId) return;
    if (!window.confirm(`Delete contract ${documentEntry.referenceNumber || documentEntry.fileName || documentId}? This removes the PDF from S3.`)) {
      return;
    }
    const deletionKey = `${user.id}:${documentId}`;
    setContractDocumentDeletingIds((prev) => ({ ...prev, [deletionKey]: true }));
    setError('');
    try {
      const res = await fetch(`/api/admin/users/billing/${user.id}/contracts/${encodeURIComponent(documentId)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to delete contract PDF.');
      applyReturnedBillingProfile(user, data?.billingProfile);
      setMessage(data?.message || `Contract deleted for ${user.email}.`);
    } catch (e) {
      setError(e?.message || 'Failed to delete contract PDF.');
    } finally {
      setContractDocumentDeletingIds((prev) => ({ ...prev, [deletionKey]: false }));
    }
  };

  const updateBillingDefaultsDraft = (key, value) => {
    setBillingDefaultsDraft((prev) => {
      const nextDraft = { ...prev, [key]: value };
      const nextPricing = normalizeBillingPricing(nextDraft, billingDefaults);
      setBillingDefaults(nextPricing);
      if (billingDefaultsSaveTimeoutRef.current) window.clearTimeout(billingDefaultsSaveTimeoutRef.current);
      billingDefaultsSaveTimeoutRef.current = window.setTimeout(() => {
        saveBillingDefaults(nextDraft, billingVatDraft);
      }, 350);
      return nextDraft;
    });
  };

  const updateBillingVatDraft = (value) => {
    const nextVatPercent = normalizeVatPercent(value, billingVatPercent);
    setBillingVatDraft(String(nextVatPercent));
    setBillingVatPercent(nextVatPercent);
    if (billingDefaultsSaveTimeoutRef.current) window.clearTimeout(billingDefaultsSaveTimeoutRef.current);
    billingDefaultsSaveTimeoutRef.current = window.setTimeout(() => {
      saveBillingDefaults(billingDefaultsDraft, nextVatPercent);
    }, 350);
  };

  const queueSaveBillingSiteDraft = (site, nextDraft) => {
    if (billingSiteSaveTimeoutsRef.current[site.id]) {
      window.clearTimeout(billingSiteSaveTimeoutsRef.current[site.id]);
    }
    billingSiteSaveTimeoutsRef.current[site.id] = window.setTimeout(() => {
      saveBillingSitePricing(site, nextDraft);
    }, 350);
  };

  const updateBillingSitePricingDraft = (site, key, value) => {
    setBillingSiteDrafts((prev) => {
      const existingDraft = prev[site.id] || buildBillingSiteDraft(site, billingDefaults);
      const nextDraft = {
        ...existingDraft,
        pricing: {
          ...existingDraft.pricing,
          [key]: value,
        },
      };
      queueSaveBillingSiteDraft(site, nextDraft);
      return {
        ...prev,
        [site.id]: nextDraft,
      };
    });
  };

  const toggleBillingSiteFeatureSelection = (site, key) => {
    setBillingSiteDrafts((prev) => {
      const existingDraft = prev[site.id] || buildBillingSiteDraft(site, billingDefaults);
      const nextDraft = {
        ...existingDraft,
        featureSelection: {
          ...existingDraft.featureSelection,
          [key]: key === 'sites' ? true : !existingDraft.featureSelection?.[key],
        },
        quantityOverrides: key === 'sites'
          ? {
            ...existingDraft.quantityOverrides,
            sites: 1,
          }
          : existingDraft.quantityOverrides,
      };
      queueSaveBillingSiteDraft(site, nextDraft);
      return {
        ...prev,
        [site.id]: nextDraft,
      };
    });
  };

  const updateBillingSiteQuantityOverride = (site, key, value) => {
    setBillingSiteDrafts((prev) => {
      const existingDraft = prev[site.id] || buildBillingSiteDraft(site, billingDefaults);
      const nextDraft = {
        ...existingDraft,
        quantityOverrides: {
          ...existingDraft.quantityOverrides,
          [key]: key === 'sites' ? 1 : (value === '' ? null : value),
        },
        featureSelection: key === 'sites'
          ? {
            ...existingDraft.featureSelection,
            sites: true,
          }
          : existingDraft.featureSelection,
      };
      queueSaveBillingSiteDraft(site, nextDraft);
      return {
        ...prev,
        [site.id]: nextDraft,
      };
    });
  };

  const updateBillingSitePackageDraft = (site, packageKey) => {
    setBillingSiteDrafts((prev) => {
      const existingDraft = prev[site.id] || buildBillingSiteDraft(site, billingDefaults);
      const packageConfig = (billingPackageCatalog || DEFAULT_BILLING_PACKAGE_CATALOG)[packageKey]
        || DEFAULT_BILLING_PACKAGE_CATALOG[packageKey];
      const nextQuantityOverrides = {
        ...existingDraft.quantityOverrides,
        sites: 1,
      };
      const nextDraft = {
        ...existingDraft,
        packageKey,
        scenarioPackageKey: packageKey,
        pricing: {
          ...existingDraft.pricing,
          sites: packageConfig?.basePrice == null
            ? existingDraft.pricing?.sites
            : String(packageConfig.basePrice),
        },
        featureSelection: {
          ...existingDraft.featureSelection,
          ...getRecommendedFeatureSelectionForPackage(site, packageKey, nextQuantityOverrides, billingPackageCatalog),
          sites: true,
        },
        quantityOverrides: nextQuantityOverrides,
      };
      queueSaveBillingSiteDraft(site, nextDraft);
      return {
        ...prev,
        [site.id]: nextDraft,
      };
    });
  };

  const loadUserUsage = async (userId) => {
    const key = `${userId}:${period}`;
    setUserUsageByKey((prev) => ({
      ...prev,
      [key]: prev[key] || { loading: true, error: '', data: null },
    }));
    try {
      const res = await fetch(`/api/admin/api-usage/users/${userId}?period=${encodeURIComponent(period)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to load user usage (${res.status})`);
      setUserUsageByKey((prev) => ({ ...prev, [key]: { loading: false, error: '', data } }));
    } catch (e) {
      setUserUsageByKey((prev) => ({ ...prev, [key]: { loading: false, error: e.message || 'Failed to load usage', data: null } }));
    }
  };

  const getRateLimitRow = ({ userId, api }) => {
    return (rateLimits || []).find((r) => {
      return (
        Number(r.userId || 0) === Number(userId || 0) &&
        r.api === api &&
        r.scope === 'user' &&
        r.period === 'day' &&
        (r.operation || '') === '' &&
        (r.feature || '') === ''
      );
    }) || null;
  };

  const getLimitDraftValue = ({ userId, api, field }) => {
    const key = `${userId}:${api}:${field}`;
    if (limitsDraft[key] !== undefined) return limitsDraft[key];
    const row = getRateLimitRow({ userId, api });
    if (!row) return '';
    if (field === 'maxCalls') return row.maxCalls == null ? '' : String(row.maxCalls);
    if (field === 'maxTotalTokens') return row.maxTotalTokens == null ? '' : String(row.maxTotalTokens);
    if (field === 'enabled') return row.enabled !== false;
    return '';
  };

  const setLimitDraftValue = ({ userId, api, field, value }) => {
    const key = `${userId}:${api}:${field}`;
    setLimitsDraft((prev) => ({ ...prev, [key]: value }));
  };

  const saveRateLimit = async ({ userId, api, enabled, maxCalls, maxTotalTokens }) => {
    const limitKey = `${userId}:${api}`;
    setSavingLimitKey(limitKey);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/admin/api-rate-limits', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          api,
          scope: 'user',
          period: 'day',
          operation: '',
          feature: '',
          enabled,
          maxCalls: maxCalls === '' ? null : Number(maxCalls),
          maxTotalTokens: maxTotalTokens === '' ? null : Number(maxTotalTokens),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to save rate limit (${res.status})`);
      await loadRateLimits();
      setMessage(`Saved rate limit for ${api}${userId ? ` (user ${userId})` : ''}`);
    } catch (e) {
      setError(e.message || 'Failed to save rate limit.');
    } finally {
      setSavingLimitKey('');
    }
  };

  const getDraftForUser = (user) => {
    const entry = draft[user.id] || {};
    return {
      role: entry.role ?? user.role ?? 'user',
      billingProfile: normalizeBillingProfile(
        entry.billingProfile ?? user.billingProfile ?? EMPTY_BILLING_PROFILE,
        user.billingSites || [],
        billingContractCatalog
      ),
    };
  };

  const setDraftField = (userId, field, value) => {
    setDraft((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value,
      },
    }));
  };

  const setDraftBillingField = (user, field, value) => {
    const nextProfile = {
      ...getDraftForUser(user).billingProfile,
      [field]: value,
    };
    setDraftField(user.id, 'billingProfile', nextProfile);
  };

  const setDraftSiteBillingField = (user, siteId, field, value) => {
    const currentProfile = getDraftForUser(user).billingProfile;
    const existingEntity = normalizeBillingIdentityFields(
      currentProfile.siteBillingEntities?.[String(siteId)] || {},
      currentProfile
    );
    const nextProfile = {
      ...currentProfile,
      siteBillingEntities: {
        ...(currentProfile.siteBillingEntities || {}),
        [String(siteId)]: {
          ...existingEntity,
          [field]: field === 'countryCode' ? String(value || '').toUpperCase() : value,
        },
      },
    };
    setDraftField(user.id, 'billingProfile', nextProfile);
  };

  const toggleDraftBillingSite = (user, siteId) => {
    const currentProfile = getDraftForUser(user).billingProfile;
    if (currentProfile.billingScope === 'per_site') {
      const selectedSiteIds = new Set(currentProfile.selectedSiteIds || []);
      if (selectedSiteIds.has(siteId)) selectedSiteIds.delete(siteId);
      else selectedSiteIds.add(siteId);
      const nextSelectedSiteIds = Array.from(selectedSiteIds).sort((a, b) => a - b);
      const nextActiveBillingSiteId = nextSelectedSiteIds.includes(Number(currentProfile.activeBillingSiteId || 0))
        ? Number(currentProfile.activeBillingSiteId || 0)
        : (nextSelectedSiteIds[0] || null);
      setDraftField(user.id, 'billingProfile', {
        ...currentProfile,
        selectedSiteIds: nextSelectedSiteIds,
        activeBillingSiteId: nextActiveBillingSiteId,
      });
      return;
    }
    const selectedSiteIds = new Set(currentProfile.selectedSiteIds || []);
    if (selectedSiteIds.has(siteId)) selectedSiteIds.delete(siteId);
    else selectedSiteIds.add(siteId);
    setDraftField(user.id, 'billingProfile', {
      ...currentProfile,
      selectedSiteIds: Array.from(selectedSiteIds).sort((a, b) => a - b),
    });
  };

  const saveUser = async (user) => {
    const next = getDraftForUser(user);
    const updates = {};
    if ((user.role || 'user') !== next.role) updates.role = next.role;
    const currentBillingProfile = normalizeBillingProfile(user.billingProfile || EMPTY_BILLING_PROFILE, user.billingSites || [], billingContractCatalog);
    if (JSON.stringify(currentBillingProfile) !== JSON.stringify(next.billingProfile)) {
      updates.billingProfile = next.billingProfile;
    }
    if (Object.keys(updates).length === 0) return true;

    setSavingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to update user (${res.status})`);

      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...data.user } : u)));
      setDraft((prev) => {
        const nextDraft = { ...prev };
        delete nextDraft[user.id];
        return nextDraft;
      });
      setMessage(`Updated ${user.email}`);
      return true;
    } catch (e) {
      setError(e.message || 'Failed to update user.');
      return false;
    } finally {
      setSavingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const deleteUser = async (user) => {
    const isSelf = currentUserId && user.id === currentUserId;
    if (isSelf) {
      setError('You cannot delete your own admin account from here.');
      return;
    }
    const confirmed = window.prompt(
      `Type DELETE ${user.email} to permanently remove this user, all their sites/destinations, and their S3 content.`
    );
    if (confirmed !== `DELETE ${user.email}`) {
      return;
    }

    setDeletingIds((prev) => ({ ...prev, [user.id]: true }));
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Failed to delete user (${res.status})`);

      setUsers((prev) => prev.filter((entry) => entry.id !== user.id));
      setDraft((prev) => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
      setEditingUserId((prev) => (prev === user.id ? null : prev));
      setMessage(data?.message || `Deleted ${user.email}`);
    } catch (e) {
      setError(e.message || 'Failed to delete user.');
    } finally {
      setDeletingIds((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  return (
    <div className="admin-shell" style={{ padding: 16, color: '#e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Admin</h2>
          <div className="admin-tab-strip" style={{ display: 'flex', gap: 8 }}>
            {['Users', 'Billing', 'Default prices', 'Network Ads', 'APIs', 'Webserver', 'Logins', 'Site Integrations', 'Partner Logos', 'S3'].map((t) => (
              <button
                key={t}
                className={`admin-tab-button${subTab === t ? ' admin-tab-button--active' : ''}`}
                type="button"
                onClick={() => setSubTab(t)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: subTab === t ? '1px solid rgba(125,211,252,0.75)' : '1px solid rgba(71,85,105,0.75)',
                  background: subTab === t
                    ? 'linear-gradient(135deg, rgba(8,47,73,0.96), rgba(30,41,59,0.98))'
                    : 'linear-gradient(135deg, rgba(30,41,59,0.82), rgba(15,23,42,0.94))',
                  color: subTab === t ? '#e0f2fe' : '#e5e7eb',
                  boxShadow: subTab === t ? '0 10px 24px rgba(2,6,23,0.28)' : '0 6px 16px rgba(2,6,23,0.18)',
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {subTab !== 'Billing' && subTab !== 'Default prices' && subTab !== 'Network Ads' && subTab !== 'S3' && subTab !== 'Logins' && subTab !== 'Site Integrations' && subTab !== 'Partner Logos' && (
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIOD_OPTIONS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          )}
          {subTab !== 'Network Ads' && (
            <button
              className="admin-toolbar-button"
              type="button"
              onClick={() => {
                if (subTab === 'Users') loadUsers();
                else if (subTab === 'Billing' || subTab === 'Default prices') loadBilling();
                else if (subTab === 'APIs') {
                  loadApiSummary();
                  loadRateLimits();
                } else if (subTab === 'Webserver') {
                  loadWebSummary();
                } else if (subTab === 'S3') {
                  loadS3Status();
                } else if (subTab === 'Logins') {
                  loadLoginSettings();
                } else if (subTab === 'Site Integrations') {
                  loadSiteIntegrations();
                } else if (subTab === 'Partner Logos') {
                  loadPartnerPanels();
                }
              }}
            >
              Refresh
            </button>
          )}
        </div>
      </div>

      {subTab === 'Users' && (
        <p style={{ marginTop: 8, color: '#9ca3af' }}>
          Promote/demote users, manage billing identity, and review the runtime tier derived from billed site packages. Users must log out + back in to receive updated role/tier in their JWT.
        </p>
      )}

      {subTab === 'Billing' && (
        <p style={{ marginTop: 8, color: '#9ca3af' }}>
              Account-level feature totals. Edit a user to see the site-by-site breakdown and account controls.
        </p>
      )}

      {subTab === 'Default prices' && (
        <p style={{ marginTop: 8, color: '#9ca3af' }}>
          Default monthly pricing presets for new sites. Billing totals use these values unless a site has its own override.
        </p>
      )}

      {(subTab === 'Users' || subTab === 'Billing') && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
          <input
            type="search"
            name="admin-user-filter"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by email…"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            style={{
              flex: 1,
              maxWidth: 420,
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #374151',
              background: '#111827',
              color: '#e5e7eb',
            }}
          />
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, border: '1px solid #065f46', background: 'rgba(6,95,70,0.2)' }}>
          {message}
        </div>
      )}

      {subTab === 'Network Ads' && <NetworkAdsAdmin />}
      {billingWarning && (subTab === 'Billing' || subTab === 'Default prices') && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 8, border: '1px solid #92400e', background: 'rgba(146,64,14,0.18)' }}>
          {billingWarning}
        </div>
      )}

      {subTab === 'S3' && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #374151', background: '#111827' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>S3 Asset Conversion</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
            Converts all image assets to WebP + 200px thumbnails and all video assets to WebM. This modifies S3 content and should be run
            during a low-traffic window.
          </div>

          {s3Error && (
            <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
              {s3Error}
            </div>
          )}
          {s3Message && (
            <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, border: '1px solid #065f46', background: 'rgba(6,95,70,0.2)' }}>
              {s3Message}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button type="button" onClick={startS3ConvertAll} disabled={s3Loading || s3Status?.running}>
              {s3Status?.running ? 'Running…' : 'Convert All'}
            </button>
            <button type="button" onClick={loadS3Status} disabled={s3Loading}>
              {s3Loading ? 'Refreshing…' : 'Refresh Status'}
            </button>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              Log file: {s3Status?.logPath || '—'}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>
              {s3Status?.running ? 'Conversion running' : 'Idle'}
            </div>
            <div style={{ height: 10, borderRadius: 6, background: '#1f2937', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: s3Status?.progress?.total
                    ? `${Math.min(100, Math.round((s3Status.progress.processed / s3Status.progress.total) * 100))}%`
                    : '0%',
                  background: '#22c55e',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
              {s3Status?.progress?.processed || 0}/{s3Status?.progress?.total || 0} processed · {s3Status?.progress?.converted || 0} converted ·{' '}
              {s3Status?.progress?.skipped || 0} skipped · {s3Status?.progress?.failed || 0} failed
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
              Started: {s3Status?.startedAt ? new Date(s3Status.startedAt).toLocaleString() : '—'} · Finished:{' '}
              {s3Status?.finishedAt ? new Date(s3Status.finishedAt).toLocaleString() : '—'}
            </div>
          </div>
        </div>
      )}

      {subTab === 'Logins' && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #374151', background: '#111827' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Login + Signup Controls</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
            Disable new signups to block account creation. Users will see: &quot;Sorry, new signups arent available at the moment, please try again later&quot;.
          </div>

          {loginSettingsError && (
            <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
              {loginSettingsError}
            </div>
          )}
          {loginSettingsMessage && (
            <div style={{ marginBottom: 10, padding: 10, borderRadius: 8, border: '1px solid #065f46', background: 'rgba(6,95,70,0.2)' }}>
              {loginSettingsMessage}
            </div>
          )}

          {loginSettingsLoading && <div style={{ color: '#9ca3af', fontSize: 12 }}>Loading login settings…</div>}

          {!loginSettingsLoading && loginSettings && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(loginSettings.enableSignups)}
                  onChange={(e) => updateLoginSettings({ enableSignups: e.target.checked })}
                  disabled={loginSettingsSaving}
                />
                <span>Enable New Signups</span>
              </label>

              <div style={{ marginLeft: 26, marginBottom: 6, color: '#9ca3af', fontSize: 12 }}>
                Override instructions: enable the override switch, then append <code>?override=1</code> to the signup URL (e.g. <code>/signup?override=1</code>) to allow a single signup.
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(loginSettings.enableOverride)}
                  onChange={(e) => updateLoginSettings({ enableOverride: e.target.checked })}
                  disabled={loginSettingsSaving}
                />
                <span>Enable Override</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={Boolean(loginSettings.enable2faForNewLogins)}
                  onChange={(e) => updateLoginSettings({ enable2faForNewLogins: e.target.checked })}
                  disabled={loginSettingsSaving}
                />
                <span>Enable 2fa for new logins (mock)</span>
              </label>

              {loginSettingsSaving && <div style={{ color: '#9ca3af', fontSize: 12 }}>Saving…</div>}
            </>
          )}
        </div>
      )}

      {subTab === 'Site Integrations' && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #374151', background: '#111827', display: 'grid', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Site Integrations</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              These platform-wide monetisation scripts apply to landing pages and monetised websites automatically. Website users cannot override them.
            </div>
          </div>

          {siteIntegrationsError && (
            <div style={{ padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
              {siteIntegrationsError}
            </div>
          )}
          {siteIntegrationsMessage && (
            <div style={{ padding: 10, borderRadius: 8, border: '1px solid #065f46', background: 'rgba(6,95,70,0.2)' }}>
              {siteIntegrationsMessage}
            </div>
          )}

          {siteIntegrationsLoading && <div style={{ color: '#9ca3af', fontSize: 12 }}>Loading site integrations…</div>}

          {!siteIntegrationsLoading && siteIntegrations && (
            <>
              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>Travelpayouts Drive</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  Applied automatically only when monetisation is set to Full.
                </span>
                <textarea
                  rows={7}
                  value={siteIntegrations.travelpayoutsDriveHeadScript || ''}
                  onChange={(e) => updateSiteIntegrationsField('travelpayoutsDriveHeadScript', e.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    border: '1px solid #374151',
                    background: '#0f172a',
                    color: '#e5e7eb',
                    padding: '10px 12px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                />
              </label>

              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>Travelpayouts White Label Script</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  Used by website trip-planner modules for hotels and flights on Standard and Full monetisation.
                </span>
                <textarea
                  rows={7}
                  value={siteIntegrations.travelpayoutsWhiteLabelHeadScript || ''}
                  onChange={(e) => updateSiteIntegrationsField('travelpayoutsWhiteLabelHeadScript', e.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    border: '1px solid #374151',
                    background: '#0f172a',
                    color: '#e5e7eb',
                    padding: '10px 12px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                />
              </label>

              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>Travelpayouts White Label Search Markup</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  Inserted into the Plan Your Trip section for metasearch.
                </span>
                <textarea
                  rows={3}
                  value={siteIntegrations.travelpayoutsWhiteLabelSearchMarkup || ''}
                  onChange={(e) => updateSiteIntegrationsField('travelpayoutsWhiteLabelSearchMarkup', e.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    border: '1px solid #374151',
                    background: '#0f172a',
                    color: '#e5e7eb',
                    padding: '10px 12px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                />
              </label>

              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>Travelpayouts White Label Results Markup</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  Inserted below the metasearch widget for in-page results.
                </span>
                <textarea
                  rows={3}
                  value={siteIntegrations.travelpayoutsWhiteLabelResultsMarkup || ''}
                  onChange={(e) => updateSiteIntegrationsField('travelpayoutsWhiteLabelResultsMarkup', e.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    border: '1px solid #374151',
                    background: '#0f172a',
                    color: '#e5e7eb',
                    padding: '10px 12px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                />
              </label>

              <label style={{ display: 'grid', gap: 8 }}>
                <span style={{ fontWeight: 600 }}>GetYourGuide Widget Script</span>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  Required for GetYourGuide widgets used in landing pages and full website tour widgets.
                </span>
                <textarea
                  rows={4}
                  value={siteIntegrations.getyourguideHeadScript || ''}
                  onChange={(e) => updateSiteIntegrationsField('getyourguideHeadScript', e.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    border: '1px solid #374151',
                    background: '#0f172a',
                    color: '#e5e7eb',
                    padding: '10px 12px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                />
              </label>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" onClick={saveSiteIntegrations} disabled={siteIntegrationsSaving}>
                  {siteIntegrationsSaving ? 'Saving…' : 'Save Site Integrations'}
                </button>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  These defaults are global and affect all monetised landing pages and websites on the platform.
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {subTab === 'Partner Logos' && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid #374151', background: '#111827', display: 'grid', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Partner Logos</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              Create system-wide partner panels for websites. These are admin-managed, stored in the platform settings, and can later be forced onto lower subscription tiers.
            </div>
          </div>

          {partnerPanelsError && (
            <div style={{ padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
              {partnerPanelsError}
            </div>
          )}
          {partnerPanelsMessage && (
            <div style={{ padding: 10, borderRadius: 8, border: '1px solid #065f46', background: 'rgba(6,95,70,0.2)' }}>
              {partnerPanelsMessage}
            </div>
          )}

          {partnerPanelsLoading && <div style={{ color: '#9ca3af', fontSize: 12 }}>Loading partner panels…</div>}

          {!partnerPanelsLoading && (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" onClick={addPartnerPanel}>
                  Add Partner Panel
                </button>
                <button type="button" onClick={savePartnerPanels} disabled={partnerPanelsSaving}>
                  {partnerPanelsSaving ? 'Saving…' : 'Save All'}
                </button>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                  A panel must have logo, title, description, and URL before it can be enabled.
                </span>
              </div>

              <div style={{ display: 'grid', gap: 14 }}>
                {(partnerPanels || []).map((panel, index) => {
                  const complete = Boolean(panel.complete);
                  const uploading = partnerPanelUploadingId === panel.id;
                  return (
                    <div
                      key={panel.id}
                      style={{
                        border: '1px solid #374151',
                        borderRadius: 12,
                        padding: 14,
                        background: '#0f172a',
                        display: 'grid',
                        gap: 12,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                        <div style={{ fontWeight: 600 }}>Partner Panel {index + 1}</div>
                        <button
                          type="button"
                          onClick={() => removePartnerPanel(panel.id)}
                          style={{ background: 'transparent', color: '#fca5a5', border: '1px solid #7f1d1d', borderRadius: 8, padding: '6px 10px' }}
                        >
                          Remove
                        </button>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16, alignItems: 'start' }}>
                        <div style={{ display: 'grid', gap: 10 }}>
                          <div
                            style={{
                              width: 160,
                              height: 96,
                              borderRadius: 10,
                              border: '1px solid #374151',
                              background: '#111827',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {panel.logoUrl ? (
                              <img
                                src={panel.logoUrl}
                                alt={panel.title || `Partner logo ${index + 1}`}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>No logo uploaded</span>
                            )}
                          </div>
                          <input
                            ref={(node) => {
                              if (node) partnerPanelFileInputsRef.current[panel.id] = node;
                            }}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(event) => handlePartnerLogoFileSelected(panel.id, event)}
                          />
                          <button type="button" onClick={() => triggerPartnerLogoSelect(panel.id)} disabled={uploading}>
                            {uploading ? 'Uploading…' : 'Upload Logo'}
                          </button>
                        </div>

                        <div style={{ display: 'grid', gap: 10 }}>
                          <label style={{ display: 'grid', gap: 6 }}>
                            <span style={{ fontSize: 12, color: '#cbd5e1' }}>Company Name</span>
                            <input
                              value={panel.title || ''}
                              onChange={(e) => updatePartnerPanelField(panel.id, 'title', e.target.value)}
                              style={{
                                width: '100%',
                                borderRadius: 8,
                                border: '1px solid #374151',
                                background: '#111827',
                                color: '#e5e7eb',
                                padding: '8px 10px',
                              }}
                            />
                          </label>

                          <label style={{ display: 'grid', gap: 6 }}>
                            <span style={{ fontSize: 12, color: '#cbd5e1' }}>Short Description</span>
                            <textarea
                              rows={3}
                              value={panel.description || ''}
                              onChange={(e) => updatePartnerPanelField(panel.id, 'description', e.target.value)}
                              style={{
                                width: '100%',
                                borderRadius: 8,
                                border: '1px solid #374151',
                                background: '#111827',
                                color: '#e5e7eb',
                                padding: '8px 10px',
                              }}
                            />
                          </label>

                          <label style={{ display: 'grid', gap: 6 }}>
                            <span style={{ fontSize: 12, color: '#cbd5e1' }}>URL</span>
                            <input
                              value={panel.url || ''}
                              onChange={(e) => updatePartnerPanelField(panel.id, 'url', e.target.value)}
                              style={{
                                width: '100%',
                                borderRadius: 8,
                                border: '1px solid #374151',
                                background: '#111827',
                                color: '#e5e7eb',
                                padding: '8px 10px',
                              }}
                            />
                          </label>

                          <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(panel.enabled)}
                              disabled={!complete}
                              onChange={(e) => updatePartnerPanelField(panel.id, 'enabled', e.target.checked)}
                            />
                            <span>Enabled</span>
                            {!complete && <span style={{ fontSize: 12, color: '#fca5a5' }}>Complete all fields first</span>}
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {subTab === 'Users' && (
        <>
          <div
            style={{
              marginTop: 12,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 12,
            }}
          >
            <div style={{ padding: 12, borderRadius: 12, border: '1px solid #374151', background: '#111827' }}>
              <div style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Site Packages In Use
              </div>
              <div style={{ marginTop: 8, fontSize: 14, color: '#e5e7eb' }}>
                {formatPackageCountSummary(filteredUsersPackageSummary) || 'No packaged sites yet'}
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 12, border: '1px solid #374151', background: '#111827' }}>
              <div style={{ fontSize: 12, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Runtime Tiers By Account
              </div>
              <div style={{ marginTop: 8, fontSize: 14, color: '#e5e7eb' }}>
                {formatPackageCountSummary(filteredUsersRuntimeSummary) || 'No accounts loaded'}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                  <th style={{ padding: '10px 8px' }}>Email</th>
                  <th style={{ padding: '10px 8px' }}>Sites</th>
                  <th style={{ padding: '10px 8px' }}>Role</th>
                  <th style={{ padding: '10px 8px' }}>Packages / Runtime</th>
                  <th style={{ padding: '10px 8px' }}>GenAI</th>
                  <th style={{ padding: '10px 8px' }}>Other</th>
                  <th style={{ padding: '10px 8px' }}>Created</th>
                  <th style={{ padding: '10px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const rowDraft = getDraftForUser(user);
                  const isSelf = currentUserId && user.id === currentUserId;
                  const currentBillingProfile = normalizeBillingProfile(user.billingProfile || EMPTY_BILLING_PROFILE, user.billingSites || [], billingContractCatalog);
                  const subscriptionSummary = user.subscriptionSummary || {};
                  const runtimeLimits = subscriptionSummary.runtimeLimits || {};
                  const packageSummaryText = formatPackageCountSummary(subscriptionSummary.packageCounts || {});
                  const runtimeOverageSummary = formatRuntimeOverageSummary(runtimeLimits);
                  const perSiteBillingSiteId = Number(
                    rowDraft.billingProfile.activeBillingSiteId ||
                    rowDraft.billingProfile.selectedSiteIds?.[0] ||
                    0
                  );
                  const perSiteBillingSite = (user.billingSites || []).find((site) => Number(site.id) === perSiteBillingSiteId) || null;
                  const perSiteBillingEntityDraft = normalizeBillingIdentityFields(
                    rowDraft.billingProfile.siteBillingEntities?.[String(perSiteBillingSiteId)] || {},
                    rowDraft.billingProfile
                  );
                  const isDirty =
                    rowDraft.role !== (user.role || 'user') ||
                    JSON.stringify(currentBillingProfile) !== JSON.stringify(rowDraft.billingProfile);
                  const isSaving = Boolean(savingIds[user.id]);
                  const isDeleting = Boolean(deletingIds[user.id]);

                  const isExpanded = editingUserId === user.id;
                  const usageKey = `${user.id}:${period}`;
                  const usageState = userUsageByKey[usageKey] || null;

                  return (
                    <React.Fragment key={user.id}>
                      <tr style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '10px 8px', fontFamily: 'monospace', fontSize: 12 }}>
                          {user.email}
                          {isSelf && <span style={{ marginLeft: 8, color: '#60a5fa' }}>(you)</span>}
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: 12 }}>
                          {formatSiteNames(user.billingSites)}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <div style={{ textTransform: 'capitalize' }}>{rowDraft.role}</div>
                          {isSelf && (
                            <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                              Self-demotion disabled.
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: 12 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                            <span
                              style={{
                                padding: '4px 8px',
                                borderRadius: 999,
                                border: '1px solid #374151',
                                background: '#0f172a',
                                color: '#e5e7eb',
                                fontWeight: 600,
                              }}
                            >
                              Runtime {formatSubscription(subscriptionSummary.effectiveSubscriptionType || user.subscription_type || 'free')}
                            </span>
                            {runtimeLimits.isOverLimit && (
                              <span
                                style={{
                                  padding: '4px 8px',
                                  borderRadius: 999,
                                  border: '1px solid #92400e',
                                  background: 'rgba(146,64,14,0.18)',
                                  color: '#fcd34d',
                                  fontWeight: 600,
                                }}
                              >
                                Over limit
                              </span>
                            )}
                          </div>
                          <div style={{ marginTop: 6, color: '#9ca3af' }}>
                            {packageSummaryText || 'No site packages assigned'}
                          </div>
                          {runtimeOverageSummary && (
                            <div style={{ marginTop: 4, color: '#fca5a5' }}>
                              {runtimeOverageSummary}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: 12 }}>
                          <div>{formatNumber(user.usage_genai_tokens)} tokens</div>
                          <div style={{ color: '#9ca3af' }}>{formatNumber(user.usage_genai_calls)} calls</div>
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: 12 }}>
                          <div>{formatNumber(user.usage_other_calls)} calls</div>
                          <div style={{ color: '#9ca3af' }}>tokens: —</div>
                        </td>
                        <td style={{ padding: '10px 8px', color: '#9ca3af', fontSize: 12 }}>
                          {user.created_at ? new Date(user.created_at).toLocaleString() : '—'}
                        </td>
                        <td style={{ padding: '10px 8px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => {
                              const nextExpanded = editingUserId !== user.id;
                              toggleExpandedUser(user.id);
                              if (nextExpanded && (!usageState || (!usageState.loading && !usageState.data && !usageState.error))) {
                                loadUserUsage(user.id);
                              }
                            }}
                          >
                            {isExpanded ? 'Edit' : 'Edit'}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, border: 'none' }}>
                            <div
                              onClick={() => setEditingUserId(null)}
                              style={{
                                position: 'fixed',
                                inset: 0,
                                background: 'rgba(2,6,23,0.72)',
                                backdropFilter: 'blur(6px)',
                                zIndex: 90,
                              }}
                            />
                            <div
                              style={{
                                position: 'fixed',
                                top: 24,
                                right: 24,
                                bottom: 24,
                                left: 24,
                                zIndex: 91,
                                borderRadius: 20,
                                border: '1px solid rgba(71,85,105,0.65)',
                                background: 'linear-gradient(180deg, rgba(15,23,42,0.98), rgba(2,6,23,0.96))',
                                boxShadow: '0 30px 80px rgba(2,6,23,0.45)',
                                overflow: 'auto',
                                padding: 18,
                              }}
                            >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                              <div style={{ display: 'grid', gap: 8 }}>
                                <div>
                                  <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' }}>Edit User</div>
                                  <div style={{ marginTop: 4, fontSize: 22, fontWeight: 700 }}>{user.email}</div>
                                </div>
                                <label style={{ display: 'grid', gap: 6, maxWidth: 220 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Role</span>
                                  <select
                                    value={rowDraft.role}
                                    onChange={(e) => setDraftField(user.id, 'role', e.target.value)}
                                    disabled={isSaving || isSelf}
                                  >
                                    <option value="user">user</option>
                                    <option value="editor">editor</option>
                                    <option value="admin">admin</option>
                                  </select>
                                </label>
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <button type="button" disabled={isSaving} onClick={() => setEditingUserId(null)}>
                                  Close
                                </button>
                                <button type="button" disabled={isSaving} onClick={() => saveUserAndClose(user)}>
                                  {isSaving ? 'Saving…' : 'Save & Close'}
                                </button>
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                              <div style={{ fontWeight: 600 }}>API usage details</div>
                              <button type="button" onClick={() => loadUserUsage(user.id)} disabled={Boolean(usageState?.loading)}>
                                {usageState?.loading ? 'Loading…' : 'Reload'}
                              </button>
                            </div>

                            {usageState?.error && (
                              <div style={{ marginTop: 10, padding: 10, borderRadius: 8, border: '1px solid #991b1b', background: 'rgba(127,29,29,0.2)' }}>
                                {usageState.error}
                              </div>
                            )}

                            {usageState?.loading && (
                              <div style={{ marginTop: 10, color: '#9ca3af' }}>Loading usage…</div>
                            )}

                            {usageState?.data?.usage && (
                              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                                      <th style={{ padding: '8px 6px' }}>Group</th>
                                      <th style={{ padding: '8px 6px' }}>API</th>
                                      <th style={{ padding: '8px 6px' }}>Scope</th>
                                      <th style={{ padding: '8px 6px' }}>Feature</th>
                                      <th style={{ padding: '8px 6px' }}>Operation</th>
                                      <th style={{ padding: '8px 6px' }}>Calls</th>
                                      <th style={{ padding: '8px 6px' }}>Tokens</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {usageState.data.usage.length === 0 && (
                                      <tr>
                                        <td colSpan={7} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                                          No usage recorded in this period.
                                        </td>
                                      </tr>
                                    )}
                                    {usageState.data.usage.map((row, idx) => (
                                      <tr key={`${row.api}:${row.scope}:${row.feature}:${row.operation}:${idx}`} style={{ borderBottom: '1px solid #1f2937' }}>
                                        <td style={{ padding: '8px 6px', color: '#9ca3af' }}>{getFeatureGroup(row.feature)}</td>
                                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.api}</td>
                                        <td style={{ padding: '8px 6px', color: '#9ca3af' }}>{row.scope}</td>
                                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.feature || '—'}</td>
                                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.operation || '—'}</td>
                                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.calls)}</td>
                                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.totalTokens)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            <div
                              style={{
                                marginTop: 12,
                                padding: 12,
                                borderRadius: 12,
                                border: runtimeLimits.isOverLimit ? '1px solid #92400e' : '1px solid #374151',
                                background: runtimeLimits.isOverLimit ? 'rgba(146,64,14,0.12)' : 'rgba(15,23,42,0.45)',
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                <div>
                                  <div style={{ fontWeight: 600 }}>Runtime subscription</div>
                                  <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                    Billing site packages currently resolve to <strong>{formatSubscription(subscriptionSummary.effectiveSubscriptionType || user.subscription_type || 'free')}</strong>.
                                  </div>
                                </div>
                                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                  Stored on user: {formatSubscription(subscriptionSummary.storedSubscriptionType || user.subscription_type || 'free')}
                                </div>
                              </div>
                              <div style={{ marginTop: 10, fontSize: 12, color: '#cbd5e1' }}>
                                Packages in use: {packageSummaryText || 'No site packages assigned'}
                              </div>
                              <div style={{ marginTop: 8, fontSize: 12, color: runtimeLimits.isOverLimit ? '#fca5a5' : '#9ca3af' }}>
                                Device usage: TVs {formatNumber(runtimeLimits.used?.tv_screens || 0)}/{formatRuntimeLimitValue(runtimeLimits.limits?.tv_screens_included)} • Music {formatNumber(runtimeLimits.used?.music_players || 0)}/{formatRuntimeLimitValue(runtimeLimits.limits?.music_players)} • Phone Remotes {formatNumber(runtimeLimits.used?.staff_phones || 0)}/{formatRuntimeLimitValue(runtimeLimits.limits?.staff_phones)} • Console Remotes {formatNumber(runtimeLimits.used?.console_tablets || 0)}/{formatRuntimeLimitValue(runtimeLimits.limits?.console_tablets)}
                              </div>
                              {runtimeLimits.isOverLimit && (
                                <div style={{ marginTop: 8, fontSize: 12, color: '#fde68a' }}>
                                  This account is outside its live runtime limits. Existing devices remain visible, but adding more can be blocked until the package mix is raised again or runtime limits are separated per site.
                                </div>
                              )}
                            </div>

                            <div
                              style={{
                                marginTop: 12,
                                paddingTop: 12,
                                borderTop: '1px solid #374151',
                                display: 'grid',
                                gap: 12,
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                <div>
                                  <div style={{ fontWeight: 600 }}>
                                    {rowDraft.billingProfile.billingScope === 'per_site' ? 'Shared billing defaults' : 'Billing identity'}
                                  </div>
                                  <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                    {rowDraft.billingProfile.billingScope === 'per_site'
                                      ? 'These act as defaults for site-specific billing entities. Each selected site can then override them below.'
                                      : 'Vietnam-first invoice details for manual red invoice preparation and future intake-link flows.'}
                                  </div>
                                </div>
                                <button type="button" disabled={!isDirty || isSaving} onClick={() => saveUser(user)}>
                                  {isSaving ? 'Saving…' : 'Save billing details'}
                                </button>
                              </div>

                              <label style={{ display: 'grid', gap: 6 }}>
                                <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing scope</span>
                                <select
                                  value={rowDraft.billingProfile.billingScope}
                                  onChange={(e) => setDraftBillingField(user, 'billingScope', e.target.value)}
                                >
                                  {BILLING_SCOPE_OPTIONS.map((option) => (
                                    <option key={option.key} value={option.key}>{option.label}</option>
                                  ))}
                                </select>
                              </label>

                              {(rowDraft.billingProfile.billingScope === 'selected_sites' || rowDraft.billingProfile.billingScope === 'per_site') && (
                                <div
                                  style={{
                                    padding: 12,
                                    borderRadius: 12,
                                    border: '1px solid #374151',
                                    background: 'rgba(15,23,42,0.45)',
                                  }}
                                >
                                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>
                                    {rowDraft.billingProfile.billingScope === 'per_site'
                                      ? 'Select every site that needs its own legal billing entity. Choose one of those sites below to edit its invoice details.'
                                      : 'Select the sites covered by this billing identity.'}
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                                    {(user.billingSites || []).map((site) => {
                                      const checked = rowDraft.billingProfile.selectedSiteIds.includes(Number(site.id));
                                      return (
                                        <label
                                          key={`${user.id}:billing-site:${site.id}`}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '8px 10px',
                                            borderRadius: 10,
                                            border: '1px solid #334155',
                                            background: checked ? 'rgba(30,41,59,0.9)' : 'rgba(15,23,42,0.35)',
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleDraftBillingSite(user, Number(site.id))}
                                          />
                                          <span>{site.name}</span>
                                        </label>
                                      );
                                    })}
                                    {(user.billingSites || []).length === 0 && (
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>No sites on this account yet.</div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {rowDraft.billingProfile.billingScope === 'per_site' && rowDraft.billingProfile.selectedSiteIds.length > 0 && (
                                <div
                                  style={{
                                    padding: 12,
                                    borderRadius: 12,
                                    border: '1px solid #374151',
                                    background: 'rgba(15,23,42,0.45)',
                                    display: 'grid',
                                    gap: 12,
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div>
                                      <div style={{ fontWeight: 600 }}>Per-site billing entity</div>
                                      <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                        The selected site below will use its own legal business details on invoices and contracts.
                                      </div>
                                    </div>
                                    <label style={{ display: 'grid', gap: 6, minWidth: 240 }}>
                                      <span style={{ color: '#9ca3af', fontSize: 12 }}>Active billing site</span>
                                      <select
                                        value={String(perSiteBillingSiteId || '')}
                                        onChange={(e) => setDraftBillingField(user, 'activeBillingSiteId', Number(e.target.value) || null)}
                                      >
                                        {rowDraft.billingProfile.selectedSiteIds.map((siteId) => {
                                          const site = (user.billingSites || []).find((entry) => Number(entry.id) === Number(siteId));
                                          return (
                                            <option key={`${user.id}:active-billing-site:${siteId}`} value={String(siteId)}>
                                              {site?.name || `Site ${siteId}`}
                                            </option>
                                          );
                                        })}
                                      </select>
                                    </label>
                                  </div>

                                  {perSiteBillingSite && (
                                    <>
                                      <div style={{ fontSize: 12, color: '#93c5fd' }}>
                                        Editing billing entity for <strong>{perSiteBillingSite.name}</strong>.
                                      </div>

                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Legal business name</span>
                                          <input
                                            value={perSiteBillingEntityDraft.legalBusinessName}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'legalBusinessName', e.target.value)}
                                            placeholder="Registered company / household business name"
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Tax code</span>
                                          <input
                                            value={perSiteBillingEntityDraft.taxCode}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'taxCode', e.target.value)}
                                            placeholder="Vietnam tax code"
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Business registration no.</span>
                                          <input
                                            value={perSiteBillingEntityDraft.businessRegistrationNumber}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'businessRegistrationNumber', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Country code</span>
                                          <input
                                            value={perSiteBillingEntityDraft.countryCode}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'countryCode', e.target.value)}
                                            maxLength={2}
                                          />
                                        </label>
                                      </div>

                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing address line 1</span>
                                          <input
                                            value={perSiteBillingEntityDraft.billingAddressLine1}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'billingAddressLine1', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing address line 2</span>
                                          <input
                                            value={perSiteBillingEntityDraft.billingAddressLine2}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'billingAddressLine2', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Ward</span>
                                          <input
                                            value={perSiteBillingEntityDraft.ward}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'ward', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>District</span>
                                          <input
                                            value={perSiteBillingEntityDraft.district}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'district', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>City / province</span>
                                          <input
                                            value={perSiteBillingEntityDraft.cityProvince}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'cityProvince', e.target.value)}
                                          />
                                        </label>
                                      </div>

                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing contact name</span>
                                          <input
                                            value={perSiteBillingEntityDraft.billingContactName}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'billingContactName', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing email</span>
                                          <input
                                            type="email"
                                            value={perSiteBillingEntityDraft.billingEmail}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'billingEmail', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing phone</span>
                                          <input
                                            value={perSiteBillingEntityDraft.billingPhone}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'billingPhone', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Signer name</span>
                                          <input
                                            value={perSiteBillingEntityDraft.signerName}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'signerName', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Signer title</span>
                                          <input
                                            value={perSiteBillingEntityDraft.signerTitle}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'signerTitle', e.target.value)}
                                          />
                                        </label>
                                      </div>

                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Invoice notes</span>
                                          <textarea
                                            rows={3}
                                            value={perSiteBillingEntityDraft.invoiceNotes}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'invoiceNotes', e.target.value)}
                                          />
                                        </label>
                                        <label style={{ display: 'grid', gap: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Payment notes</span>
                                          <textarea
                                            rows={3}
                                            value={perSiteBillingEntityDraft.paymentNotes}
                                            onChange={(e) => setDraftSiteBillingField(user, perSiteBillingSiteId, 'paymentNotes', e.target.value)}
                                          />
                                        </label>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )}

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Legal business name</span>
                                  <input
                                    value={rowDraft.billingProfile.legalBusinessName}
                                    onChange={(e) => setDraftBillingField(user, 'legalBusinessName', e.target.value)}
                                    placeholder="Registered company / household business name"
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Tax code</span>
                                  <input
                                    value={rowDraft.billingProfile.taxCode}
                                    onChange={(e) => setDraftBillingField(user, 'taxCode', e.target.value)}
                                    placeholder="Vietnam tax code"
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Business registration no.</span>
                                  <input
                                    value={rowDraft.billingProfile.businessRegistrationNumber}
                                    onChange={(e) => setDraftBillingField(user, 'businessRegistrationNumber', e.target.value)}
                                    placeholder="Business registration"
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Country code</span>
                                  <input
                                    value={rowDraft.billingProfile.countryCode}
                                    onChange={(e) => setDraftBillingField(user, 'countryCode', e.target.value.toUpperCase())}
                                    placeholder="VN"
                                    maxLength={2}
                                  />
                                </label>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing address line 1</span>
                                  <input
                                    value={rowDraft.billingProfile.billingAddressLine1}
                                    onChange={(e) => setDraftBillingField(user, 'billingAddressLine1', e.target.value)}
                                    placeholder="Street / building / number"
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing address line 2</span>
                                  <input
                                    value={rowDraft.billingProfile.billingAddressLine2}
                                    onChange={(e) => setDraftBillingField(user, 'billingAddressLine2', e.target.value)}
                                    placeholder="Additional address details"
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Ward</span>
                                  <input
                                    value={rowDraft.billingProfile.ward}
                                    onChange={(e) => setDraftBillingField(user, 'ward', e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>District</span>
                                  <input
                                    value={rowDraft.billingProfile.district}
                                    onChange={(e) => setDraftBillingField(user, 'district', e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>City / province</span>
                                  <input
                                    value={rowDraft.billingProfile.cityProvince}
                                    onChange={(e) => setDraftBillingField(user, 'cityProvince', e.target.value)}
                                    placeholder="Ho Chi Minh City / Hanoi / Da Nang"
                                  />
                                </label>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing contact name</span>
                                  <input
                                    value={rowDraft.billingProfile.billingContactName}
                                    onChange={(e) => setDraftBillingField(user, 'billingContactName', e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing email</span>
                                  <input
                                    type="email"
                                    value={rowDraft.billingProfile.billingEmail}
                                    onChange={(e) => setDraftBillingField(user, 'billingEmail', e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Billing phone</span>
                                  <input
                                    value={rowDraft.billingProfile.billingPhone}
                                    onChange={(e) => setDraftBillingField(user, 'billingPhone', e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Signer name</span>
                                  <input
                                    value={rowDraft.billingProfile.signerName}
                                    onChange={(e) => setDraftBillingField(user, 'signerName', e.target.value)}
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Signer title</span>
                                  <input
                                    value={rowDraft.billingProfile.signerTitle}
                                    onChange={(e) => setDraftBillingField(user, 'signerTitle', e.target.value)}
                                    placeholder="Director / Owner / Manager"
                                  />
                                </label>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Invoice notes</span>
                                  <textarea
                                    rows={3}
                                    value={rowDraft.billingProfile.invoiceNotes}
                                    onChange={(e) => setDraftBillingField(user, 'invoiceNotes', e.target.value)}
                                    placeholder="Notes for invoice wording / contract context"
                                  />
                                </label>
                                <label style={{ display: 'grid', gap: 6 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Payment notes</span>
                                  <textarea
                                    rows={3}
                                    value={rowDraft.billingProfile.paymentNotes}
                                    onChange={(e) => setDraftBillingField(user, 'paymentNotes', e.target.value)}
                                    placeholder="Bank transfer details / payment handling notes"
                                  />
                                </label>
                              </div>
                            </div>

                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #374151' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                <div style={{ fontWeight: 600 }}>Danger zone</div>
                                <button
                                  type="button"
                                  onClick={() => deleteUser(user)}
                                  disabled={isDeleting || isSelf}
                                  style={{
                                    padding: '8px 12px',
                                    borderRadius: 8,
                                    border: '1px solid #7f1d1d',
                                    background: isDeleting || isSelf ? '#374151' : '#991b1b',
                                    color: '#fff',
                                  }}
                                >
                                  {isDeleting ? 'Deleting…' : 'Delete User'}
                                </button>
                              </div>
                              <div style={{ marginBottom: 12, fontSize: 12, color: '#fca5a5' }}>
                                Permanently deletes the user, their sites, destinations, related DB rows, and S3 content.
                              </div>

                              <div style={{ fontWeight: 600 }}>Rate limits (per-user overrides)</div>
                              <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>OpenAI max calls/day</span>
                                  <input
                                    value={getLimitDraftValue({ userId: user.id, api: 'openai', field: 'maxCalls' })}
                                    onChange={(e) => setLimitDraftValue({ userId: user.id, api: 'openai', field: 'maxCalls', value: e.target.value })}
                                    placeholder="e.g. 50"
                                  />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>OpenAI max tokens/day</span>
                                  <input
                                    value={getLimitDraftValue({ userId: user.id, api: 'openai', field: 'maxTotalTokens' })}
                                    onChange={(e) => setLimitDraftValue({ userId: user.id, api: 'openai', field: 'maxTotalTokens', value: e.target.value })}
                                    placeholder="e.g. 200000"
                                  />
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <span style={{ color: '#9ca3af', fontSize: 12 }}>Gemini max calls/day</span>
                                  <input
                                    value={getLimitDraftValue({ userId: user.id, api: 'gemini', field: 'maxCalls' })}
                                    onChange={(e) => setLimitDraftValue({ userId: user.id, api: 'gemini', field: 'maxCalls', value: e.target.value })}
                                    placeholder="e.g. 20"
                                  />
                                </label>
                              </div>
                              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                                <button
                                  type="button"
                                  disabled={savingLimitKey === `${user.id}:openai`}
                                  onClick={() =>
                                    saveRateLimit({
                                      userId: user.id,
                                      api: 'openai',
                                      enabled: true,
                                      maxCalls: getLimitDraftValue({ userId: user.id, api: 'openai', field: 'maxCalls' }),
                                      maxTotalTokens: getLimitDraftValue({ userId: user.id, api: 'openai', field: 'maxTotalTokens' }),
                                    })
                                  }
                                >
                                  {savingLimitKey === `${user.id}:openai` ? 'Saving…' : 'Save OpenAI limit'}
                                </button>
                                <button
                                  type="button"
                                  disabled={savingLimitKey === `${user.id}:gemini`}
                                  onClick={() =>
                                    saveRateLimit({
                                      userId: user.id,
                                      api: 'gemini',
                                      enabled: true,
                                      maxCalls: getLimitDraftValue({ userId: user.id, api: 'gemini', field: 'maxCalls' }),
                                      maxTotalTokens: '',
                                    })
                                  }
                                >
                                  {savingLimitKey === `${user.id}:gemini` ? 'Saving…' : 'Save Gemini limit'}
                                </button>
                              </div>
                              <div style={{ marginTop: 8, color: '#9ca3af', fontSize: 12 }}>
                                Token limits are enforced using a conservative estimate (request <code>max_tokens</code>) before the call runs.
                              </div>
                            </div>
                            <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #374151', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                              <button type="button" disabled={isSaving} onClick={() => setEditingUserId(null)}>
                                Close
                              </button>
                              <button type="button" disabled={isSaving} onClick={() => saveUserAndClose(user)}>
                                {isSaving ? 'Saving…' : 'Save & Close'}
                              </button>
                            </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 10, color: '#9ca3af', fontSize: 12 }}>
            Runtime tier now follows the highest billed site package on the account. The user record is synced automatically from Billing.
          </div>
        </>
      )}

      {subTab === 'Billing' && (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            borderRadius: 18,
            border: '1px solid rgba(148,163,184,0.2)',
            background:
              'radial-gradient(circle at top left, rgba(59,130,246,0.18), transparent 30%), radial-gradient(circle at top right, rgba(16,185,129,0.12), transparent 28%), linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.72))',
            boxShadow: '0 20px 40px rgba(2,6,23,0.28)',
          }}
        >
          <div
            style={{
              marginBottom: 16,
              padding: '14px 16px',
              borderRadius: 16,
              border: '1px solid rgba(96,165,250,0.2)',
              background: 'linear-gradient(135deg, rgba(30,41,59,0.86), rgba(15,23,42,0.94))',
            }}
          >
            <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' }}>Billing Matrix</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700 }}>Usage counts with live pricing.</div>
            <div style={{ marginTop: 4, fontSize: 13, color: '#94a3b8' }}>
              Edit an account to see site-level feature breakdowns, monthly pricing inputs, and VAT-inclusive invoice previews.
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#cbd5f5' }}>
              Global VAT: {billingVatPercent}% applied to invoice preview totals.
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'center', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                <th style={{ padding: '10px 8px' }}>Email</th>
                <th style={{ padding: '10px 8px', textAlign: 'left' }}>Sites</th>
                {BILLING_COLUMNS.map((column) => (
                  <th key={column.key} style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
                    {column.label}
                  </th>
                ))}
                <th style={{ padding: '10px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {!billingLoading && displayBillingUsers.length === 0 && (
                <tr>
                  <td colSpan={BILLING_COLUMNS.length + 3} style={{ padding: '12px 8px', color: '#9ca3af' }}>
                    No users found.
                  </td>
                </tr>
              )}

              {displayBillingUsers.map((user) => {
                const isSelf = currentUserId && user.id === currentUserId;
                const isExpanded = editingBillingUserId === user.id;
                const isInvoicePreviewExpanded = expandedInvoicePreviewUserIds.has(user.id);
                const isContractsCollapsed = collapsedBillingContractsByUserId[user.id] !== false;
                const isWorkbenchCollapsed = collapsedBillingWorkbenchByUserId[user.id] !== false;
                const isTimeMaterialsCollapsed = collapsedTimeMaterialsByUserId[user.id] !== false;
                const billingProfileDraft = getBillingProfileDraft(user);
                const billingIntakeRequest = billingProfileDraft.billingIntakeRequest || null;
                const invoiceScheduleDraft = computeInvoiceSchedule(billingProfileDraft);
                const isSavingBillingProfile = Boolean(billingProfileSavingIds[user.id]);
                const isGeneratingBillingIntake = Boolean(billingIntakeGeneratingIds[user.id]);
                const billingIntakeCopied = Boolean(billingIntakeCopiedIds[user.id]);
                const redInvoiceCopied = Boolean(redInvoiceCopiedIds[user.id]);
                const isGeneratingInvoicePdf = Boolean(invoicePdfGeneratingIds[user.id]);
                const isResettingInvoicing = Boolean(invoiceResettingIds[user.id]);
                const totals = user.totals || EMPTY_BILLING_TOTALS;
                const sites = Array.isArray(user.sites) ? user.sites : [];
                const billingProfile = user.billingProfile || EMPTY_BILLING_PROFILE;
                const billingSources = normalizeBillingSources(billingProfileDraft.billingSources, billingProfileDraft.issuerCompanyKey);
                const combinedInvoice = normalizeCombinedInvoice(billingProfileDraft.combinedInvoice, billingProfileDraft.issuerCompanyKey);
                const companyProfile = billingCompanies.find((company) => company.key === billingSources.service.issuerCompanyKey)
                  || user.companyProfile
                  || PLATFORM_COMPANY_PROFILE;
                const contractCompanyProfile = billingCompanies.find((company) => company.key === billingSources.contract.issuerCompanyKey)
                  || companyProfile;
                const combinedCompanyProfile = billingCompanies.find((company) => company.key === combinedInvoice.issuerCompanyKey)
                  || companyProfile;
                const persistedInvoiceDocuments = Array.isArray(billingProfile.invoiceDocuments) ? billingProfile.invoiceDocuments : [];
                const draftInvoiceDocuments = Array.isArray(billingProfileDraft.invoiceDocuments) ? billingProfileDraft.invoiceDocuments : [];
                const invoiceDocuments = draftInvoiceDocuments.length ? draftInvoiceDocuments : persistedInvoiceDocuments;
                const timeMaterialsInvoiceDocuments = invoiceDocuments.filter((entry) => entry.invoiceMode === 'time_materials');
                const combinedInvoiceDocuments = invoiceDocuments.filter((entry) => entry.invoiceMode === 'combined');
                const latestInvoiceDocument = invoiceDocuments[0] || persistedInvoiceDocuments[0] || null;
                const contractDocuments = Array.isArray(billingProfileDraft.contractDocuments) ? billingProfileDraft.contractDocuments : [];
                const coveredSites = Array.isArray(user.coveredSites) ? user.coveredSites : sites;
                const uncoveredSites = Array.isArray(user.uncoveredSites) ? user.uncoveredSites : [];
                const effectiveBillingEntityProfile = user.effectiveBillingEntityProfile || getEffectiveBillingEntityProfile(billingProfileDraft, coveredSites);
                const activeBillingSite = user.activeBillingSite || coveredSites[0] || null;
                const persistedInvoiceReadiness = user.invoiceReadiness || { issuerMissingFields: [], customerMissingFields: [], ready: false };
                const issuerMissingFields = getCompanyProfileMissingFields(companyProfile);
                const invoiceReadiness = {
                  ...persistedInvoiceReadiness,
                  issuerMissingFields,
                  ready: issuerMissingFields.length === 0 && (persistedInvoiceReadiness.customerMissingFields || []).length === 0,
                };
                const invoiceReadinessReasons = [
                  ...(invoiceReadiness.issuerMissingFields || []).map((field) => `Seller: ${field}`),
                  ...(invoiceReadiness.customerMissingFields || []).map((field) => `Buyer: ${field}`),
                ];
                const contractDraft = normalizeBillingContractDraft(billingProfileDraft.contractDraft, billingContractCatalog);
                const timeAndMaterials = normalizeTimeAndMaterials(billingProfileDraft.timeAndMaterials);
                const includedTimeMaterials = timeAndMaterials.items.filter((item) => item.description && item.quantity > 0);
                const timeMaterialsTotal = includedTimeMaterials.reduce(
                  (sum, item) => sum + Math.round(item.quantity * item.unitRate),
                  0
                );
                const calculatedMonthlyRate = coveredSites.reduce(
                  (sum, site) => sum + Number(site.manualMonthlyTotal || 0),
                  0
                );
                const agreedMonthlyRate = contractDraft.monthlyRate === null
                  ? calculatedMonthlyRate
                  : Number(contractDraft.monthlyRate || 0);
                const recurringInvoiceSubtotal = agreedMonthlyRate * Number(invoiceScheduleDraft.cadenceMonths || 1);
                const calculatedInvoiceSubtotal = coveredSites.reduce(
                  (sum, site) => sum + (Number(site.manualMonthlyTotal || 0) * Number(invoiceScheduleDraft.cadenceMonths || 1)),
                  0
                );
                const invoicePreviewSubtotal = calculatedInvoiceSubtotal;
                const invoicePreviewVatAmount = Math.round(invoicePreviewSubtotal * (normalizeVatPercent(billingVatPercent) / 100));
                const invoicePreviewTotalWithVat = invoicePreviewSubtotal + invoicePreviewVatAmount;
                const invoicePreviewNumber = `PREVIEW-${String(user.id).padStart(4, '0')}-${(invoiceScheduleDraft.periodStart || getTodayIsoDate()).replace(/-/g, '')}`;
                const invoiceLineItemsDraft = coveredSites.map((site) => ({
                  id: site.id,
                  description: `Suitestream.tv subscription services for ${site.name} (${formatIsoDate(invoiceScheduleDraft.periodStart)} - ${formatIsoDate(invoiceScheduleDraft.periodEndInclusive)})`,
                  quantity: Number(invoiceScheduleDraft.cadenceMonths || 1),
                  unitPrice: Number(site.manualMonthlyTotal || 0),
                  amount: Number(site.manualMonthlyTotal || 0) * Number(invoiceScheduleDraft.cadenceMonths || 1),
                }));
                const combinedServiceSubtotal = billingSources.service.addToCombined ? calculatedInvoiceSubtotal : 0;
                const combinedContractSubtotal = billingSources.contract.addToCombined && contractDraft.monthlyRate !== null
                  ? Number(contractDraft.monthlyRate || 0) * Number(invoiceScheduleDraft.cadenceMonths || 1)
                  : 0;
                const combinedTimeMaterialsSubtotal = billingSources.timeMaterials.addToCombined ? timeMaterialsTotal : 0;
                const combinedBaseSubtotal = combinedServiceSubtotal + combinedContractSubtotal + combinedTimeMaterialsSubtotal;
                const combinedSurchargeAmount = Math.round(combinedBaseSubtotal * (combinedInvoice.surchargePercent / 100));
                const combinedBeforeDiscount = combinedBaseSubtotal + combinedSurchargeAmount;
                const requestedCombinedDiscount = combinedInvoice.discountType === 'flat'
                  ? combinedInvoice.discountValue
                  : Math.round(combinedBeforeDiscount * (combinedInvoice.discountValue / 100));
                const combinedDiscountAmount = Math.min(combinedBeforeDiscount, Math.max(0, requestedCombinedDiscount));
                const combinedSubtotal = combinedBeforeDiscount - combinedDiscountAmount;
                const combinedVatAmount = Math.round(combinedSubtotal * (normalizeVatPercent(billingVatPercent) / 100));
                const combinedTotalWithVat = combinedSubtotal + combinedVatAmount;
                const showCombinedInvoice = Object.values(billingSources).some((source) => source.addToCombined)
                  || combinedInvoiceDocuments.length > 0;
                const renderBillingSourceControls = (sourceKey) => {
                  const source = billingSources[sourceKey];
                  return (
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        aria-label={`${sourceKey} billing company`}
                        value={source.issuerCompanyKey}
                        onChange={(event) => updateBillingProfileDraft(user, 'billingSources', {
                          ...billingSources,
                          [sourceKey]: { ...source, issuerCompanyKey: event.target.value },
                        })}
                      >
                        {billingCompanies.map((company) => (
                          <option key={`${sourceKey}:${company.key}`} value={company.key}>{company.companyName || company.key}</option>
                        ))}
                      </select>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={source.addToCombined}
                          onChange={(event) => updateBillingProfileDraft(user, 'billingSources', {
                            ...billingSources,
                            [sourceKey]: { ...source, addToCombined: event.target.checked },
                          })}
                        />
                        Add to combined invoice
                      </label>
                    </div>
                  );
                };
                const total3Months = agreedMonthlyRate * 3;
                const total6Months = agreedMonthlyRate * 6;
                const total12Months = agreedMonthlyRate * 12;
                const total3MonthsWithVat = applyVatToAmount(total3Months, billingVatPercent);
                const total6MonthsWithVat = applyVatToAmount(total6Months, billingVatPercent);
                const total12MonthsWithVat = applyVatToAmount(total12Months, billingVatPercent);
                const hasFreePackageOnMultiSiteAccount = sites.some((site) => site.resolvedPackageKey === 'free') && sites.length > 1;
                const subscriptionSummary = user.subscriptionSummary || {};
                const runtimeLimits = subscriptionSummary.runtimeLimits || {};
                const packageSummaryText = formatPackageCountSummary(subscriptionSummary.packageCounts || {});
                const runtimeOverageSummary = formatRuntimeOverageSummary(runtimeLimits);
                const isGeneratingContractPdf = Boolean(contractPdfGeneratingIds[user.id]);
                const contractTemplate = billingContractCatalog?.templates?.[contractDraft.templateKey] || DEFAULT_BILLING_CONTRACT_CATALOG.templates.managed_service;
                const contractReadinessReasons = [
                  ...getCompanyProfileMissingFields(contractCompanyProfile).map((field) => `Provider: ${field}`),
                  ...getContractProfileMissingFields(billingProfileDraft, coveredSites, billingContractCatalog).map((field) => `Customer: ${field}`),
                ];
                const contractPreviewDefinition = buildContractPreviewDefinition({
                  userId: user.id,
                  email: user.email,
                  billingProfile: billingProfileDraft,
                  effectiveBillingEntityProfile,
                  coveredSites,
                  invoiceSchedule: invoiceScheduleDraft,
                  invoicePreviewSubtotal: recurringInvoiceSubtotal,
                  invoicePreviewVatAmount: Math.round(recurringInvoiceSubtotal * (normalizeVatPercent(billingVatPercent) / 100)),
                  invoicePreviewTotalWithVat: applyVatToAmount(recurringInvoiceSubtotal, billingVatPercent),
                  vatPercent: billingVatPercent,
                  contractDraft,
                  contractCatalog: billingContractCatalog,
                  billingPackageCatalog,
                  companyProfile: contractCompanyProfile,
                  logoUrl: contractCompanyProfile.logoUrl || DEFAULT_INVOICE_LOGO_URL,
                });

                return (
                  <React.Fragment key={user.id}>
                    <tr style={{ borderBottom: '1px solid #1f2937' }}>
                      <td style={{ padding: '10px 8px', fontFamily: 'monospace', fontSize: 12, textAlign: 'left' }}>
                        {user.email}
                        {isSelf && <span style={{ marginLeft: 8, color: '#60a5fa' }}>(you)</span>}
                        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <span
                            style={{
                              padding: '3px 8px',
                              borderRadius: 999,
                              border: '1px solid #334155',
                              background: '#0f172a',
                              color: '#e5e7eb',
                              fontWeight: 600,
                            }}
                          >
                            Runtime {formatSubscription(subscriptionSummary.effectiveSubscriptionType || user.subscription_type || 'free')}
                          </span>
                          {runtimeLimits.isOverLimit && (
                            <span
                              style={{
                                padding: '3px 8px',
                                borderRadius: 999,
                                border: '1px solid #92400e',
                                background: 'rgba(146,64,14,0.18)',
                                color: '#fcd34d',
                                fontWeight: 600,
                              }}
                            >
                              Over limit
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '10px 8px', fontSize: 12, textAlign: 'left' }}>
                        {formatSiteNames(sites)}
                      </td>
                      {BILLING_COLUMNS.map((column) => (
                        <td key={column.key} style={{ padding: '10px 8px', whiteSpace: 'nowrap', textAlign: 'center' }}>
                          {formatNumber(totals[column.key] || 0)}
                        </td>
                      ))}
                      <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                        <button type="button" onClick={() => toggleExpandedBillingUser(user.id)}>
                          Edit
                        </button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={BILLING_COLUMNS.length + 3} style={{ padding: 0, border: 'none' }}>
                          <div
                            onClick={() => setEditingBillingUserId(null)}
                            style={{
                              position: 'fixed',
                              inset: 0,
                              background: 'rgba(2,6,23,0.72)',
                              backdropFilter: 'blur(6px)',
                              zIndex: 90,
                            }}
                          />
                          <div
                            style={{
                              position: 'fixed',
                              top: 24,
                              right: 24,
                              bottom: 24,
                              left: 24,
                              zIndex: 91,
                              borderRadius: 20,
                              border: '1px solid rgba(71,85,105,0.65)',
                              background: 'linear-gradient(180deg, rgba(15,23,42,0.98), rgba(2,6,23,0.96))',
                              boxShadow: '0 30px 80px rgba(2,6,23,0.45)',
                              overflow: 'auto',
                              padding: 18,
                            }}
                          >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' }}>Edit Billing</div>
                              <div style={{ marginTop: 4, fontSize: 22, fontWeight: 700 }}>{user.email}</div>
                              <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
                                Edit commercial terms, billing identity, invoices, and contracts for this account.
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                              <button type="button" disabled={isSavingBillingProfile} onClick={() => setEditingBillingUserId(null)}>
                                Close
                              </button>
                              <button type="button" disabled={isSavingBillingProfile} onClick={() => saveBillingProfileAndClose(user)}>
                                {isSavingBillingProfile ? 'Saving…' : 'Save & Close'}
                              </button>
                            </div>
                          </div>
                          <div
                            style={{
                              marginBottom: 14,
                              padding: 12,
                              borderRadius: 12,
                              border: '1px solid #334155',
                              background: 'rgba(15,23,42,0.3)',
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                              gap: 12,
                              alignItems: 'end',
                            }}
                          >
                            <label style={{ display: 'grid', gap: 6 }}>
                              <span style={{ fontSize: 12, color: '#cbd5e1' }}>Invoice issuer</span>
                              <select
                                value={billingProfileDraft.issuerCompanyKey}
                                onChange={(event) => updateBillingProfileDraft(user, 'issuerCompanyKey', event.target.value, { autoSave: true })}
                              >
                                {billingCompanies.map((company) => (
                                  <option key={company.key} value={company.key}>
                                    {company.companyName || company.key}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
                              <div style={{ color: '#e5e7eb', fontWeight: 600 }}>{companyProfile.companyName || 'Company details missing'}</div>
                              <div>{companyProfile.taxCode ? `Tax code: ${companyProfile.taxCode}` : 'Tax code missing'}</div>
                              <div>{companyProfile.bankName || 'Bank missing'}{companyProfile.bankAccountNumber ? ` | ${companyProfile.bankAccountNumber}` : ''}</div>
                            </div>
                          </div>
                          {sites.length === 0 ? (
                            <div style={{ color: '#9ca3af' }}>No sites for this user.</div>
                          ) : (
                            <>
                              <div
                                style={{
                                  marginBottom: 14,
                                  padding: 12,
                                  borderRadius: 12,
                                  border: runtimeLimits.isOverLimit ? '1px solid #92400e' : '1px solid #334155',
                                  background: runtimeLimits.isOverLimit ? 'rgba(146,64,14,0.12)' : 'rgba(15,23,42,0.3)',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <div style={{ fontWeight: 600 }}>
                                    Packages in use: {packageSummaryText || 'No site packages assigned'}
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                      Runtime tier: {formatSubscription(subscriptionSummary.effectiveSubscriptionType || user.subscription_type || 'free')}
                                    </div>
                                    {latestInvoiceDocument && (
                                      <button
                                        type="button"
                                        onClick={() => copyRedInvoiceInfo({
                                          userId: user.id,
                                          issuerProfile: companyProfile,
                                          invoiceDocument: latestInvoiceDocument,
                                          customerProfile: effectiveBillingEntityProfile,
                                          coveredSites,
                                          billingScopeLabel: formatBillingScopeLabel(billingProfile.billingScope),
                                        })}
                                      >
                                        {redInvoiceCopied ? 'Copied' : 'Copy Red Invoice Info'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {runtimeOverageSummary && (
                                  <div style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>
                                    Runtime overages: {runtimeOverageSummary}
                                  </div>
                                )}
                                {!runtimeOverageSummary && (
                                  <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
                                    Device usage is currently within the live runtime tier for this account.
                                  </div>
                                )}
                              </div>

                              <div
                                style={{
                                  marginBottom: 14,
                                  overflowX: 'auto',
                                  borderRadius: 14,
                                  border: '1px solid rgba(71,85,105,0.5)',
                                  background: 'rgba(15,23,42,0.52)',
                                }}
                              >
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ textAlign: 'center', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                                      <th style={{ padding: '8px 6px', textAlign: 'left' }}>Site</th>
                                      <th style={{ padding: '8px 6px' }}>Package</th>
                                      <th style={{ padding: '8px 6px' }}>TV&apos;s</th>
                                      <th style={{ padding: '8px 6px' }}>Music</th>
                                      <th style={{ padding: '8px 6px' }}>Website</th>
                                      <th style={{ padding: '8px 6px' }}>Wi-Fi</th>
                                      <th style={{ padding: '8px 6px' }}>Menu</th>
                                      <th style={{ padding: '8px 6px' }}>Product</th>
                                      <th style={{ padding: '8px 6px' }}>Service</th>
                                      <th style={{ padding: '8px 6px' }}>Info</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sites.map((site) => (
                                      <tr key={`billing-feature-summary-${site.id}`} style={{ borderBottom: '1px solid #1f2937' }}>
                                        <td style={{ padding: '8px 6px', textAlign: 'left' }}>
                                          <div>{site.name}</div>
                                          <div style={{ marginTop: 2, fontSize: 12, color: '#9ca3af' }}>
                                            {site.siteType === 'destination' ? 'Destination' : 'Venue'}
                                          </div>
                                        </td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                                          {site.assignedPackageEstimate?.label
                                            || formatSubscription(site.resolvedPackageKey || site.packageKey || site.defaultPackageKey || 'free')}
                                        </td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{formatNumber(site.screens || 0)}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{formatNumber(site.musicPlayers || 0)}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{site.websites ? 'Yes' : '—'}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{site.wifi ? 'Yes' : '—'}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{formatNumber(site.menuQrs || 0)}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{formatNumber(site.productQrs || 0)}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{formatNumber(site.serviceQrs || 0)}</td>
                                        <td style={{ padding: '8px 6px', textAlign: 'center' }}>{formatNumber(site.infoQrs || 0)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                <div>
                                  <div style={{ fontWeight: 700 }}>Runtime Subscriptions</div>
                                  <div style={{ marginTop: 3, fontSize: 12, color: '#9ca3af' }}>
                                    Select a package to apply it to {sites.length === 1 ? sites[0].name : `all ${sites.length} sites`}.
                                  </div>
                                </div>
                                {accountPackageSavingIds[user.id] && <span style={{ fontSize: 12, color: '#93c5fd' }}>Updating package…</span>}
                              </div>
                              <div
                                style={{
                                  marginBottom: 14,
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                  gap: 12,
                                }}
                              >
                                {Object.values(billingPackageCatalog || DEFAULT_BILLING_PACKAGE_CATALOG).map((pkg) => {
                                  const pkgCopy = getPackageUiCopy(pkg);
                                  const activeRuntimePackageKey = normalizeBillingPackageKey(
                                    subscriptionSummary.effectiveSubscriptionType || user.subscription_type || 'free',
                                    'free'
                                  );
                                  const isActivePackage = pkg.key === activeRuntimePackageKey;
                                  return (
                                    <button
                                      type="button"
                                      key={`top-legend-${user.id}-${pkg.key}`}
                                      disabled={isActivePackage || Boolean(accountPackageSavingIds[user.id])}
                                      onClick={() => changeAccountPackage(user, pkg.key)}
                                      style={{
                                        padding: 14,
                                        borderRadius: 14,
                                        border: isActivePackage ? '2px solid #60a5fa' : '1px solid rgba(71,85,105,0.45)',
                                        background: isActivePackage
                                          ? 'linear-gradient(135deg, rgba(37,99,235,0.3), rgba(15,23,42,0.78))'
                                          : 'rgba(15,23,42,0.42)',
                                        display: 'grid',
                                        gap: 7,
                                        color: '#e5e7eb',
                                        textAlign: 'left',
                                        cursor: isActivePackage ? 'default' : 'pointer',
                                        boxShadow: isActivePackage ? '0 0 0 3px rgba(96,165,250,0.12)' : 'none',
                                        opacity: 1,
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                                          <div style={{ fontWeight: 700 }}>{pkg.label}</div>
                                          {isActivePackage && (
                                            <span style={{ padding: '3px 7px', borderRadius: 999, background: '#2563eb', color: '#fff', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>
                                              In use
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ fontSize: 11, color: isActivePackage ? '#bfdbfe' : '#93c5fd' }}>
                                          {isActivePackage ? 'Current package' : pkg.basePrice == null ? 'POA' : `${formatNumber(pkg.basePrice)}/mo`}
                                        </div>
                                      </div>
                                      <div style={{ fontSize: 12, color: '#cbd5e1' }}>{pkgCopy.summary}</div>
                                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                        {pkg.advertisingEnabled ? 'Ads mandatory' : 'Ads off by default'}
                                        {pkg.maxSitesPerAccount === 1 ? ' • Single-site only' : ''}
                                      </div>
                                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                        Includes {Object.entries(pkg.included || {})
                                          .filter(([, count]) => Number(count || 0) > 0)
                                          .slice(0, 4)
                                          .map(([key, count]) => {
                                            const label = BILLING_COLUMNS.find((column) => column.key === key)?.label || key;
                                            return `${count} ${label.toLowerCase()}`;
                                          })
                                          .join(' | ')}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>

                              <div
                                style={{
                                  marginBottom: 14,
                                  padding: 16,
                                  borderRadius: 16,
                                  border: '1px solid rgba(71,85,105,0.5)',
                                  background: 'rgba(15,23,42,0.45)',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 18 }}>Customer Billing Intake Link</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                      Generate a public link the customer can use to fill out their own company and invoicing details.
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <span
                                      style={{
                                        padding: '6px 10px',
                                        borderRadius: 999,
                                        background: billingIntakeRequest?.lastSubmittedAt ? 'rgba(34,197,94,0.16)' : 'rgba(96,165,250,0.16)',
                                        color: billingIntakeRequest?.lastSubmittedAt ? '#86efac' : '#bfdbfe',
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {billingIntakeRequest?.lastSubmittedAt ? 'Submitted' : billingIntakeRequest?.token ? 'Link Ready' : 'Not Generated'}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={isGeneratingBillingIntake}
                                      onClick={() => generateBillingIntakeLink(user)}
                                    >
                                      {isGeneratingBillingIntake ? 'Generating…' : billingIntakeRequest?.token ? 'Regenerate Link' : 'Generate Link'}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={!billingIntakeRequest?.intakeUrl}
                                      onClick={() => copyBillingIntakeUrl(user.id, billingIntakeRequest?.intakeUrl)}
                                    >
                                      {billingIntakeCopied ? 'Copied' : 'Copy Link'}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={!billingIntakeRequest?.intakeUrl}
                                      onClick={() => openBillingIntakeForm(user.id, billingIntakeRequest?.intakeUrl)}
                                    >
                                      Open Form
                                    </button>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    marginTop: 14,
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                                    gap: 10,
                                    alignItems: 'center',
                                  }}
                                >
                                  <input
                                    readOnly
                                    value={billingIntakeRequest?.intakeUrl || ''}
                                    placeholder="Generate a billing intake link to copy it here"
                                    style={{
                                      color: '#e5e7eb',
                                      background: '#020617',
                                      border: '1px solid #475569',
                                      borderRadius: 8,
                                      padding: '10px 12px',
                                      fontFamily: 'monospace',
                                      fontSize: 12,
                                    }}
                                  />
                                  <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                                    {billingIntakeRequest?.lastSubmittedAt
                                      ? `Last submitted ${new Date(billingIntakeRequest.lastSubmittedAt).toLocaleString()}`
                                      : billingIntakeRequest?.createdAt
                                        ? `Created ${new Date(billingIntakeRequest.createdAt).toLocaleString()}`
                                        : 'No intake link generated yet'}
                                  </div>
                                </div>
                              </div>

                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <div
                                style={{
                                  order: 4,
                                  marginTop: 18,
                                  padding: 18,
                                  borderRadius: 18,
                                  border: '1px solid rgba(71,85,105,0.5)',
                                  background:
                                    'radial-gradient(circle at top left, rgba(16,185,129,0.12), transparent 24%), linear-gradient(180deg, rgba(17,24,39,0.88), rgba(15,23,42,0.96))',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 18 }}>Contracts</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                      Keep draft and stored agreements tucked away until you need to edit or generate them.
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {renderBillingSourceControls('contract')}
                                    <button type="button" onClick={() => toggleBillingContractsCollapsed(user.id)}>
                                      {isContractsCollapsed ? 'Show' : 'Hide'}
                                    </button>
                                  </div>
                                </div>

                                {!isContractsCollapsed && (
                                  <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 18 }}>Template & Preview</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                      Choose a contract template, set the operational terms, preview the agreement, and store the final PDF in S3.
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <button
                                      type="button"
                                      disabled={isGeneratingInvoicePdf || contractDraft.monthlyRate === null}
                                      onClick={() => generateInvoicePdf(user, 'contract')}
                                    >
                                      {isGeneratingInvoicePdf ? 'Generating…' : 'Generate Contract Invoice'}
                                    </button>
                                    <button
                                      type="button"
                                      disabled={isGeneratingContractPdf}
                                      onClick={() => generateContractPdf(user)}
                                    >
                                      {isGeneratingContractPdf ? 'Generating…' : 'Generate PDF'}
                                    </button>
                                    <div
                                      style={{
                                        padding: '7px 10px',
                                        borderRadius: 999,
                                        background: contractDocuments.length ? 'rgba(16,185,129,0.16)' : 'rgba(148,163,184,0.16)',
                                        color: contractDocuments.length ? '#a7f3d0' : '#cbd5e1',
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {contractDocuments.length ? `${formatNumber(contractDocuments.length)} stored` : 'Draft only'}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    marginTop: 14,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                    gap: 10,
                                  }}
                                >
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Template
                                    </span>
                                    <select
                                      value={contractDraft.templateKey}
                                      onChange={(e) => {
                                        const nextTemplateKey = normalizeBillingContractTemplateKey(e.target.value, billingContractCatalog);
                                        const nextTemplate = billingContractCatalog?.templates?.[nextTemplateKey] || DEFAULT_BILLING_CONTRACT_CATALOG.templates.managed_service;
                                        updateBillingContractDraft(user, {
                                          templateKey: nextTemplateKey,
                                          serviceModel: nextTemplate.defaultServiceModel,
                                          responseTimeKey: nextTemplate.defaultResponseTimeKey,
                                          supportChannel: nextTemplate.defaultSupportChannel,
                                          contentResponsibility: nextTemplate.defaultContentResponsibility,
                                          includedUpdateRequests: nextTemplate.defaultIncludedUpdateRequests,
                                          includedAdminHours: nextTemplate.defaultIncludedAdminHours,
                                          selectedClauseKeys: nextTemplate.availableClauseKeys.filter((key) => ['venue_network_exclusion', 'third_party_dependencies'].includes(key)),
                                        });
                                      }}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    >
                                      {Object.values(billingContractCatalog?.templates || DEFAULT_BILLING_CONTRACT_CATALOG.templates).map((templateEntry) => (
                                        <option key={`${user.id}:contract-template:${templateEntry.key}`} value={templateEntry.key}>
                                          {templateEntry.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Contract Start
                                    </span>
                                    <input
                                      type="date"
                                      value={contractDraft.contractStartDate || ''}
                                      onChange={(e) => updateBillingContractDraft(user, { contractStartDate: e.target.value })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    />
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Agreed Monthly Rate (ex VAT)
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1000"
                                      value={contractDraft.monthlyRate ?? ''}
                                      placeholder={String(calculatedMonthlyRate)}
                                      onChange={(e) => updateBillingContractDraft(user, {
                                        monthlyRate: e.target.value === '' ? null : e.target.value,
                                      })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    />
                                    <span style={{ fontSize: 11, color: '#9ca3af' }}>
                                      Blank uses the calculated site total of {formatNumber(calculatedMonthlyRate)} per month.
                                    </span>
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Term
                                    </span>
                                    <select
                                      value={String(contractDraft.termMonths)}
                                      onChange={(e) => updateBillingContractDraft(user, { termMonths: Number(e.target.value) })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    >
                                      {(billingContractCatalog?.termOptions || DEFAULT_BILLING_CONTRACT_CATALOG.termOptions).map((termMonths) => (
                                        <option key={`${user.id}:contract-term:${termMonths}`} value={String(termMonths)}>
                                          {termMonths} month{termMonths === 1 ? '' : 's'}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Response Target
                                    </span>
                                    <select
                                      value={contractDraft.responseTimeKey}
                                      onChange={(e) => updateBillingContractDraft(user, { responseTimeKey: e.target.value })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    >
                                      {(billingContractCatalog?.responseTimes || DEFAULT_BILLING_CONTRACT_CATALOG.responseTimes).map((entry) => (
                                        <option key={`${user.id}:contract-response:${entry.key}`} value={entry.key}>
                                          {entry.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Support Channel
                                    </span>
                                    <select
                                      value={contractDraft.supportChannel}
                                      onChange={(e) => updateBillingContractDraft(user, { supportChannel: e.target.value })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    >
                                      {(billingContractCatalog?.supportChannels || DEFAULT_BILLING_CONTRACT_CATALOG.supportChannels).map((entry) => (
                                        <option key={`${user.id}:contract-channel:${entry.key}`} value={entry.key}>
                                          {entry.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                </div>

                                <div
                                  style={{
                                    marginTop: 10,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                    gap: 10,
                                    alignItems: 'start',
                                  }}
                                >
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, lineHeight: 1.45, minHeight: 32, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Content Responsibility
                                    </span>
                                    <select
                                      value={contractDraft.contentResponsibility}
                                      onChange={(e) => updateBillingContractDraft(user, { contentResponsibility: e.target.value })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    >
                                      {(billingContractCatalog?.contentResponsibilities || DEFAULT_BILLING_CONTRACT_CATALOG.contentResponsibilities).map((entry) => (
                                        <option key={`${user.id}:contract-content:${entry.key}`} value={entry.key}>
                                          {entry.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, lineHeight: 1.45, minHeight: 32, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Included Update Requests / Month
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={String(contractDraft.includedUpdateRequests)}
                                      onChange={(e) => updateBillingContractDraft(user, { includedUpdateRequests: e.target.value })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    />
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, lineHeight: 1.45, minHeight: 32, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Included Admin Hours / Month
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={String(contractDraft.includedAdminHours)}
                                      onChange={(e) => updateBillingContractDraft(user, { includedAdminHours: e.target.value })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    />
                                  </label>
                                  <label style={{ display: 'grid', gap: 6 }}>
                                    <span style={{ fontSize: 11, lineHeight: 1.45, minHeight: 32, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                      Notice Days
                                    </span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={String(contractDraft.noticeDays)}
                                      onChange={(e) => updateBillingContractDraft(user, { noticeDays: e.target.value })}
                                      style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                    />
                                  </label>
                                </div>

                                <div
                                  style={{
                                    marginTop: 12,
                                    display: 'flex',
                                    gap: 16,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#cbd5e1' }}>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(contractDraft.autoRenew)}
                                      onChange={(e) => updateBillingContractDraft(user, { autoRenew: e.target.checked })}
                                    />
                                    Auto-renew after initial term
                                  </label>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#cbd5e1' }}>
                                    <input
                                      type="checkbox"
                                      checked={Boolean(contractDraft.onSiteSupportIncluded)}
                                      onChange={(e) => updateBillingContractDraft(user, { onSiteSupportIncluded: e.target.checked })}
                                    />
                                    On-site support included
                                  </label>
                                </div>

                                <div style={{ marginTop: 14 }}>
                                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                    Optional Clauses
                                  </div>
                                  <div
                                    style={{
                                      marginTop: 8,
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                      gap: 8,
                                    }}
                                  >
                                    {(contractTemplate?.availableClauseKeys || []).map((clauseKey) => {
                                      const clause = billingContractCatalog?.clauses?.[clauseKey];
                                      const checked = contractDraft.selectedClauseKeys.includes(clauseKey);
                                      if (!clause) return null;
                                      return (
                                        <label
                                          key={`${user.id}:contract-clause:${clauseKey}`}
                                          style={{
                                            display: 'grid',
                                            gap: 4,
                                            padding: '10px 12px',
                                            borderRadius: 12,
                                            border: '1px solid rgba(71,85,105,0.45)',
                                            background: checked ? 'rgba(16,185,129,0.12)' : 'rgba(2,6,23,0.34)',
                                            cursor: 'pointer',
                                          }}
                                        >
                                          <span style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 600, color: '#e5e7eb' }}>
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={(e) => {
                                                const nextSelected = e.target.checked
                                                  ? [...contractDraft.selectedClauseKeys, clauseKey]
                                                  : contractDraft.selectedClauseKeys.filter((entry) => entry !== clauseKey);
                                                updateBillingContractDraft(user, { selectedClauseKeys: nextSelected });
                                              }}
                                            />
                                            {clause.label}
                                          </span>
                                          <span style={{ fontSize: 12, color: '#9ca3af' }}>{clause.summary}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>

                                <label style={{ marginTop: 14, display: 'grid', gap: 6 }}>
                                  <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#86efac' }}>
                                    Special Terms
                                  </span>
                                  <textarea
                                    rows={4}
                                    value={contractDraft.specialTerms || ''}
                                    onChange={(e) => updateBillingContractDraft(user, { specialTerms: e.target.value }, { save: false })}
                                    style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 10, padding: '10px 12px' }}
                                  />
                                </label>

                                <div
                                  style={{
                                    marginTop: 16,
                                    padding: 18,
                                    borderRadius: 18,
                                    border: '1px solid rgba(148,163,184,0.28)',
                                    background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))',
                                    color: '#0f172a',
                                    display: 'grid',
                                    gap: 14,
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                                      {contractPreviewDefinition.logoUrl ? (
                                        <img
                                          src={contractPreviewDefinition.logoUrl}
                                          alt="Suitestream default logo"
                                          style={{ width: 144, height: 144, objectFit: 'contain', borderRadius: 20, background: '#fff', border: '1px solid rgba(15,23,42,0.08)', padding: 12 }}
                                        />
                                      ) : (
                                        <div style={{ width: 144, height: 144, borderRadius: 20, border: '1px solid rgba(15,23,42,0.12)', background: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 30 }}>
                                          SS
                                        </div>
                                      )}
                                      <div>
                                        <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em' }}>{contractPreviewDefinition.title}</div>
                                        <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>{contractPreviewDefinition.subtitle}</div>
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>Preview number: {contractPreviewDefinition.referenceNumber}</div>
                                        <div style={{ fontSize: 12, color: '#475569' }}>Issue date: {contractPreviewDefinition.issueDate}</div>
                                      </div>
                                    </div>
                                    <div style={{ minWidth: 240, display: 'grid', gap: 6, fontSize: 12, color: '#334155' }}>
                                      {contractPreviewDefinition.headerMeta.map((entry) => (
                                        <div key={`${user.id}:contract-preview-meta:${entry.label}`}>
                                          <strong>{entry.label}:</strong> {entry.value || '—'}
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                                    {contractPreviewDefinition.parties.map((party) => (
                                      <div key={`${user.id}:contract-preview-party:${party.label}`} style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', display: 'grid', gap: 5 }}>
                                        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>{party.label}</div>
                                        <div style={{ fontWeight: 700 }}>{party.title}</div>
                                        {party.lines.map((line, lineIndex) => (
                                          <div key={`${user.id}:contract-preview-party:${party.label}:${lineIndex}`} style={{ fontSize: 13, color: '#475569' }}>
                                            {line}
                                          </div>
                                        ))}
                                      </div>
                                    ))}
                                  </div>

                                  <div style={{ overflowX: 'auto', borderRadius: 14, border: '1px solid rgba(15,23,42,0.08)' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                                      <thead>
                                        <tr style={{ background: '#e2e8f0', textAlign: 'left' }}>
                                          {contractPreviewDefinition.table.columns.map((column) => (
                                            <th key={`${user.id}:contract-preview-column:${column}`} style={{ padding: '10px 12px', fontSize: 12 }}>
                                              {column}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {contractPreviewDefinition.table.rows.map((row, rowIndex) => (
                                          <tr key={`${user.id}:contract-preview-row:${rowIndex}`} style={{ borderTop: '1px solid rgba(15,23,42,0.08)' }}>
                                            {row.map((cell, cellIndex) => (
                                              <td key={`${user.id}:contract-preview-row:${rowIndex}:${cellIndex}`} style={{ padding: '10px 12px', fontSize: 13 }}>
                                                {cell}
                                              </td>
                                            ))}
                                          </tr>
                                        ))}
                                        {contractPreviewDefinition.table.rows.length === 0 && (
                                          <tr>
                                            <td colSpan={contractPreviewDefinition.table.columns.length} style={{ padding: '12px', fontSize: 13, color: '#64748b' }}>
                                              {contractPreviewDefinition.table.emptyMessage}
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>

                                  <div style={{ display: 'grid', gap: 12 }}>
                                    {contractPreviewDefinition.sections.map((section) => (
                                      <div key={`${user.id}:contract-preview-section:${section.heading}`} style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', display: 'grid', gap: 8 }}>
                                        <div style={{ fontWeight: 700 }}>{section.heading}</div>
                                        <div style={{ display: 'grid', gap: 8 }}>
                                          {section.body.map((line, lineIndex) => (
                                            <div key={`${user.id}:contract-preview-section:${section.heading}:${lineIndex}`} style={{ fontSize: 14, lineHeight: 1.6, color: '#334155' }}>
                                              {line}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(220px, 320px)', gap: 14 }}>
                                    <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', fontSize: 13, color: '#475569', display: 'grid', gap: 6 }}>
                                      {contractPreviewDefinition.notes.map((entry) => (
                                        <div key={`${user.id}:contract-preview-note:${entry.label}`}>
                                          <strong>{entry.label}:</strong> {entry.value || '—'}
                                        </div>
                                      ))}
                                      {contractReadinessReasons.length ? (
                                        <div style={{ color: '#b45309' }}>
                                          Missing contract details: {contractReadinessReasons.join(', ')}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', display: 'grid', gap: 8 }}>
                                      {contractPreviewDefinition.totals.map((entry, index) => (
                                        <div
                                          key={`${user.id}:contract-preview-total:${entry.label}`}
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            paddingTop: index === contractPreviewDefinition.totals.length - 1 ? 8 : 0,
                                            borderTop: index === contractPreviewDefinition.totals.length - 1 ? '1px solid rgba(15,23,42,0.1)' : 'none',
                                            fontSize: index === contractPreviewDefinition.totals.length - 1 ? 16 : 13,
                                          }}
                                        >
                                          <span>{entry.label}</span>
                                          <strong>{entry.value}</strong>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    marginTop: 14,
                                    padding: 14,
                                    borderRadius: 14,
                                    border: '1px solid rgba(71,85,105,0.45)',
                                    background: 'rgba(2,6,23,0.34)',
                                  }}
                                >
                                  <div style={{ fontWeight: 700 }}>Saved Contracts</div>
                                  <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                    Generated PDFs are stored in S3 as immutable agreement records.
                                  </div>
                                  {contractDocuments.length ? (
                                    <div
                                      style={{
                                        marginTop: 12,
                                        display: 'grid',
                                        gap: 10,
                                      }}
                                    >
                                      {contractDocuments.map((documentEntry) => {
                                        const contractDeletionKey = `${user.id}:${documentEntry.id}`;
                                        const isDeletingContractDocument = Boolean(contractDocumentDeletingIds[contractDeletionKey]);
                                        return (
                                          <div
                                            key={`${user.id}:contract-document:${documentEntry.id}`}
                                            style={{
                                              padding: 12,
                                              borderRadius: 12,
                                              border: '1px solid rgba(71,85,105,0.45)',
                                              background: 'rgba(15,23,42,0.42)',
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              gap: 12,
                                              alignItems: 'center',
                                              flexWrap: 'wrap',
                                            }}
                                          >
                                            <div style={{ display: 'grid', gap: 4 }}>
                                              <div style={{ fontWeight: 700 }}>
                                                {documentEntry.referenceNumber || documentEntry.fileName || 'Stored contract'}
                                              </div>
                                              <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                                                {(documentEntry.templateLabel || 'Contract')} • {formatIsoDate(documentEntry.contractStartDate)} • {formatNumber(documentEntry.termMonths || 0)} month{Number(documentEntry.termMonths || 0) === 1 ? '' : 's'} • {formatVnd(documentEntry.totalWithVat || 0)}
                                              </div>
                                              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                                {(documentEntry.coveredSiteNames || []).join(' | ') || 'No covered sites recorded'}
                                              </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                              <button
                                                type="button"
                                                disabled={isDeletingContractDocument}
                                                onClick={() => deleteContractDocument(user, documentEntry)}
                                              >
                                                {isDeletingContractDocument ? 'Deleting…' : 'Delete'}
                                              </button>
                                              <span
                                                style={{
                                                  padding: '6px 10px',
                                                  borderRadius: 999,
                                                  border: '1px solid rgba(71,85,105,0.6)',
                                                  background: 'rgba(15,23,42,0.72)',
                                                  color: '#cbd5e1',
                                                  fontSize: 11,
                                                  fontWeight: 700,
                                                  textTransform: 'uppercase',
                                                  letterSpacing: '0.04em',
                                                }}
                                              >
                                                {documentEntry.status || 'stored'}
                                              </span>
                                              <button
                                                type="button"
                                                disabled={!documentEntry.downloadUrl || isDeletingContractDocument}
                                                onClick={() => openDownloadUrl(documentEntry.downloadUrl)}
                                              >
                                                Download
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
                                      No contract PDFs generated yet for this billing entity.
                                    </div>
                                  )}
                                </div>
                                  </>
                                )}
                              </div>

                              <div
                                style={{
                                  order: 1,
                                  marginTop: 18,
                                  padding: 18,
                                  borderRadius: 18,
                                  border: '1px solid rgba(71,85,105,0.5)',
                                  background:
                                    'radial-gradient(circle at top right, rgba(59,130,246,0.12), transparent 28%), linear-gradient(180deg, rgba(17,24,39,0.88), rgba(15,23,42,0.96))',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 18 }}>Invoices & Pricing Workbench</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                      Keep invoice controls, invoice prep, and per-site commercial pricing together in one tidy workspace.
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {renderBillingSourceControls('service')}
                                    <button type="button" onClick={() => toggleBillingWorkbenchCollapsed(user.id)}>
                                      {isWorkbenchCollapsed ? 'Show' : 'Hide'}
                                    </button>
                                  </div>
                                </div>

                                {!isWorkbenchCollapsed && (
                                  <>
                                <div
                                  style={{
                                    marginTop: 14,
                                    padding: 16,
                                    borderRadius: 16,
                                    border: '1px solid rgba(71,85,105,0.5)',
                                    background: 'rgba(15,23,42,0.45)',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    <div>
                                      <div style={{ fontWeight: 700, fontSize: 18 }}>Invoice Preview</div>
                                      <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                        Preview only. Final tax authority code, provider details, invoice number, and signature are assigned when the real e-invoice is issued.
                                      </div>
                                      {!invoiceReadiness.ready && invoiceReadinessReasons.length > 0 && (
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>
                                          Missing: {invoiceReadinessReasons.join(' • ')}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                      <span
                                        style={{
                                          padding: '6px 10px',
                                          borderRadius: 999,
                                          background: invoiceReadiness.ready ? 'rgba(34,197,94,0.16)' : 'rgba(251,191,36,0.16)',
                                          color: invoiceReadiness.ready ? '#86efac' : '#fde68a',
                                          fontSize: 12,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {invoiceReadiness.ready ? 'Invoice Ready' : 'Missing Invoice Details'}
                                      </span>
                                      <button type="button" onClick={() => toggleExpandedInvoicePreview(user.id)}>
                                        {isInvoicePreviewExpanded ? 'Hide Preview' : 'Show Preview'}
                                      </button>
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      marginTop: 14,
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                                      gap: 10,
                                    }}
                                  >
                                    <label style={{ display: 'grid', gap: 6 }}>
                                      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                        Start Date
                                      </span>
                                      <input
                                        type="date"
                                        value={billingProfileDraft.invoiceStartDate || ''}
                                        onChange={(e) => updateBillingProfileDraft(user, 'invoiceStartDate', e.target.value)}
                                        style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                      />
                                    </label>
                                    <label style={{ display: 'grid', gap: 6 }}>
                                      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                        Invoice Every
                                      </span>
                                      <select
                                        value={String(invoiceScheduleDraft.cadenceMonths)}
                                        onChange={(e) => updateBillingProfileDraft(user, 'invoiceCadenceMonths', Number(e.target.value))}
                                        style={{ color: '#e5e7eb', background: '#0f172a', border: '1px solid #475569', borderRadius: 8, padding: '9px 10px' }}
                                      >
                                        {INVOICE_CADENCE_OPTIONS.map((months) => (
                                          <option key={`${user.id}:cadence:${months}`} value={String(months)}>
                                            {months} month{months === 1 ? '' : 's'}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                    <div style={{ display: 'grid', gap: 6, alignContent: 'end' }}>
                                      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                        Next Invoice Due
                                      </span>
                                      <div style={{ padding: '9px 10px', borderRadius: 8, border: '1px solid #475569', background: '#020617', color: '#e5e7eb', minHeight: 40 }}>
                                        {formatIsoDate(invoiceScheduleDraft.nextDueDate)}
                                      </div>
                                    </div>
                                    <div style={{ display: 'grid', gap: 6, alignContent: 'end' }}>
                                      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                        Billing Period
                                      </span>
                                      <div style={{ padding: '9px 10px', borderRadius: 8, border: '1px solid #475569', background: '#020617', color: '#e5e7eb', minHeight: 40 }}>
                                        {formatIsoDate(invoiceScheduleDraft.periodStart)} - {formatIsoDate(invoiceScheduleDraft.periodEndInclusive)}
                                      </div>
                                    </div>
                                    <div style={{ display: 'grid', gap: 6, alignContent: 'end' }}>
                                      <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                        Invoice Status
                                      </span>
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <button
                                          type="button"
                                          disabled={isSavingBillingProfile || isResettingInvoicing}
                                          onClick={() => {
                                            const nextProfile = {
                                              ...billingProfileDraft,
                                              invoiceStartDate: billingProfileDraft.invoiceStartDate || getTodayIsoDate(),
                                              invoicingActive: true,
                                            };
                                            setBillingProfileDrafts((prev) => ({ ...prev, [user.id]: nextProfile }));
                                            saveBillingProfile(user, nextProfile, { message: `Started invoicing for ${user.email}` });
                                          }}
                                        >
                                          {billingProfileDraft.invoicingActive ? (isSavingBillingProfile ? 'Saving…' : 'Invoicing Active') : 'Start Invoicing'}
                                        </button>
                                        {(billingProfileDraft.invoicingActive || invoiceDocuments.length > 0 || billingProfileDraft.invoiceStartDate) && (
                                          <button
                                            type="button"
                                            disabled={isResettingInvoicing}
                                            onClick={() => resetInvoicing(user)}
                                          >
                                            {isResettingInvoicing ? 'Resetting…' : 'Reset Invoicing'}
                                          </button>
                                        )}
                                        {billingProfileDraft.invoicingActive && (
                                          <button
                                            type="button"
                                            disabled={isGeneratingInvoicePdf || isResettingInvoicing}
                                            onClick={() => generateInvoicePdf(user, 'service')}
                                          >
                                            {isGeneratingInvoicePdf ? 'Generating…' : 'Generate PDF'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      marginTop: 14,
                                      padding: 14,
                                      borderRadius: 14,
                                      border: '1px solid rgba(71,85,105,0.45)',
                                      background: 'rgba(2,6,23,0.34)',
                                    }}
                                  >
                                    <div style={{ fontWeight: 700 }}>Saved Invoices</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                      Generated PDFs are stored in S3 as immutable billing records.
                                    </div>
                                    {invoiceDocuments.length ? (
                                      <div
                                        style={{
                                          marginTop: 12,
                                          display: 'grid',
                                          gap: 10,
                                        }}
                                      >
                                        {invoiceDocuments.map((documentEntry) => {
                                          const invoiceDeletionKey = `${user.id}:${documentEntry.id}`;
                                          const isDeletingInvoiceDocument = Boolean(invoiceDocumentDeletingIds[invoiceDeletionKey]);
                                          return (
                                            <div
                                              key={`${user.id}:invoice-document:${documentEntry.id}`}
                                              style={{
                                                padding: 12,
                                                borderRadius: 12,
                                                border: '1px solid rgba(71,85,105,0.45)',
                                                background: 'rgba(15,23,42,0.42)',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 12,
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                              }}
                                            >
                                              <div style={{ display: 'grid', gap: 4 }}>
                                                <div style={{ fontWeight: 700 }}>{documentEntry.referenceNumber || documentEntry.fileName || 'Stored invoice'}</div>
                                                <div style={{ fontSize: 11, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                  {(documentEntry.invoiceMode || 'standard').replace('_', ' ')}
                                                  {documentEntry.issuerCompanyName ? ` • ${documentEntry.issuerCompanyName}` : ''}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                                                  {formatIsoDate(documentEntry.issueDate)} • {formatIsoDate(documentEntry.periodStart)} - {formatIsoDate(documentEntry.periodEndInclusive)} • {formatVnd(documentEntry.totalWithVat || 0)}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                                                  {(documentEntry.coveredSiteNames || []).join(' | ') || 'No covered sites recorded'}
                                                </div>
                                              </div>
                                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                {documentEntry.draftSnapshot && ['time_materials', 'combined'].includes(documentEntry.invoiceMode) && (
                                                  <button
                                                    type="button"
                                                    disabled={isDeletingInvoiceDocument || isResettingInvoicing}
                                                    onClick={() => loadInvoiceDraft(user, documentEntry)}
                                                  >
                                                    Load & Adjust
                                                  </button>
                                                )}
                                                <button
                                                  type="button"
                                                  disabled={isDeletingInvoiceDocument || isResettingInvoicing}
                                                  onClick={() => deleteInvoiceDocument(user, documentEntry)}
                                                >
                                                  {isDeletingInvoiceDocument ? 'Deleting…' : 'Delete'}
                                                </button>
                                                <button
                                                  type="button"
                                                  disabled={isDeletingInvoiceDocument || isResettingInvoicing}
                                                  onClick={() => copyRedInvoiceInfo({
                                                    userId: user.id,
                                                    invoiceDocument: documentEntry,
                                                    customerProfile: effectiveBillingEntityProfile,
                                                    coveredSites,
                                                    billingScopeLabel: formatBillingScopeLabel(billingProfile.billingScope),
                                                  })}
                                                >
                                                  Copy Red Invoice
                                                </button>
                                                <span
                                                  style={{
                                                    padding: '6px 10px',
                                                    borderRadius: 999,
                                                    border: '1px solid rgba(71,85,105,0.6)',
                                                    background: 'rgba(15,23,42,0.72)',
                                                    color: '#cbd5e1',
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em',
                                                  }}
                                                >
                                                  {documentEntry.status || 'stored'}
                                                </span>
                                                <button
                                                  type="button"
                                                  disabled={!documentEntry.downloadUrl || isDeletingInvoiceDocument}
                                                  onClick={() => openDownloadUrl(documentEntry.downloadUrl)}
                                                >
                                                  Download
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
                                        No invoice PDFs stored yet.
                                      </div>
                                    )}
                                  </div>

                                  {isInvoicePreviewExpanded && (
                                    <div
                                      style={{
                                        marginTop: 16,
                                        padding: 18,
                                        borderRadius: 18,
                                        border: '1px solid rgba(148,163,184,0.28)',
                                        background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96))',
                                        color: '#0f172a',
                                        display: 'grid',
                                        gap: 14,
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                                          {companyProfile.logoUrl ? (
                                            <img
                                              src={companyProfile.logoUrl}
                                              alt={`${companyProfile.companyName || 'Billing company'} logo`}
                                              style={{ width: 144, height: 144, objectFit: 'contain', borderRadius: 20, background: '#fff', border: '1px solid rgba(15,23,42,0.08)', padding: 12 }}
                                            />
                                          ) : (
                                            <div style={{ width: 144, height: 144, borderRadius: 20, border: '1px solid rgba(15,23,42,0.12)', background: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 30 }}>
                                              SS
                                            </div>
                                          )}
                                          <div>
                                            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em' }}>VAT INVOICE</div>
                                            <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>Hoa don gia tri gia tang</div>
                                            <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>Preview number: {invoicePreviewNumber}</div>
                                            <div style={{ fontSize: 12, color: '#475569' }}>Issue date: {formatIsoDate(getTodayIsoDate())}</div>
                                          </div>
                                        </div>
                                        <div style={{ minWidth: 240, display: 'grid', gap: 6, fontSize: 12, color: '#334155' }}>
                                          <div><strong>Tax authority code:</strong> Assigned on issue</div>
                                          <div><strong>E-invoice provider:</strong> Assigned on issue</div>
                                          <div><strong>Billing cadence:</strong> {invoiceScheduleDraft.cadenceMonths} month{invoiceScheduleDraft.cadenceMonths === 1 ? '' : 's'}</div>
                                          <div><strong>Service period:</strong> {formatIsoDate(invoiceScheduleDraft.periodStart)} - {formatIsoDate(invoiceScheduleDraft.periodEndInclusive)}</div>
                                          <div><strong>Next invoice due:</strong> {formatIsoDate(invoiceScheduleDraft.nextDueDate)}</div>
                                        </div>
                                      </div>

                                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                                        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', display: 'grid', gap: 5 }}>
                                          <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>Seller</div>
                                          <div style={{ fontWeight: 700 }}>{companyProfile.companyName || 'Company details missing'}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>{companyProfile.address || '—'}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Tax code: {companyProfile.taxCode || '—'}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Representative: {companyProfile.representative || '—'}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Phone: {companyProfile.phone || '—'} | Email: {companyProfile.email || '—'}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Bank: {companyProfile.bankName || '—'} | {companyProfile.bankAccountNumber || '—'}</div>
                                        </div>
                                        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', display: 'grid', gap: 5 }}>
                                          <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>Buyer</div>
                                          <div style={{ fontWeight: 700 }}>{billingProfile.legalBusinessName || user.email}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>
                                            {[billingProfile.billingAddressLine1, billingProfile.billingAddressLine2, billingProfile.ward, billingProfile.district, billingProfile.cityProvince, billingProfile.countryCode]
                                              .filter(Boolean)
                                              .join(', ') || '—'}
                                          </div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Tax code: {billingProfile.taxCode || '—'}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Signer: {billingProfile.signerName || '—'} {billingProfile.signerTitle ? `| ${billingProfile.signerTitle}` : ''}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Billing email: {billingProfile.billingEmail || '—'}</div>
                                          <div style={{ fontSize: 13, color: '#475569' }}>Covered sites: {coveredSites.map((site) => site.name).join(' | ') || '—'}</div>
                                        </div>
                                      </div>

                                      <div style={{ overflowX: 'auto', borderRadius: 14, border: '1px solid rgba(15,23,42,0.08)' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                                          <thead>
                                            <tr style={{ background: '#e2e8f0', textAlign: 'left' }}>
                                              <th style={{ padding: '10px 12px', fontSize: 12 }}>Description / Dien giai</th>
                                              <th style={{ padding: '10px 12px', fontSize: 12 }}>Unit</th>
                                              <th style={{ padding: '10px 12px', fontSize: 12 }}>Qty</th>
                                              <th style={{ padding: '10px 12px', fontSize: 12 }}>Unit Price</th>
                                              <th style={{ padding: '10px 12px', fontSize: 12 }}>Amount</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {invoiceLineItemsDraft.map((line) => (
                                              <tr key={`${user.id}:invoice-line:${line.id}`} style={{ borderTop: '1px solid rgba(15,23,42,0.08)' }}>
                                                <td style={{ padding: '10px 12px', fontSize: 13 }}>{line.description}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 13 }}>site-month</td>
                                                <td style={{ padding: '10px 12px', fontSize: 13 }}>{line.quantity}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 13 }}>{formatVnd(line.unitPrice)}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 13 }}>{formatVnd(line.amount)}</td>
                                              </tr>
                                            ))}
                                            {invoiceLineItemsDraft.length === 0 && (
                                              <tr>
                                                <td colSpan={5} style={{ padding: '12px', fontSize: 13, color: '#64748b' }}>
                                                  No covered sites selected for this billing entity yet.
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>

                                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(220px, 320px)', gap: 14 }}>
                                        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', fontSize: 13, color: '#475569', display: 'grid', gap: 6 }}>
                                          <div><strong>Notes:</strong> {billingProfile.invoiceNotes || 'Subscription services billed periodically in advance.'}</div>
                                          <div><strong>Payment notes:</strong> {billingProfile.paymentNotes || 'Bank transfer to the account listed above.'}</div>
                                          {!invoiceReadiness.ready && (
                                            <div style={{ color: '#b45309' }}>
                                              Missing invoice details: {[...invoiceReadiness.issuerMissingFields, ...invoiceReadiness.customerMissingFields].join(', ')}
                                            </div>
                                          )}
                                        </div>
                                        <div style={{ padding: 14, borderRadius: 14, border: '1px solid rgba(15,23,42,0.1)', background: '#fff', display: 'grid', gap: 8 }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                            <span>Subtotal</span>
                                            <strong>{formatVnd(invoicePreviewSubtotal)}</strong>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                            <span>VAT ({billingVatPercent}%)</span>
                                            <strong>{formatVnd(invoicePreviewVatAmount)}</strong>
                                          </div>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, paddingTop: 8, borderTop: '1px solid rgba(15,23,42,0.1)', fontSize: 16 }}>
                                            <span>Total payment</span>
                                            <strong>{formatVnd(invoicePreviewTotalWithVat)}</strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {hasFreePackageOnMultiSiteAccount && (
                                  <div
                                    style={{
                                      marginBottom: 14,
                                      padding: '12px 14px',
                                      borderRadius: 14,
                                      border: '1px solid rgba(251,191,36,0.35)',
                                      background: 'rgba(120,53,15,0.22)',
                                      color: '#fde68a',
                                      fontSize: 13,
                                    }}
                                  >
                                    One or more sites on this account are assigned to `Free`, but `Free` is intended for single-site accounts only. Keep it if this is a deliberate trial / exception, otherwise move those sites to `Basic` or higher.
                                  </div>
                                )}

                                <div
                                  style={{
                                    marginBottom: 14,
                                    padding: 16,
                                    borderRadius: 16,
                                    border: '1px solid rgba(71,85,105,0.5)',
                                    background: 'rgba(15,23,42,0.45)',
                                    display: 'grid',
                                    gap: 14,
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                                    <div>
                                      <div style={{ fontWeight: 700, fontSize: 18 }}>Invoice Prep</div>
                                      <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                        Manual red-invoice prep using saved customer details, current site scope, and your platform company details.
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        padding: '8px 10px',
                                        borderRadius: 999,
                                        background: invoiceReadiness.ready ? 'rgba(34,197,94,0.16)' : 'rgba(251,191,36,0.16)',
                                        color: invoiceReadiness.ready ? '#86efac' : '#fde68a',
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {invoiceReadiness.ready ? 'Invoice Ready' : 'Missing Invoice Details'}
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                      gap: 12,
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: 14,
                                        borderRadius: 14,
                                        border: '1px solid rgba(71,85,105,0.45)',
                                        background: 'rgba(2,6,23,0.35)',
                                        display: 'grid',
                                        gap: 6,
                                      }}
                                    >
                                      <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' }}>From</div>
                                      <div style={{ fontWeight: 700 }}>{companyProfile.companyName || 'Company details missing'}</div>
                                      <div style={{ fontSize: 12, color: '#cbd5e1' }}>{companyProfile.address || 'No address configured'}</div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Tax code: {companyProfile.taxCode || '—'}</div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Representative: {companyProfile.representative || '—'}</div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Contact: {companyProfile.phone || '—'} {companyProfile.email ? `| ${companyProfile.email}` : ''}</div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Bank: {companyProfile.bankName || '—'} {companyProfile.bankAccountNumber ? `| ${companyProfile.bankAccountNumber}` : ''}</div>
                                      {invoiceReadiness.issuerMissingFields.length > 0 && (
                                        <div style={{ marginTop: 4, fontSize: 12, color: '#fca5a5' }}>
                                          Missing: {invoiceReadiness.issuerMissingFields.join(', ')}
                                        </div>
                                      )}
                                    </div>

                                    <div
                                      style={{
                                        padding: 14,
                                        borderRadius: 14,
                                        border: '1px solid rgba(71,85,105,0.45)',
                                        background: 'rgba(2,6,23,0.35)',
                                        display: 'grid',
                                        gap: 6,
                                      }}
                                    >
                                      <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' }}>Bill To</div>
                                      <div style={{ fontWeight: 700 }}>{effectiveBillingEntityProfile.legalBusinessName || user.email}</div>
                                      <div style={{ fontSize: 12, color: '#cbd5e1' }}>
                                        {formatBillingAddress(effectiveBillingEntityProfile)}
                                      </div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Tax code: {effectiveBillingEntityProfile.taxCode || '—'}</div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Signer: {effectiveBillingEntityProfile.signerName || '—'} {effectiveBillingEntityProfile.signerTitle ? `| ${effectiveBillingEntityProfile.signerTitle}` : ''}</div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Billing contact: {effectiveBillingEntityProfile.billingContactName || '—'} {effectiveBillingEntityProfile.billingEmail ? `| ${effectiveBillingEntityProfile.billingEmail}` : ''}</div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Scope: {formatBillingScopeLabel(billingProfile.billingScope)}</div>
                                      {billingProfile.billingScope === 'per_site' && activeBillingSite && (
                                        <div style={{ fontSize: 12, color: '#93c5fd' }}>Active site entity: {activeBillingSite.name}</div>
                                      )}
                                      {billingProfile.billingScope === 'per_site' && (billingProfile.selectedSiteIds || []).length > 1 && (
                                        <label style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                                          <span style={{ color: '#9ca3af', fontSize: 12 }}>Preview site entity</span>
                                          <select
                                            value={String(billingProfileDraft.activeBillingSiteId || activeBillingSite?.id || '')}
                                            onChange={(e) => updateBillingProfileDraft(user, 'activeBillingSiteId', Number(e.target.value) || null)}
                                          >
                                            {(billingProfile.selectedSiteIds || []).map((siteId) => {
                                              const site = sites.find((entry) => Number(entry.id) === Number(siteId));
                                              return (
                                                <option key={`${user.id}:billing-preview-site:${siteId}`} value={String(siteId)}>
                                                  {site?.name || `Site ${siteId}`}
                                                </option>
                                              );
                                            })}
                                          </select>
                                        </label>
                                      )}
                                      {invoiceReadiness.customerMissingFields.length > 0 && (
                                        <div style={{ marginTop: 4, fontSize: 12, color: '#fca5a5' }}>
                                          Missing: {invoiceReadiness.customerMissingFields.join(', ')}
                                        </div>
                                      )}
                                    </div>

                                    <div
                                      style={{
                                        padding: 14,
                                        borderRadius: 14,
                                        border: '1px solid rgba(71,85,105,0.45)',
                                        background: 'rgba(2,6,23,0.35)',
                                        display: 'grid',
                                        gap: 6,
                                      }}
                                    >
                                      <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' }}>Covered Sites</div>
                                      <div style={{ fontWeight: 700 }}>
                                        {coveredSites.length} of {sites.length} sites in this billing entity
                                      </div>
                                      <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                        {coveredSites.length
                                          ? coveredSites.map((site) => site.name).join(' | ')
                                          : 'No covered sites selected yet'}
                                      </div>
                                      {uncoveredSites.length > 0 && (
                                        <div style={{ fontSize: 12, color: '#fde68a' }}>
                                          Not covered by this billing entity: {uncoveredSites.map((site) => site.name).join(' | ')}
                                        </div>
                                      )}
                                      <div style={{ marginTop: 4, fontSize: 12, color: '#cbd5f5' }}>
                                        Covered total ex VAT: {formatNumber(user.coveredManualMonthlyTotal || 0)}
                                      </div>
                                      <div style={{ fontSize: 12, color: '#86efac' }}>
                                        Covered total inc VAT: {formatNumber(user.coveredManualMonthlyTotalWithVat || 0)}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 18 }}>Pricing Workbench</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                      Assigned package pricing drives the base monthly rate and included allowances. Use the feature toggles and prices below only for manual exceptions.
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      minWidth: 280,
                                      padding: '14px 16px',
                                      borderRadius: 16,
                                      border: '1px solid rgba(96,165,250,0.22)',
                                      background: 'linear-gradient(135deg, rgba(30,41,59,0.85), rgba(15,23,42,0.95))',
                                      textAlign: 'right',
                                    }}
                                  >
                                    <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#93c5fd' }}>Billing Monthly Rate</div>
                                    <div style={{ marginTop: 4, fontSize: 22, fontWeight: 700 }}>{formatNumber(agreedMonthlyRate)}/mo ex VAT</div>
                                    <div style={{ marginTop: 6, fontSize: 13, color: '#86efac' }}>
                                      {formatNumber(applyVatToAmount(agreedMonthlyRate, billingVatPercent))}/mo inc VAT
                                    </div>
                                    {contractDraft.monthlyRate !== null && (
                                      <div style={{ marginTop: 6, fontSize: 12, color: '#fde68a' }}>
                                        Contract override; calculated site total is {formatNumber(calculatedMonthlyRate)}/mo.
                                      </div>
                                    )}
                                    <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
                                      3m/6m/12m ex VAT: {formatNumber(total3Months)} | {formatNumber(total6Months)} | {formatNumber(total12Months)}
                                    </div>
                                    <div style={{ marginTop: 6, fontSize: 12, color: '#86efac' }}>
                                      3m/6m/12m inc VAT: {formatNumber(total3MonthsWithVat)} | {formatNumber(total6MonthsWithVat)} | {formatNumber(total12MonthsWithVat)}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    marginTop: 14,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
                                    gap: 14,
                                  }}
                                >
                                  {sites.map((site) => {
                                    const siteDraft = billingSiteDrafts[site.id] || buildBillingSiteDraft(site, billingDefaults);
                                    const pricingDraft = siteDraft.pricing;
                                    const isSavingSitePricing = Boolean(billingSiteSavingIds[site.id]);
                                    const activeColumns = getActiveBillingColumnsForSite(site).filter((column) => column.key !== 'sites');
                                    const assignedPackageEstimate = site.assignedPackageEstimate;
                                    const assignedPackageConfig = (billingPackageCatalog || DEFAULT_BILLING_PACKAGE_CATALOG)[site.resolvedPackageKey]
                                      || DEFAULT_BILLING_PACKAGE_CATALOG[site.resolvedPackageKey];
                                    const assignedPackageCopy = getPackageUiCopy(assignedPackageConfig);
                                    const isCustomPackage = site.resolvedPackageKey !== (site.defaultPackageKey || 'free');
                                    const hasCustomFeatureSelection = JSON.stringify(normalizeBillingFeatureSelection(site.featureSelection, site))
                                      !== JSON.stringify(site.resolvedFeatureSelection);
                                    const hasCustomQuantityOverrides = JSON.stringify(normalizeBillingQuantityOverrides(site.quantityOverrides))
                                      !== JSON.stringify(site.resolvedQuantityOverrides);
                                    return (
                                      <div
                                        key={`pricing-${site.id}`}
                                        style={{
                                          padding: 16,
                                          borderRadius: 16,
                                          border: '1px solid rgba(71,85,105,0.55)',
                                          background:
                                            'radial-gradient(circle at top right, rgba(34,197,94,0.08), transparent 28%), linear-gradient(180deg, rgba(30,41,59,0.82), rgba(15,23,42,0.96))',
                                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                                        }}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                          <div>
                                            <div style={{ fontWeight: 700 }}>{site.name}</div>
                                            <div style={{ marginTop: 3, fontSize: 12, color: '#9ca3af' }}>
                                              {site.siteType === 'destination' ? 'Destination' : 'Venue'} • {isSavingSitePricing ? 'Saving…' : (isCustomPackage || hasCustomFeatureSelection || hasCustomQuantityOverrides || site.pricingOverride ? 'Custom billing' : 'Using defaults')}
                                            </div>
                                          </div>
                                          <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700 }}>{formatNumber(site.manualMonthlyTotal || 0)} ex VAT</div>
                                            <div style={{ fontSize: 12, color: '#86efac' }}>{formatNumber(applyVatToAmount(site.manualMonthlyTotal || 0, billingVatPercent))} inc VAT</div>
                                            <div style={{ marginTop: 3, fontSize: 11, color: '#cbd5f5' }}>
                                              {assignedPackageEstimate?.isPoa
                                                ? 'Enterprise / POA package'
                                                : `${formatNumber(assignedPackageEstimate?.basePrice || 0)} base + ${formatNumber(Math.max(0, Number(site.manualMonthlyTotal || 0) - Number(pricingDraft.sites || 0)))} extras`}
                                            </div>
                                          </div>
                                        </div>

                                        <div
                                          style={{
                                            marginTop: 12,
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(170px, 220px)',
                                            gap: 10,
                                          }}
                                        >
                                          <label style={{ display: 'grid', gap: 6 }}>
                                            <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                              Per-Site Base Price
                                            </span>
                                            <input
                                              type="number"
                                              min="0"
                                              step="1000"
                                              value={pricingDraft.sites}
                                              onChange={(e) => updateBillingSitePricingDraft(site, 'sites', e.target.value)}
                                              style={{
                                                width: '100%',
                                                borderRadius: 8,
                                                border: '1px solid #475569',
                                                background: '#0f172a',
                                                color: '#e5e7eb',
                                                padding: '8px 10px',
                                                fontWeight: 700,
                                              }}
                                            />
                                          </label>
                                        </div>

                                        {assignedPackageConfig?.maxSitesPerAccount === 1 && sites.length > 1 && (
                                          <div
                                            style={{
                                              marginTop: 12,
                                              padding: '10px 12px',
                                              borderRadius: 12,
                                              border: '1px solid rgba(251,191,36,0.35)',
                                              background: 'rgba(120,53,15,0.18)',
                                              color: '#fde68a',
                                              fontSize: 12,
                                            }}
                                          >
                                            `{assignedPackageEstimate?.label || 'Free'}` is marked as single-site only in the package matrix. Keep it only if this site is a deliberate trial / ad-supported exception.
                                          </div>
                                        )}

                                        <div
                                          style={{
                                            marginTop: 14,
                                            padding: 12,
                                            borderRadius: 12,
                                            border: '1px solid rgba(71,85,105,0.5)',
                                            background: 'rgba(15,23,42,0.55)',
                                            display: 'grid',
                                            gap: 10,
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: 'grid',
                                              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                              gap: 10,
                                            }}
                                          >
                                            <label style={{ display: 'grid', gap: 6 }}>
                                              <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                                Assigned Package
                                              </span>
                                              <select
                                                value={siteDraft.packageKey}
                                                onChange={(e) => updateBillingSitePackageDraft(site, e.target.value)}
                                                style={{
                                                  width: '100%',
                                                  borderRadius: 8,
                                                  border: '1px solid #475569',
                                                  background: '#0f172a',
                                                  color: '#e5e7eb',
                                                  padding: '8px 10px',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {Object.values(billingPackageCatalog || DEFAULT_BILLING_PACKAGE_CATALOG).map((pkg) => (
                                                  <option key={pkg.key} value={pkg.key}>
                                                    {pkg.label} • {pkg.basePrice == null ? 'POA' : `${formatNumber(pkg.basePrice)}/mo`}
                                                  </option>
                                                ))}
                                              </select>
                                            </label>
                                          </div>
                                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <span
                                              style={{
                                                padding: '6px 8px',
                                                borderRadius: 999,
                                                fontSize: 11,
                                                letterSpacing: '0.05em',
                                                textTransform: 'uppercase',
                                                background: assignedPackageEstimate?.advertisingEnabled ? 'rgba(251,191,36,0.18)' : 'rgba(34,197,94,0.15)',
                                                color: assignedPackageEstimate?.advertisingEnabled ? '#fde68a' : '#86efac',
                                              }}
                                            >
                                              {assignedPackageEstimate?.advertisingEnabled ? 'Advertising On' : 'Advertising Off'}
                                            </span>
                                            <span
                                              style={{
                                                padding: '6px 8px',
                                                borderRadius: 999,
                                                fontSize: 11,
                                                letterSpacing: '0.05em',
                                                textTransform: 'uppercase',
                                                background: 'rgba(59,130,246,0.15)',
                                                color: '#93c5fd',
                                              }}
                                            >
                                              Default {assignedPackageEstimate?.label || formatSubscription(site.resolvedPackageKey)}
                                            </span>
                                            <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                              Includes {formatIncludedAllowance(assignedPackageEstimate)}
                                            </span>
                                          </div>
                                          <div style={{ display: 'grid', gap: 6, fontSize: 12, color: '#cbd5e1' }}>
                                            <div><strong>Assigned:</strong> {assignedPackageCopy.summary}</div>
                                            <div><strong>Website:</strong> {assignedPackageCopy.websitePolicy}</div>
                                            <div><strong>AI:</strong> {assignedPackageCopy.aiPolicy}</div>
                                            <div><strong>API:</strong> {assignedPackageCopy.apiPolicy}</div>
                                          </div>
                                        </div>

                                        <div
                                          style={{
                                            marginTop: 14,
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                                            gap: 10,
                                          }}
                                        >
                                          {activeColumns.map((column) => {
                                            const isEnabled = Boolean(siteDraft.featureSelection?.[column.key]);
                                            const availableQuantity = getAvailableIncludedQuantity(
                                              site,
                                              site.resolvedQuantityOverrides,
                                              assignedPackageEstimate,
                                              column.key
                                            );
                                            return (
                                              <div
                                                key={`${site.id}:${column.key}`}
                                                onClick={() => toggleBillingSiteFeatureSelection(site, column.key)}
                                                style={{
                                                  display: 'grid',
                                                  gap: 6,
                                                  padding: '10px 10px 12px',
                                                  borderRadius: 12,
                                                  border: `1px solid ${isEnabled ? 'rgba(96,165,250,0.6)' : 'rgba(71,85,105,0.5)'}`,
                                                  background: isEnabled ? 'rgba(30,41,59,0.92)' : 'rgba(15,23,42,0.45)',
                                                  cursor: 'pointer',
                                                  opacity: isEnabled ? 1 : 0.7,
                                                }}
                                              >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                                                  <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#93c5fd' }}>
                                                    {column.label}
                                                  </span>
                                                  <span style={{ fontSize: 11, color: isEnabled ? '#86efac' : '#9ca3af' }}>
                                                    {isEnabled ? 'On' : 'Off'}
                                                  </span>
                                                </div>
                                                <span style={{ fontSize: 12, color: '#9ca3af' }}>
                                                  {formatNumber(site[column.key] || 0)} detected • {formatNumber(availableQuantity)} available
                                                </span>
                                                <input
                                                  type="number"
                                                  min="0"
                                                  max={BILLING_BINARY_KEYS.has(column.key) || column.key === 'sites' ? '1' : undefined}
                                                  step="1"
                                                  value={siteDraft.quantityOverrides?.[column.key] ?? ''}
                                                  placeholder={String(BILLING_BINARY_KEYS.has(column.key) || column.key === 'sites'
                                                    ? (Number(site[column.key] || 0) > 0 ? 1 : 0)
                                                    : Number(site[column.key] || 0))}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={(e) => updateBillingSiteQuantityOverride(site, column.key, e.target.value)}
                                                  style={{
                                                    width: '100%',
                                                    borderRadius: 8,
                                                    border: '1px solid #475569',
                                                    background: '#020617',
                                                    color: '#cbd5e1',
                                                    padding: '7px 8px',
                                                    textAlign: 'center',
                                                    fontWeight: 600,
                                                  }}
                                                />
                                                <input
                                                  type="number"
                                                  min="0"
                                                  step="1000"
                                                  value={pricingDraft[column.key]}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={(e) => updateBillingSitePricingDraft(site, column.key, e.target.value)}
                                                  style={{
                                                    width: '100%',
                                                    borderRadius: 8,
                                                    border: '1px solid #475569',
                                                    background: '#0f172a',
                                                    color: '#e5e7eb',
                                                    padding: '7px 8px',
                                                    textAlign: 'center',
                                                    fontWeight: 600,
                                                  }}
                                                />
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                  </>
                                )}
                              </div>
                              <div
                                style={{
                                  order: 2,
                                  marginTop: 18,
                                  padding: 18,
                                  borderRadius: 18,
                                  border: '1px solid rgba(71,85,105,0.5)',
                                  background: 'radial-gradient(circle at top left, rgba(245,158,11,0.12), transparent 26%), linear-gradient(180deg, rgba(17,24,39,0.88), rgba(15,23,42,0.96))',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 18 }}>Time and Materials</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>
                                      Record installation labor, cable, equipment, and other one-off costs. Every completed row is part of this T&M invoice.
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {renderBillingSourceControls('timeMaterials')}
                                    <button
                                      type="button"
                                      disabled={isGeneratingInvoicePdf || timeMaterialsTotal <= 0}
                                      onClick={() => generateInvoicePdf(user, 'time_materials')}
                                    >
                                      {isGeneratingInvoicePdf ? 'Generating…' : 'Generate T&M Invoice'}
                                    </button>
                                    <button type="button" onClick={() => toggleTimeMaterialsCollapsed(user.id)}>
                                      {isTimeMaterialsCollapsed ? 'Show' : 'Hide'}
                                    </button>
                                  </div>
                                </div>
                                {!isTimeMaterialsCollapsed && (
                                  <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
                                      <label style={{ display: 'grid', gap: 6, minWidth: 240 }}>
                                        <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#fbbf24' }}>Default Hourly Rate</span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="1000"
                                          value={timeAndMaterials.hourlyRate}
                                          onChange={(event) => updateTimeAndMaterials(user, {
                                            ...timeAndMaterials,
                                            hourlyRate: event.target.value,
                                          })}
                                        />
                                      </label>
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>T&M Total</div>
                                        <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700 }}>{formatNumber(timeMaterialsTotal)}</div>
                                      </div>
                                    </div>

                                    {timeAndMaterials.items.map((item) => (
                                      <div
                                        key={item.id}
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns: '110px minmax(180px, 2fr) 90px 100px 140px auto',
                                          gap: 8,
                                          alignItems: 'end',
                                          padding: 10,
                                          borderRadius: 10,
                                          border: '1px solid #334155',
                                          overflowX: 'auto',
                                        }}
                                      >
                                        <label style={{ display: 'grid', gap: 4 }}>
                                          <span style={{ fontSize: 10, color: '#9ca3af' }}>Type</span>
                                          <select
                                            value={item.type}
                                            onChange={(event) => {
                                              const type = event.target.value;
                                              updateTimeAndMaterials(user, {
                                                ...timeAndMaterials,
                                                items: timeAndMaterials.items.map((entry) => entry.id === item.id ? {
                                                  ...entry,
                                                  type,
                                                  unit: type === 'labor' ? 'hours' : 'units',
                                                  unitRate: type === 'labor' && entry.unitRate === 0 ? timeAndMaterials.hourlyRate : entry.unitRate,
                                                } : entry),
                                              });
                                            }}
                                          >
                                            <option value="labor">Labor</option>
                                            <option value="material">Material</option>
                                          </select>
                                        </label>
                                        {[
                                          ['description', 'Description', 'text'],
                                          ['quantity', 'Qty', 'number'],
                                          ['unit', 'Unit', 'text'],
                                          ['unitRate', 'Unit rate', 'number'],
                                        ].map(([field, label, type]) => (
                                          <label key={`${item.id}:${field}`} style={{ display: 'grid', gap: 4 }}>
                                            <span style={{ fontSize: 10, color: '#9ca3af' }}>{label}</span>
                                            <input
                                              type={type}
                                              min={type === 'number' ? '0' : undefined}
                                              step={field === 'quantity' ? '0.25' : field === 'unitRate' ? '1000' : undefined}
                                              value={item[field]}
                                              onChange={(event) => updateTimeAndMaterials(user, {
                                                ...timeAndMaterials,
                                                items: timeAndMaterials.items.map((entry) => entry.id === item.id
                                                  ? { ...entry, [field]: event.target.value }
                                                  : entry),
                                              })}
                                            />
                                          </label>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() => updateTimeAndMaterials(user, {
                                            ...timeAndMaterials,
                                            items: timeAndMaterials.items.filter((entry) => entry.id !== item.id),
                                          })}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    ))}

                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                      {[
                                        ['labor', 'Add Time'],
                                        ['material', 'Add Material'],
                                      ].map(([type, label]) => (
                                        <button
                                          key={type}
                                          type="button"
                                          onClick={() => updateTimeAndMaterials(user, {
                                            ...timeAndMaterials,
                                            items: [
                                              ...timeAndMaterials.items,
                                              {
                                                id: `${Date.now()}-${type}`,
                                                type,
                                                description: '',
                                                quantity: type === 'labor' ? 1 : 1,
                                                unit: type === 'labor' ? 'hours' : 'units',
                                                unitRate: type === 'labor' ? timeAndMaterials.hourlyRate : 0,
                                              },
                                            ],
                                          })}
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {renderSavedInvoiceList(
                                  user,
                                  timeMaterialsInvoiceDocuments,
                                  'No standalone T&M invoices saved yet.'
                                )}
                              </div>
                              {showCombinedInvoice && (
                                <div
                                  style={{
                                    order: 5,
                                    marginTop: 18,
                                    padding: 18,
                                    borderRadius: 18,
                                    border: '1px solid rgba(167,139,250,0.45)',
                                    background: 'radial-gradient(circle at top right, rgba(139,92,246,0.16), transparent 30%), linear-gradient(180deg, rgba(17,24,39,0.9), rgba(15,23,42,0.98))',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <div>
                                      <div style={{ fontWeight: 700, fontSize: 18 }}>Combined Invoice</div>
                                      <div style={{ marginTop: 4, fontSize: 12, color: '#c4b5fd' }}>
                                        One invoice containing the selected contract, service/package, and time-and-material charges.
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      disabled={isGeneratingInvoicePdf || combinedSubtotal <= 0}
                                      onClick={() => generateInvoicePdf(user, 'combined')}
                                    >
                                      {isGeneratingInvoicePdf ? 'Generating…' : 'Generate Combined Invoice'}
                                    </button>
                                  </div>

                                  <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
                                    <label style={{ display: 'grid', gap: 6 }}>
                                      <span style={{ fontSize: 11, color: '#c4b5fd', textTransform: 'uppercase' }}>Bill From</span>
                                      <select
                                        value={combinedInvoice.issuerCompanyKey}
                                        onChange={(event) => updateBillingProfileDraft(user, 'combinedInvoice', {
                                          ...combinedInvoice,
                                          issuerCompanyKey: event.target.value,
                                        })}
                                      >
                                        {billingCompanies.map((company) => (
                                          <option key={`combined:${company.key}`} value={company.key}>{company.companyName || company.key}</option>
                                        ))}
                                      </select>
                                    </label>
                                    <label style={{ display: 'grid', gap: 6 }}>
                                      <span style={{ fontSize: 11, color: '#c4b5fd', textTransform: 'uppercase' }}>Discount Type</span>
                                      <select
                                        value={combinedInvoice.discountType}
                                        onChange={(event) => updateBillingProfileDraft(user, 'combinedInvoice', {
                                          ...combinedInvoice,
                                          discountType: event.target.value,
                                        })}
                                      >
                                        <option value="percent">Percentage</option>
                                        <option value="flat">Flat amount</option>
                                      </select>
                                    </label>
                                    <label style={{ display: 'grid', gap: 6 }}>
                                      <span style={{ fontSize: 11, color: '#c4b5fd', textTransform: 'uppercase' }}>Discount</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step={combinedInvoice.discountType === 'flat' ? '1000' : '0.5'}
                                        value={combinedInvoice.discountValue}
                                        onChange={(event) => updateBillingProfileDraft(user, 'combinedInvoice', {
                                          ...combinedInvoice,
                                          discountValue: event.target.value,
                                        })}
                                      />
                                    </label>
                                    <label style={{ display: 'grid', gap: 6 }}>
                                      <span style={{ fontSize: 11, color: '#fca5a5', textTransform: 'uppercase' }}>Asshole Tax (%)</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="0.5"
                                        value={combinedInvoice.surchargePercent}
                                        onChange={(event) => updateBillingProfileDraft(user, 'combinedInvoice', {
                                          ...combinedInvoice,
                                          surchargePercent: event.target.value,
                                        })}
                                      />
                                    </label>
                                  </div>

                                  <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                                    {[
                                      ['Services / packages', combinedServiceSubtotal, billingSources.service.addToCombined],
                                      ['Contract rate', combinedContractSubtotal, billingSources.contract.addToCombined],
                                      ['Time and materials', combinedTimeMaterialsSubtotal, billingSources.timeMaterials.addToCombined],
                                      ['Asshole tax', combinedSurchargeAmount, combinedSurchargeAmount > 0],
                                      ['Discount', -combinedDiscountAmount, combinedDiscountAmount > 0],
                                    ].filter((entry) => entry[2]).map(([label, amount]) => (
                                      <div key={label} style={{ padding: 10, borderRadius: 10, border: '1px solid #374151', background: 'rgba(2,6,23,0.35)' }}>
                                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{label}</div>
                                        <div style={{ marginTop: 4, fontWeight: 700 }}>{formatNumber(amount)}</div>
                                      </div>
                                    ))}
                                  </div>

                                  <div style={{ marginTop: 14, textAlign: 'right' }}>
                                    <div style={{ fontSize: 12, color: '#cbd5e1' }}>Subtotal ex VAT: {formatNumber(combinedSubtotal)}</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#cbd5e1' }}>VAT ({billingVatPercent}%): {formatNumber(combinedVatAmount)}</div>
                                    <div style={{ marginTop: 5, fontSize: 22, fontWeight: 800, color: '#ddd6fe' }}>Total: {formatNumber(combinedTotalWithVat)}</div>
                                    <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>Issuer: {combinedCompanyProfile.companyName || 'Company details missing'}</div>
                                  </div>
                                  {renderSavedInvoiceList(
                                    user,
                                    combinedInvoiceDocuments,
                                    'No combined invoices saved yet.'
                                  )}
                                </div>
                              )}
                              </div>
                            </>
                          )}
                          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #374151', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button type="button" disabled={isSavingBillingProfile} onClick={() => setEditingBillingUserId(null)}>
                              Close
                            </button>
                            <button type="button" disabled={isSavingBillingProfile} onClick={() => saveBillingProfileAndClose(user)}>
                              {isSavingBillingProfile ? 'Saving…' : 'Save & Close'}
                            </button>
                          </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {billingLoading && (
            <div style={{ marginTop: 10, color: '#9ca3af', fontSize: 12 }}>
              Loading billing…
            </div>
          )}
        </div>
        </div>
      )}

      {subTab === 'Default prices' && (
        <div
          style={{
            marginTop: 12,
            padding: 16,
            borderRadius: 18,
            border: '1px solid rgba(148,163,184,0.2)',
            background:
              'radial-gradient(circle at top left, rgba(14,165,233,0.14), transparent 30%), radial-gradient(circle at bottom right, rgba(251,191,36,0.12), transparent 28%), linear-gradient(180deg, rgba(15,23,42,0.92), rgba(15,23,42,0.72))',
            boxShadow: '0 20px 40px rgba(2,6,23,0.28)',
          }}
        >
          <div
            style={{
              marginBottom: 16,
              padding: '14px 16px',
              borderRadius: 16,
              border: '1px solid rgba(125,211,252,0.2)',
              background: 'linear-gradient(135deg, rgba(30,41,59,0.86), rgba(15,23,42,0.94))',
            }}
          >
            <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7dd3fc' }}>Default Prices</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700 }}>Preset monthly rates for new sites.</div>
            <div style={{ marginTop: 4, fontSize: 13, color: '#94a3b8' }}>
              Autosaves as you type. Billing uses these values wherever a site does not have a custom override, then applies global VAT for invoice preview totals.
            </div>
          </div>

          <div
            style={{
              marginBottom: 14,
              display: 'grid',
              gridTemplateColumns: 'minmax(220px, 320px)',
              gap: 12,
            }}
          >
            <label
              style={{
                display: 'grid',
                gap: 8,
                padding: '14px 12px',
                borderRadius: 14,
                border: '1px solid rgba(71,85,105,0.55)',
                background: 'linear-gradient(180deg, rgba(30,41,59,0.78), rgba(15,23,42,0.96))',
              }}
            >
              <span style={{ textAlign: 'center', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7dd3fc' }}>
                Global VAT
              </span>
              <select
                value={billingVatDraft}
                onChange={(e) => updateBillingVatDraft(e.target.value)}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  border: '1px solid #475569',
                  background: '#0f172a',
                  color: '#e5e7eb',
                  padding: '9px 10px',
                  textAlign: 'center',
                  fontWeight: 700,
                }}
              >
                {VAT_RATE_OPTIONS.map((rate) => (
                  <option key={`vat-${rate}`} value={String(rate)}>
                    {rate}%
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
            }}
          >
            {BILLING_COLUMNS.map((column) => (
              <label
                key={column.key}
                style={{
                  display: 'grid',
                  gap: 8,
                  padding: '14px 12px',
                  borderRadius: 14,
                  border: '1px solid rgba(71,85,105,0.55)',
                  background: 'linear-gradient(180deg, rgba(30,41,59,0.78), rgba(15,23,42,0.96))',
                }}
              >
                <span style={{ textAlign: 'center', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7dd3fc' }}>
                  {column.label}
                </span>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={billingDefaultsDraft[column.key]}
                  onChange={(e) => updateBillingDefaultsDraft(column.key, e.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 8,
                    border: '1px solid #475569',
                    background: '#0f172a',
                    color: '#e5e7eb',
                    padding: '9px 10px',
                    textAlign: 'center',
                    fontWeight: 700,
                  }}
                />
              </label>
            ))}
          </div>

          <div
            style={{
              marginTop: 14,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid rgba(71,85,105,0.45)',
              background: 'rgba(15,23,42,0.55)',
            }}
          >
            <div style={{ color: '#9ca3af', fontSize: 12 }}>
              Suggested starting point: 500k per site, 250k per other feature, VAT {billingVatPercent}% globally.
            </div>
            <div style={{ color: billingDefaultsSaving ? '#e5e7eb' : '#9ca3af', fontSize: 12 }}>
              {billingDefaultsSaving ? 'Saving…' : 'Saved automatically'}
            </div>
          </div>
        </div>
      )}

      {subTab === 'APIs' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: 'rgba(17,24,39,0.4)' }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Total calls</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatNumber(apiSummary?.totals?.calls || 0)}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: 'rgba(17,24,39,0.4)' }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Total tokens (reported)</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatNumber(apiSummary?.totals?.totalTokens || 0)}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: 'rgba(17,24,39,0.4)' }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Top user</div>
              <div style={{ fontSize: 12, color: '#e5e7eb', marginTop: 4, fontFamily: 'monospace' }}>
                {apiSummary?.topUsers?.[0]?.email || '—'}
              </div>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                {apiSummary?.topUsers?.[0] ? `${formatNumber(apiSummary.topUsers[0].totalTokens)} tokens` : '—'}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
            <div style={{ fontWeight: 700 }}>Global rate limits (defaults)</div>
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>OpenAI max calls/day</span>
                <input
                  value={getLimitDraftValue({ userId: 0, api: 'openai', field: 'maxCalls' })}
                  onChange={(e) => setLimitDraftValue({ userId: 0, api: 'openai', field: 'maxCalls', value: e.target.value })}
                  placeholder="e.g. 200"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>OpenAI max tokens/day</span>
                <input
                  value={getLimitDraftValue({ userId: 0, api: 'openai', field: 'maxTotalTokens' })}
                  onChange={(e) => setLimitDraftValue({ userId: 0, api: 'openai', field: 'maxTotalTokens', value: e.target.value })}
                  placeholder="e.g. 500000"
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>Gemini max calls/day</span>
                <input
                  value={getLimitDraftValue({ userId: 0, api: 'gemini', field: 'maxCalls' })}
                  onChange={(e) => setLimitDraftValue({ userId: 0, api: 'gemini', field: 'maxCalls', value: e.target.value })}
                  placeholder="e.g. 100"
                />
              </label>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button
                type="button"
                disabled={savingLimitKey === '0:openai'}
                onClick={() =>
                  saveRateLimit({
                    userId: 0,
                    api: 'openai',
                    enabled: true,
                    maxCalls: getLimitDraftValue({ userId: 0, api: 'openai', field: 'maxCalls' }),
                    maxTotalTokens: getLimitDraftValue({ userId: 0, api: 'openai', field: 'maxTotalTokens' }),
                  })
                }
              >
                {savingLimitKey === '0:openai' ? 'Saving…' : 'Save OpenAI default'}
              </button>
              <button
                type="button"
                disabled={savingLimitKey === '0:gemini'}
                onClick={() =>
                  saveRateLimit({
                    userId: 0,
                    api: 'gemini',
                    enabled: true,
                    maxCalls: getLimitDraftValue({ userId: 0, api: 'gemini', field: 'maxCalls' }),
                    maxTotalTokens: '',
                  })
                }
              >
                {savingLimitKey === '0:gemini' ? 'Saving…' : 'Save Gemini default'}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
              <div style={{ fontWeight: 700 }}>APIs</div>
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                      <th style={{ padding: '8px 6px' }}>API</th>
                      <th style={{ padding: '8px 6px' }}>Scope</th>
                      <th style={{ padding: '8px 6px' }}>Calls</th>
                      <th style={{ padding: '8px 6px' }}>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(apiSummary?.byApi || []).length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                          No usage recorded.
                        </td>
                      </tr>
                    )}
                    {(apiSummary?.byApi || []).map((row) => (
                      <tr key={`${row.api}:${row.scope}`} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.api}</td>
                        <td style={{ padding: '8px 6px', color: '#9ca3af' }}>{row.scope}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.calls)}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.totalTokens)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
              <div style={{ fontWeight: 700 }}>Usage by feature</div>
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                      <th style={{ padding: '8px 6px' }}>Group</th>
                      <th style={{ padding: '8px 6px' }}>Feature</th>
                      <th style={{ padding: '8px 6px' }}>API</th>
                      <th style={{ padding: '8px 6px' }}>Calls</th>
                      <th style={{ padding: '8px 6px' }}>Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(apiSummary?.byFeature || []).length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                          No usage recorded.
                        </td>
                      </tr>
                    )}
                    {(apiSummary?.byFeature || []).map((row) => (
                      <tr key={`${row.api}:${row.scope}:${row.feature}`} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '8px 6px', color: '#9ca3af' }}>{getFeatureGroup(row.feature)}</td>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.feature || '—'}</td>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.api}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.calls)}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.totalTokens)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
            <div style={{ fontWeight: 700 }}>Top users</div>
            <div style={{ marginTop: 10, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                    <th style={{ padding: '8px 6px' }}>Email</th>
                    <th style={{ padding: '8px 6px' }}>Calls</th>
                    <th style={{ padding: '8px 6px' }}>Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {(apiSummary?.topUsers || []).length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                        No usage recorded.
                      </td>
                    </tr>
                  )}
                  {(apiSummary?.topUsers || []).map((row) => (
                    <tr key={`${row.userId}:${row.email}`} style={{ borderBottom: '1px solid #1f2937' }}>
                      <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.email}</td>
                      <td style={{ padding: '8px 6px' }}>{formatNumber(row.calls)}</td>
                      <td style={{ padding: '8px 6px' }}>{formatNumber(row.totalTokens)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {subTab === 'Webserver' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: 'rgba(17,24,39,0.4)' }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Web app pageviews</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatNumber(webSummary?.webApp?.pageviews || 0)}</div>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                {formatNumber(webSummary?.webApp?.uniqueVisitors || 0)} unique visitors
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: 'rgba(17,24,39,0.4)' }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Logged-in usage</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatNumber(webSummary?.webApp?.authenticatedPageviews || 0)}</div>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                {formatNumber(webSummary?.webApp?.uniqueUsers || 0)} unique users
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: 'rgba(17,24,39,0.4)' }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Landing visits</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{formatNumber(webSummary?.landing?.visits || 0)}</div>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                {formatNumber(webSummary?.landing?.uniqueVisitors || 0)} unique visitors
              </div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151', background: 'rgba(17,24,39,0.4)' }}>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Subscribers + MRR</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {formatNumber((webSummary?.users?.byTier?.basic || 0) + (webSummary?.users?.byTier?.standard || 0) + (webSummary?.users?.byTier?.premium || 0))}
              </div>
              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                {webSummary?.users?.estimatedMrr == null ? 'MRR estimate: —' : `MRR estimate: $${formatNumber(webSummary.users.estimatedMrr)}`}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
              <div style={{ fontWeight: 700 }}>Web app traffic (by day)</div>
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                      <th style={{ padding: '8px 6px' }}>Day</th>
                      <th style={{ padding: '8px 6px' }}>Pageviews</th>
                      <th style={{ padding: '8px 6px' }}>Uniques</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(webSummary?.webApp?.byDay || []).length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                          No traffic recorded yet (tracking starts after the DB migration is applied).
                        </td>
                      </tr>
                    )}
                    {(webSummary?.webApp?.byDay || []).map((row) => (
                      <tr key={row.day} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.day}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.pageviews)}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.uniqueVisitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
              <div style={{ fontWeight: 700 }}>Landing traffic (by day)</div>
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                      <th style={{ padding: '8px 6px' }}>Day</th>
                      <th style={{ padding: '8px 6px' }}>Visits</th>
                      <th style={{ padding: '8px 6px' }}>Uniques</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(webSummary?.landing?.byDay || []).length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                          No landing traffic recorded.
                        </td>
                      </tr>
                    )}
                    {(webSummary?.landing?.byDay || []).map((row) => (
                      <tr key={row.day} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.day}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.visits)}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.uniqueVisitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
              <div style={{ fontWeight: 700 }}>Top web app pages</div>
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                      <th style={{ padding: '8px 6px' }}>Path</th>
                      <th style={{ padding: '8px 6px' }}>Views</th>
                      <th style={{ padding: '8px 6px' }}>Uniques</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(webSummary?.webApp?.topPages || []).length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                          No traffic recorded.
                        </td>
                      </tr>
                    )}
                    {(webSummary?.webApp?.topPages || []).map((row) => (
                      <tr key={row.path} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.path}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.pageviews)}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.uniqueVisitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 8, color: '#9ca3af', fontSize: 12 }}>
                Note: dynamic IDs are normalized (e.g. <code>/sites/:id/confirm/:id</code>).
              </div>
            </div>

            <div style={{ padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
              <div style={{ fontWeight: 700 }}>Landing breakdown</div>
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                      <th style={{ padding: '8px 6px' }}>Page type</th>
                      <th style={{ padding: '8px 6px' }}>Visits</th>
                      <th style={{ padding: '8px 6px' }}>Uniques</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(webSummary?.landing?.byPageType || []).length === 0 && (
                      <tr>
                        <td colSpan={3} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                          No landing traffic recorded.
                        </td>
                      </tr>
                    )}
                    {(webSummary?.landing?.byPageType || []).map((row) => (
                      <tr key={row.pageType} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.pageType}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.visits)}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.uniqueVisitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 12, fontWeight: 700 }}>Top campaigns (landing)</div>
              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid #374151', color: '#9ca3af' }}>
                      <th style={{ padding: '8px 6px' }}>Campaign</th>
                      <th style={{ padding: '8px 6px' }}>Key</th>
                      <th style={{ padding: '8px 6px' }}>Visits</th>
                      <th style={{ padding: '8px 6px' }}>Uniques</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(webSummary?.landing?.topCampaigns || []).length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ padding: '10px 6px', color: '#9ca3af' }}>
                          No campaign landing traffic recorded.
                        </td>
                      </tr>
                    )}
                    {(webSummary?.landing?.topCampaigns || []).map((row) => (
                      <tr key={`${row.campaignId}:${row.landingKey}`} style={{ borderBottom: '1px solid #1f2937' }}>
                        <td style={{ padding: '8px 6px' }}>{row.title || `Campaign ${row.campaignId}`}</td>
                        <td style={{ padding: '8px 6px', fontFamily: 'monospace', fontSize: 12 }}>{row.landingKey}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.visits)}</td>
                        <td style={{ padding: '8px 6px' }}>{formatNumber(row.uniqueVisitors)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: '1px solid #374151' }}>
            <div style={{ fontWeight: 700 }}>Users</div>
            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
              <div style={{ padding: 10, border: '1px solid #1f2937', borderRadius: 10 }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Total users</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(webSummary?.users?.totalUsers || 0)}</div>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>
                  +{formatNumber(webSummary?.users?.newUsers || 0)} new in period
                </div>
              </div>
              <div style={{ padding: 10, border: '1px solid #1f2937', borderRadius: 10 }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Free</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(webSummary?.users?.byTier?.free || 0)}</div>
              </div>
              <div style={{ padding: 10, border: '1px solid #1f2937', borderRadius: 10 }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Standard</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(webSummary?.users?.byTier?.standard || 0)}</div>
              </div>
              <div style={{ padding: 10, border: '1px solid #1f2937', borderRadius: 10 }}>
                <div style={{ color: '#9ca3af', fontSize: 12 }}>Premium</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{formatNumber(webSummary?.users?.byTier?.premium || 0)}</div>
              </div>
            </div>
            <div style={{ marginTop: 8, color: '#9ca3af', fontSize: 12 }}>
              MRR estimate uses env vars (<code>TIER_PRICE_BASIC</code>, <code>TIER_PRICE_STANDARD</code>, <code>TIER_PRICE_PREMIUM</code>) if set; otherwise it shows <code>—</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
